import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  MenuItem,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Avatar,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Divider,
  Snackbar,
  LinearProgress,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  useMediaQuery,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  Paper,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Send,
  Delete,
  Edit,
  CheckCircle,
  MailOutline,
  Reply,
  FileUpload,
  CloudDone,
  MarkEmailRead,
  MarkEmailUnread,
  Add,
  Save,
  Inbox,
  WarningAmber,
  Campaign,
  Settings,
  Mail,
  Check,
  PeopleAlt,
  Layers,
  Refresh,
  Close,
  Visibility,
  VolumeUp,
  Groups,
  ExpandMore,
  ExpandLess,
  AccountTree,
  Analytics,
  Schedule,
  TrendingUp,
  ThumbUp,
  MessageSquare,
  MoreVert,
  Search,
  Download,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatClear,
  Link as LinkIcon,
  Title,
  FormatQuote,
  Code,
  Pause,
  AutoAwesome,
  Shield,
  Lightbulb,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import AdvancedEmailComposer from './AdvancedEmailComposer';
import EmailDeliverabilityGuide from './EmailDeliverabilityGuide';
import EmailStudioHeader from './EmailStudioHeader';
import AccountsManager from './AccountsManager';
import CampaignPlanner from './CampaignPlanner';
import InboxManager from './InboxManager';
import TrackingAnalytics from './TrackingAnalytics';
import TemplateManager from './TemplateManager';
import RichTextEditor from './RichTextEditor';
import { analyzeSpamScore, suggestReplacements } from './SpamDetector';
import { generateEmailWithAI, rewriteToAvoidSpam } from './AIEmailWriter';
import { useEmailManager, normalizeEmails } from '../../hooks/useEmailManager';
import { loadCarrierExport, carrierExportHeaders } from '../../utils/carrierExport';
import { readFileAsBase64 } from '../../utils/file';
import { useAuth } from '../../context/AuthContext';
import { adminApi } from '../../services/adminApi';
import { templateApi, TemplateApiError } from '../../services/templateApi';
import {
  EMAIL_PROVIDERS,
  detectProviderByEmail,
  detectProviderByHost,
  applyProviderTemplate,
  getProviderHelp,
  getProviderOptions,
  validateAccountForm,
} from '../../utils/emailProviders';

const defaultAccount = {
  label: '',
  provider: 'SMTP',
  fromName: '',
  fromEmail: '',
  host: '',
  port: 587,
  secure: true,
  username: '',
  password: '',
  imapHost: '',
  imapPort: 993,
  imapSecure: true,
  mailbox: 'INBOX',
  popHost: '',
  popPort: 995,
  popSecure: true,
  dailyLimit: 0,
};

const TRACKING_CACHE_PREFIX = 'emailStudio:tracking:v1';
const TRACKING_CACHE_TTL = 2 * 60 * 1000;
const TRACKING_CACHE_LIMIT = 500;

const mergeRecipients = (current = [], next = []) =>
  normalizeEmails([...(current || []), ...(next || [])]);

const isValidEmail = (value = '') => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
};

const extractCarrierEmails = (carrierData) => {
  if (!carrierData) return [];
  const pool = [];
  const consider = (row) => {
    if (!row) return;
    const candidates = [
      row.email,
      row.email_address,
      row.contactEmail,
      row.dispatchEmail,
    ].filter(Boolean);
    candidates.forEach((v) => pool.push(v));
  };
  if (Array.isArray(carrierData)) {
    carrierData.forEach(consider);
  } else if (carrierData.searchType === 'batch') {
    (carrierData.batchResults || []).forEach(consider);
  } else {
    consider(carrierData);
  }
  return normalizeEmails(pool);
};

const buildCarrierMergeRow = (carrier = {}, fallbackEmail = '') => {
  const pick = (...values) => {
    const match = values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
    return match === undefined || match === null ? '' : String(match);
  };

  const alerts = Array.isArray(carrier.alerts) ? carrier.alerts.join('; ') : pick(carrier.alerts, '');
  const physicalState = pick(carrier.phyState, carrier.physicalStateCode, '');
  const mailingState = pick(carrier.mailState, carrier.mailingStateCode, '');
  const row = {
    Email: pick(carrier.email, carrier.email_address, fallbackEmail),
    email: pick(carrier.email, carrier.email_address, fallbackEmail),
    'Legal Name': pick(carrier.legalName),
    legal_name: pick(carrier.legalName),
    DBA: pick(carrier.dbaName, carrier.legalName),
    dba: pick(carrier.dbaName, carrier.legalName),
    DOT: pick(carrier.dotNumber),
    dot: pick(carrier.dotNumber),
    MC: pick(carrier.mcNumber),
    mc: pick(carrier.mcNumber),
    'Primary Contact': pick(carrier.companyOfficer1),
    primary_contact: pick(carrier.companyOfficer1),
    'Secondary Contact': pick(carrier.companyOfficer2),
    secondary_contact: pick(carrier.companyOfficer2),
    Phone: pick(carrier.phone),
    phone: pick(carrier.phone),
    'Cell Phone': pick(carrier.cellPhone),
    cell_phone: pick(carrier.cellPhone),
    Fax: pick(carrier.fax),
    fax: pick(carrier.fax),
    'State(Physical)': physicalState,
    state_physical: physicalState,
    State: physicalState || mailingState,
    state: physicalState || mailingState,
    'State(Mailing)': mailingState,
    state_mailing: mailingState,
    Status: pick(carrier.status),
    status: pick(carrier.status),
    'Carrier Operation': pick(carrier.carrierOperation),
    carrier_operation: pick(carrier.carrierOperation),
    'Entity Type': pick(carrier.entityType),
    entity_type: pick(carrier.entityType),
    'Entity Classification': pick(carrier.entityClassification),
    entity_classification: pick(carrier.entityClassification),
    'Business Organization': pick(carrier.businessOrganization),
    business_organization: pick(carrier.businessOrganization),
    'Cargo Classification': pick(carrier.cargoClassification),
    cargo_classification: pick(carrier.cargoClassification),
    'Power Units': pick(carrier.powerUnits),
    power_units: pick(carrier.powerUnits),
    Drivers: pick(carrier.totalDrivers),
    drivers: pick(carrier.totalDrivers),
    'Total CDL Drivers': pick(carrier.totalCDLDrivers),
    total_cdl_drivers: pick(carrier.totalCDLDrivers),
    'Owned Trucks': pick(carrier.ownTrucks),
    owned_trucks: pick(carrier.ownTrucks),
    'Owned Tractors': pick(carrier.ownTractors),
    owned_tractors: pick(carrier.ownTractors),
    'Owned Trailers': pick(carrier.ownTrailers),
    owned_trailers: pick(carrier.ownTrailers),
    'Leased Trucks': pick(carrier.termLeasedTrucks),
    leased_trucks: pick(carrier.termLeasedTrucks),
    'Leased Tractors': pick(carrier.termLeasedTractors),
    leased_tractors: pick(carrier.termLeasedTractors),
    'Leased Trailers': pick(carrier.termLeasedTrailers),
    leased_trailers: pick(carrier.termLeasedTrailers),
    'MCS-150 Date': pick(carrier.mcs150Date),
    mcs150_date: pick(carrier.mcs150Date),
    'Add Date': pick(carrier.addDate),
    add_date: pick(carrier.addDate),
    'MCS-150 Mileage': pick(carrier.mcs150Mileage),
    mcs150_mileage: pick(carrier.mcs150Mileage),
    'MCS-150 Mileage Year': pick(carrier.mcs150MileageYear),
    mcs150_mileage_year: pick(carrier.mcs150MileageYear),
    'Active Duration': pick(carrier.activeDuration),
    active_duration: pick(carrier.activeDuration),
    'Physical Address': pick(carrier.physicalAddress),
    physical_address: pick(carrier.physicalAddress),
    'Mailing Address': pick(carrier.mailingAddress),
    mailing_address: pick(carrier.mailingAddress),
    'Authority Common': pick(carrier.authorityInfo?.commonAuthority),
    authority_common: pick(carrier.authorityInfo?.commonAuthority),
    'Authority Broker': pick(carrier.authorityInfo?.brokerAuthority),
    authority_broker: pick(carrier.authorityInfo?.brokerAuthority),
    'Authority Pending Common': carrier.authorityInfo?.pendingCommon ? 'Yes' : '',
    authority_pending_common: carrier.authorityInfo?.pendingCommon ? 'Yes' : '',
    'Authority Pending Contract': carrier.authorityInfo?.pendingContract ? 'Yes' : '',
    authority_pending_contract: carrier.authorityInfo?.pendingContract ? 'Yes' : '',
    'Authority Pending Broker': carrier.authorityInfo?.pendingBroker ? 'Yes' : '',
    authority_pending_broker: carrier.authorityInfo?.pendingBroker ? 'Yes' : '',
    'Authority Revocation Common': carrier.authorityInfo?.revocationCommon ? 'Yes' : '',
    authority_revocation_common: carrier.authorityInfo?.revocationCommon ? 'Yes' : '',
    'Authority Revocation Contract': carrier.authorityInfo?.revocationContract ? 'Yes' : '',
    authority_revocation_contract: carrier.authorityInfo?.revocationContract ? 'Yes' : '',
    'Authority Revocation Broker': carrier.authorityInfo?.revocationBroker ? 'Yes' : '',
    authority_revocation_broker: carrier.authorityInfo?.revocationBroker ? 'Yes' : '',
    'Insurance Type': pick(carrier.insuranceInfo?.type),
    insurance_type: pick(carrier.insuranceInfo?.type),
    'Insurance Class': pick(carrier.insuranceInfo?.class),
    insurance_class: pick(carrier.insuranceInfo?.class),
    'Insurance Max Coverage': pick(carrier.insuranceInfo?.maxCoverage),
    insurance_max_coverage: pick(carrier.insuranceInfo?.maxCoverage),
    'Insurance Underlying Limit': pick(carrier.insuranceInfo?.underlyingLimit),
    insurance_underlying_limit: pick(carrier.insuranceInfo?.underlyingLimit),
    'Insurance Policy Number': pick(carrier.insuranceInfo?.policyNumber),
    insurance_policy_number: pick(carrier.insuranceInfo?.policyNumber),
    'Insurance Effective Date': pick(carrier.insuranceInfo?.effectiveDate),
    insurance_effective_date: pick(carrier.insuranceInfo?.effectiveDate),
    'Insurance Form Code': pick(carrier.insuranceInfo?.formCode),
    insurance_form_code: pick(carrier.insuranceInfo?.formCode),
    'Insurance Company': pick(carrier.insuranceInfo?.companyName),
    insurance_company: pick(carrier.insuranceInfo?.companyName),
    'Pending Insurance Form Code': pick(carrier.pendingInsurance?.formCode),
    pending_insurance_form_code: pick(carrier.pendingInsurance?.formCode),
    'Pending Insurance Type': pick(carrier.pendingInsurance?.typeDescription),
    pending_insurance_type: pick(carrier.pendingInsurance?.typeDescription),
    'Pending Insurance Company': pick(carrier.pendingInsurance?.companyName),
    pending_insurance_company: pick(carrier.pendingInsurance?.companyName),
    'Pending Insurance Policy': pick(carrier.pendingInsurance?.policyNumber),
    pending_insurance_policy: pick(carrier.pendingInsurance?.policyNumber),
    'Pending Insurance Posted Date': pick(carrier.pendingInsurance?.postedDate),
    pending_insurance_posted_date: pick(carrier.pendingInsurance?.postedDate),
    'Pending Insurance Underlying Limit': pick(carrier.pendingInsurance?.underlyingLimit),
    pending_insurance_underlying_limit: pick(carrier.pendingInsurance?.underlyingLimit),
    'Pending Insurance Max Coverage': pick(carrier.pendingInsurance?.maxCoverage),
    pending_insurance_max_coverage: pick(carrier.pendingInsurance?.maxCoverage),
    'Pending Insurance Effective Date': pick(carrier.pendingInsurance?.effectiveDate),
    pending_insurance_effective_date: pick(carrier.pendingInsurance?.effectiveDate),
    'Pending Insurance Cancel Date': pick(carrier.pendingInsurance?.cancelEffectiveDate),
    pending_insurance_cancel_date: pick(carrier.pendingInsurance?.cancelEffectiveDate),
    'Authority History Action': pick(carrier.authorityHistory?.action),
    authority_history_action: pick(carrier.authorityHistory?.action),
    'Authority History Service Type': pick(carrier.authorityHistory?.serviceType),
    authority_history_service_type: pick(carrier.authorityHistory?.serviceType),
    'Authority History Served Date': pick(carrier.authorityHistory?.servedDate),
    authority_history_served_date: pick(carrier.authorityHistory?.servedDate),
    'Revocation Pending': carrier.revocationInfo?.hasRevocation ? 'Yes' : '',
    revocation_pending: carrier.revocationInfo?.hasRevocation ? 'Yes' : '',
    Alerts: alerts,
    alerts,
  };

  return Object.fromEntries(
    Object.entries(row).map(([key, value]) => [key, value === 'N/A' ? '' : value])
  );
};

const EmailWorkspace = ({
  carrierData,
  onStatsChange,
  inboxOnly = false,
  currentView = 0,
  quickComposePayload = null,
  onQuickComposeConsumed = null,
  quickComposeReturnToSearch = false,
  onQuickComposeSent = null,
}) => {
  const { isAuthenticated, token, user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const envApiBase = process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_BASE_URL || '';
  const apiBase = useMemo(() => {
    if (envApiBase) return envApiBase.replace(/\/$/, '');
    if (typeof window === 'undefined') return '';
    return window.location.origin || '';
  }, [envApiBase]);
  const apiUrl = useCallback((path) => `${apiBase}${path}`, [apiBase]);
  const authHeaders = useMemo(
    () =>
      token
        ? { Authorization: `Bearer ${token}`, 'X-Auth-Token': token }
        : {},
    [token]
  );
  const {
    accounts,
    selectedAccountId,
    setSelectedAccountId,
    inbox,
    sent,
    sending,
    statusText,
    sendProgress,
    addAccount,
    updateAccount,
    deleteAccount,
    testAccount,
    sendEmail,
    fetchTrackingStats,
    fetchTrackingTotals,
    fetchTrackingList,
    setMessageStatus,
    fetchMessageStatuses,
    applyMessageStatuses,
    replyToMessage,
    deleteMessage,
    fetchInbox,
    fetchAllInboxes,
    fetchingInbox,
    stats,
    prefsError,
  } = useEmailManager();

  const [accountForm, setAccountForm] = useState(defaultAccount);
  const [editingId, setEditingId] = useState('');
  const [accountMessage, setAccountMessage] = useState('');
  const [accountError, setAccountError] = useState('');
  const [testPassed, setTestPassed] = useState(false);
  const [testingAccount, setTestingAccount] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [selectedProviderTemplate, setSelectedProviderTemplate] = useState(null);
  const [showProviderGuide, setShowProviderGuide] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountProviderFilter, setAccountProviderFilter] = useState('');
  const [compactAccounts, setCompactAccounts] = useState(true);
  const [bulkTesting, setBulkTesting] = useState(false);
  const [bulkTestSummary, setBulkTestSummary] = useState({ success: 0, failed: 0 });
  const [accountGroups, setAccountGroups] = useState([]);
  const [groupOpen, setGroupOpen] = useState({});
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupForm, setGroupForm] = useState({ id: '', name: '', accountIds: [] });

  const [compose, setCompose] = useState({
    accountId: '',
    subject: '',
    body: '',
    recipients: [],
    manualBlock: '',
    trackOpens: false,
  });
  const [trackingProvider, setTrackingProvider] = useState('none');
  const [trackingIntegration, setTrackingIntegration] = useState({});
  const [composeRecipientRows, setComposeRecipientRows] = useState({ headers: [], rows: [], byEmail: {} });
  const [quickComposeSeed, setQuickComposeSeed] = useState(null);
  const [quickComposeFlowActive, setQuickComposeFlowActive] = useState(false);
  const [sendError, setSendError] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [filterUnread, setFilterUnread] = useState(false);
  // Use currentView prop instead of internal activeTab state
  const activeTab = currentView;
  const isComposeTab = activeTab === 0;

  // Pick up Quick Email from Carrier Search (if navigated via "Send via Email Studio" button)
  useEffect(() => {
    if (isComposeTab) {
      if (quickComposePayload?.email && Date.now() - quickComposePayload.timestamp < 60000) {
        setQuickComposeSeed(quickComposePayload);
        setQuickComposeFlowActive(true);
        return;
      }
      try {
        const raw = localStorage.getItem('cfp_quick_email');
        if (raw) {
          const quickData = JSON.parse(raw);
          if (quickData.email && Date.now() - quickData.timestamp < 60000) {
            setQuickComposeSeed(quickData);
            setQuickComposeFlowActive(true);
          } else {
            localStorage.removeItem('cfp_quick_email');
          }
        }
      } catch (e) { /* ignore */ }
    }
  }, [isComposeTab, quickComposePayload]);

  const [activeMessageId, setActiveMessageId] = useState('');
  const [replyDrafts, setReplyDrafts] = useState({});
  const fileInputRef = useRef(null);
  const replyFileInputRef = useRef(null);
  const [replyAttachments, setReplyAttachments] = useState([]); // kept for compatibility; attachments not sent in reply
  const [replySending, setReplySending] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [notice, setNotice] = useState({ open: false, type: 'info', message: '', scope: 'global' });
  const [trackingMap, setTrackingMap] = useState({});
  const [trackingTotals, setTrackingTotals] = useState({ totalOpens: 0 });
  const [externalOpenCount, setExternalOpenCount] = useState(0);
  const [seenLoading, setSeenLoading] = useState(false);
  const [seenError, setSeenError] = useState('');
  const [lastSeenRefreshAt, setLastSeenRefreshAt] = useState(null);
  const [seenPage, setSeenPage] = useState(1);
  const [inboxEmails, setInboxEmails] = useState([]);
  const [threads, setThreads] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedEmailId, setSelectedEmailId] = useState(null);
  const [lastInboxRefreshAt, setLastInboxRefreshAt] = useState(null);
  const [trackingData, setTrackingData] = useState([]);
  const trackingCacheReadyRef = useRef(false);
  const trackingCacheTimestampRef = useRef(0);
  const trackingCacheKey = useMemo(() => {
    const baseId = user?.id || user?.email || 'anon';
    const safeToken = token ? String(token).replace(/[^a-z0-9]/gi, '').slice(0, 24) : 'anon';
    return `${TRACKING_CACHE_PREFIX}:${baseId}:${safeToken}`;
  }, [token, user?.email, user?.id]);
  const loadTrackingCache = useCallback(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    try {
      const raw = window.localStorage.getItem(trackingCacheKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.items)) {
        return null;
      }
      return parsed;
    } catch (error) {
      console.warn('Failed to read tracking cache', error);
      return null;
    }
  }, [trackingCacheKey]);
  const saveTrackingCache = useCallback(
    (items) => {
      if (typeof window === 'undefined') {
        return;
      }
      try {
        const payload = {
          timestamp: Date.now(),
          items: (items || []).slice(0, TRACKING_CACHE_LIMIT),
        };
        window.localStorage.setItem(trackingCacheKey, JSON.stringify(payload));
        trackingCacheTimestampRef.current = payload.timestamp;
      } catch (error) {
        console.warn('Failed to persist tracking cache', error);
      }
    },
    [trackingCacheKey]
  );
  const [emailStats, setEmailStats] = useState({});
  const [campaignAnalytics, setCampaignAnalytics] = useState({});
  const notificationsInitializedRef = useRef(false);
  const trackingInitializedRef = useRef(false);
  const externalTrackingInitializedRef = useRef(false);
  const externalTrackingLastIdRef = useRef(0);
  const defaultCampaignPlan = {
    selectedTemplates: [],
    selectedAccounts: [],
    perAccountDaily: 50,
    perAccountLimits: {},
    batchSize: 10,
    minDelaySec: 30,
    maxDelaySec: 90,
    shuffleTemplates: true,
    rotateAccounts: true,
    startOffsetSec: 0,
    jitterSec: 5,
    batchPauseEvery: 10,
    batchPauseSec: 120,
    maxPerAccountPerHour: 40,
    maxFailures: 5,
    enforceMinDelay: true,
    trackOpens: true,
    trackClicks: true,
  };

  const [campaignTemplates, setCampaignTemplates] = useState([]);
  const [templateForm, setTemplateForm] = useState({ id: '', name: '', subject: '', body: '' });
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const templateBodyRef = useRef(null);
  
  // AI Email Writer & Spam Detection States
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiTone, setAiTone] = useState('professional');
  const [aiLength, setAiLength] = useState('medium');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [spamAnalysis, setSpamAnalysis] = useState(null);
  const [showSpamDetails, setShowSpamDetails] = useState(false);
  const [rewriteLoading, setRewriteLoading] = useState(false);
  
  const [campaignPlan, setCampaignPlan] = useState(defaultCampaignPlan);
  const [campaignRun, setCampaignRun] = useState({ id: null, counts: {}, running: false });
  const [plannerStats, setPlannerStats] = useState({});
  const [campaignTrackOpens, setCampaignTrackOpens] = useState(true);
  const [savedPlans, setSavedPlans] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [composePlanId, setComposePlanId] = useState('');
  const [composeTemplateId, setComposeTemplateId] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [inboxSearch, setInboxSearch] = useState('');
  const [inboxAccountFilter, setInboxAccountFilter] = useState('all');
  const [engagedSearch, setEngagedSearch] = useState('');
  const [engagedMinOpens, setEngagedMinOpens] = useState(0);
  const campaignPollRef = useRef(null);
  const [plannerLoaded, setPlannerLoaded] = useState(false);
  const initialSyncSkipped = useRef(false);
  const uiPrefsSyncSkipped = useRef(false);
  const uiPrefsTimer = useRef(null);
  const inboxAutoRefreshRef = useRef(null);
  const gravatarCache = useRef({});
  const [planOpen, setPlanOpen] = useState({});
  const [plannerRecipientColumns, setPlannerRecipientColumns] = useState([]);
  useEffect(() => {
    if (!isComposeTab || !quickComposeSeed?.email) return;
    const preferredAccountId = selectedAccountId || accounts[0]?.id || '';
    const preferredTemplate =
      campaignTemplates.find((template) => template.id === composeTemplateId) ||
      campaignTemplates.find((template) => template.isFavorite) ||
      campaignTemplates[0] ||
      null;

    setCompose((prev) => ({
      ...prev,
      accountId: preferredAccountId || prev.accountId || '',
      recipients: [...new Set([...(prev.recipients || []), quickComposeSeed.email])],
      manualBlock: prev.manualBlock || quickComposeSeed.email,
      subject: prev.subject || preferredTemplate?.subject || '',
      body: prev.body || preferredTemplate?.body || '',
    }));

    if (quickComposeSeed?.carrier && quickComposeSeed.email) {
      const mergeRow = buildCarrierMergeRow(quickComposeSeed.carrier, quickComposeSeed.email);
      setComposeRecipientRows((prev) => {
        const byEmail = { ...(prev.byEmail || {}), [quickComposeSeed.email.toLowerCase()]: mergeRow };
        const headerSet = new Set([...(prev.headers || []), ...Object.keys(mergeRow)]);
        const nextRows = [
          ...(prev.rows || []).filter((row) => String(row.email || row.Email || '').toLowerCase() !== quickComposeSeed.email.toLowerCase()),
          mergeRow,
        ];
        return {
          headers: Array.from(headerSet),
          rows: nextRows,
          byEmail,
        };
      });
    }

    if (preferredAccountId && selectedAccountId !== preferredAccountId) {
      setSelectedAccountId(preferredAccountId);
    }

    if (preferredTemplate?.id) {
      setComposeTemplateId(preferredTemplate.id);
    }

    localStorage.removeItem('cfp_quick_email');
    if (typeof onQuickComposeConsumed === 'function') {
      onQuickComposeConsumed();
    }
    setQuickComposeSeed(null);
  }, [
    accounts,
    campaignTemplates,
    composeTemplateId,
    isComposeTab,
    onQuickComposeConsumed,
    quickComposeSeed,
    selectedAccountId,
    setSelectedAccountId,
  ]);

  const mergeTagOptions = useMemo(() => {
    const explicit = carrierExportHeaders;
    const fromCompose = composeRecipientRows.headers || [];
    const fromPlanner = plannerRecipientColumns || [];
    const combined = [...explicit, ...fromCompose, ...fromPlanner]
      .map((item) => String(item || '').trim())
      .filter(Boolean);
    return Array.from(new Set(combined)).map((key) => `{{${key}}}`);
  }, [composeRecipientRows.headers, plannerRecipientColumns]);
  const filteredTemplates = useMemo(() => {
    const query = String(templateSearch || '').trim().toLowerCase();
    if (!query) return campaignTemplates;
    return campaignTemplates.filter((template) => {
      const hay = `${template.name || ''} ${template.subject || ''}`.toLowerCase();
      return hay.includes(query);
    });
  }, [campaignTemplates, templateSearch]);
  const updateSavedPlan = useCallback((id, updater) => {
    setSavedPlans((prev) => prev.map((p) => (p.id === id ? updater(p) : p)));
  }, []);
  const [notificationPrefs, setNotificationPrefs] = useState({
    newEmail: true,
    seen: true,
    opens: true,
    sound: true,
  });
  const prevUnreadCount = useRef(0);
  const prevReadIds = useRef(new Set());
  const [footerNotice, setFooterNotice] = useState({ open: false, message: '' });
  const prevTotalOpens = useRef(0);
  const engagedGroups = useMemo(() => {
    const groups = {};
    sent.forEach((s) => {
      const mid = s.messageId || s.id;
      const stats = trackingMap[mid];
      if (!stats || (stats.totalOpens || 0) <= 0) return;
      const recipients = (s.recipients || []).map((r) => r.toLowerCase()).sort();
      const key = recipients.join('|') || mid;
      const lastOpened = stats.lastOpenedAt ? new Date(stats.lastOpenedAt).getTime() : 0;
      if (!groups[key]) {
        groups[key] = {
          id: key,
          recipients: s.recipients || [],
          subjects: new Set(),
          totalOpens: 0,
          messages: [],
          lastOpenedTs: lastOpened,
        };
      }
      groups[key].subjects.add(s.subject || '(No subject)');
      groups[key].totalOpens += stats.totalOpens || 0;
      groups[key].messages.push({ ...s, stats });
      if (lastOpened > groups[key].lastOpenedTs) {
        groups[key].lastOpenedTs = lastOpened;
      }
    });
    return Object.values(groups).map((g) => ({
      ...g,
      subjectLabel: Array.from(g.subjects).join(' | '),
      lastOpened: g.lastOpenedTs ? new Date(g.lastOpenedTs).toISOString() : null,
    }));
  }, [sent, trackingMap]);
  const filteredEngagedGroups = useMemo(() => {
    const query = String(engagedSearch || '').trim().toLowerCase();
    const minOpens = Number(engagedMinOpens) || 0;
    return engagedGroups.filter((group) => {
      if (minOpens && (group.totalOpens || 0) < minOpens) return false;
      if (!query) return true;
      const hay = [
        group.subjectLabel,
        (group.recipients || []).join(' '),
        group.lastOpened,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(query);
    });
  }, [engagedGroups, engagedSearch, engagedMinOpens]);
  const engagedPageCount = Math.max(1, Math.ceil(filteredEngagedGroups.length / 10));
  const subjectInsights = useMemo(() => {
    const value = compose.subject || '';
    const length = value.length;
    const lower = value.toLowerCase();
    const spamHints = ['free', 'urgent', 'act now', 'winner', 'guarantee', 'buy now', 'limited'];
    const hits = spamHints.filter((word) => lower.includes(word));
    return {
      length,
      tooShort: length > 0 && length < 4,
      tooLong: length > 70,
      spamHits: hits,
    };
  }, [compose.subject]);
  const bodyInsights = useMemo(() => {
    const text = htmlToText(compose.body || '');
    const words = text.trim().split(/\s+/).filter(Boolean);
    const linkCount = (compose.body || '').match(/<a\s/gi)?.length || 0;
    return {
      wordCount: words.length,
      linkCount,
      hasGreeting: /(hi|hello|dear)/i.test(text),
    };
  }, [compose.body]);
  const recipientStats = useMemo(() => {
    const list = compose.recipients || [];
    const invalid = list.filter((email) => !isValidEmail(email)).length;
    const domainCounts = {};
    list.forEach((email) => {
      const parts = String(email || '').split('@');
      if (parts.length < 2) return;
      const domain = parts[1].toLowerCase();
      if (!domain) return;
      domainCounts[domain] = (domainCounts[domain] || 0) + 1;
    });
    const topDomains = Object.entries(domainCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([domain, count]) => `${domain} (${count})`);
    return {
      total: list.length,
      invalid,
      topDomains,
    };
  }, [compose.recipients]);
  const syncUiPrefsToServer = async () => {
    if (!isAuthenticated) return;
    try {
      await adminApi.savePrefs(
        {
          activeTab,
          composePlanId,
          composeTemplateId,
          composeDraft: {
            accountId: compose.accountId || '',
            subject: compose.subject || '',
            body: compose.body || '',
            recipients: Array.isArray(compose.recipients) ? compose.recipients : [],
            manualBlock: compose.manualBlock || '',
            trackOpens: compose.trackOpens !== false,
          },
          notificationPrefs,
          filterUnread,
          engagedSearch,
          engagedMinOpens,
          trackingProvider,
          trackingIntegration,
        },
        token
      );
    } catch (err) {
      setNotice({ open: true, type: 'error', message: 'Could not save email studio preferences to server.', scope: 'compose' });
    }
  };

  const loadPrefsFromServer = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await adminApi.loadPrefs(token);
      if (res?.prefs) {
        if (res.prefs.campaignPlan) setCampaignPlan((p) => ({ ...p, ...res.prefs.campaignPlan, perAccountLimits: res.prefs.campaignPlan?.perAccountLimits || {} }));
        if (Array.isArray(res.prefs.accountGroups)) setAccountGroups(res.prefs.accountGroups);
        if (typeof res.prefs.accountProviderFilter === 'string') setAccountProviderFilter(res.prefs.accountProviderFilter);
        if (typeof res.prefs.compactAccounts === 'boolean') setCompactAccounts(res.prefs.compactAccounts);
        if (typeof res.prefs.composePlanId === 'string') setComposePlanId(res.prefs.composePlanId);
        if (typeof res.prefs.composeTemplateId === 'string') setComposeTemplateId(res.prefs.composeTemplateId);
        if (typeof res.prefs.trackingProvider === 'string') setTrackingProvider(res.prefs.trackingProvider);
        if (res.prefs.trackingIntegration && typeof res.prefs.trackingIntegration === 'object') {
          setTrackingIntegration(res.prefs.trackingIntegration);
        }
        if (res.prefs.composeDraft && typeof res.prefs.composeDraft === 'object') {
          const draft = res.prefs.composeDraft;
          setCompose((prev) => ({
            ...prev,
            accountId: typeof draft.accountId === 'string' ? draft.accountId : prev.accountId,
            subject: typeof draft.subject === 'string' ? draft.subject : prev.subject,
            body: typeof draft.body === 'string' ? draft.body : prev.body,
            recipients: Array.isArray(draft.recipients) ? draft.recipients : prev.recipients,
            manualBlock: typeof draft.manualBlock === 'string' ? draft.manualBlock : prev.manualBlock,
            trackOpens: typeof draft.trackOpens === 'boolean' ? draft.trackOpens : prev.trackOpens,
          }));
        }
        if (res.prefs.notificationPrefs && typeof res.prefs.notificationPrefs === 'object') {
          setNotificationPrefs((prev) => ({ ...prev, ...res.prefs.notificationPrefs }));
        }
        if (typeof res.prefs.filterUnread === 'boolean') setFilterUnread(res.prefs.filterUnread);
        if (typeof res.prefs.engagedSearch === 'string') setEngagedSearch(res.prefs.engagedSearch);
        if (typeof res.prefs.engagedMinOpens === 'number') setEngagedMinOpens(res.prefs.engagedMinOpens);
      }
    } catch (e) {
      const msg = e?.message || 'Could not load planner settings from server.';
      setNotice({ open: true, type: 'error', message: msg, scope: 'campaign' });
    }
  }, [isAuthenticated, token]);

  const trackingIntegrationConnected = useMemo(
    () => Boolean(trackingIntegration?.enabled && trackingIntegration?.accountEmail),
    [trackingIntegration]
  );

  const effectiveTrackingProvider = useMemo(() => {
    if (trackingProvider === 'mailsuite' && trackingIntegrationConnected) {
      return 'mailsuite';
    }
    if (trackingProvider === 'internal') {
      return 'internal';
    }
    return 'none';
  }, [trackingIntegrationConnected, trackingProvider]);

  const carrierEmails = useMemo(
    () => extractCarrierEmails(carrierData),
    [carrierData]
  );

  const normalizeTemplateRecord = useCallback((template, fallback = {}) => {
    const id = template?.id || template?.template_id || fallback.id || '';
    return {
      id,
      name: template?.name ?? fallback.name ?? '',
      subject: template?.subject ?? fallback.subject ?? '',
      body: template?.body ?? fallback.body ?? '',
      category: template?.category ?? fallback.category ?? 'general',
      description: template?.description ?? fallback.description ?? '',
      tags: Array.isArray(template?.tags) ? template.tags : (fallback.tags || []),
      isFavorite: typeof template?.isFavorite === 'boolean'
        ? template.isFavorite
        : (typeof template?.is_favorite === 'boolean' ? template.is_favorite : !!fallback.isFavorite),
      uses: template?.uses ?? fallback.uses ?? 0,
      openRate: template?.openRate ?? template?.open_rate ?? fallback.openRate ?? 0,
      createdAt: template?.createdAt ?? template?.created_at ?? fallback.createdAt,
      updatedAt: template?.updatedAt ?? template?.updated_at ?? fallback.updatedAt,
    };
  }, []);

  const upsertTemplateRecord = useCallback((record) => {
    if (!record?.id) return;
    setCampaignTemplates((prev) => {
      const existingIdx = prev.findIndex((t) => t.id === record.id);
      if (existingIdx >= 0) {
        const next = [...prev];
        next[existingIdx] = { ...prev[existingIdx], ...record };
        return next;
      }
      return [record, ...prev].slice(0, 50);
    });
  }, []);

  const normalizePlanRecord = useCallback((plan, fallback = {}) => {
    const id = plan?.id || plan?.plan_id || fallback.id || '';
    const strategy = plan?.strategy || fallback.strategy || {};
    const meta = strategy?.meta || {};
    const cleanedStrategy = { ...strategy };
    if (cleanedStrategy && cleanedStrategy.meta) {
      delete cleanedStrategy.meta;
    }
    return {
      id,
      name: plan?.name ?? fallback.name ?? '',
      strategy: cleanedStrategy,
      templates: Array.isArray(plan?.templates) ? plan.templates : (fallback.templates || []),
      recipientRows: Array.isArray(plan?.recipientRows)
        ? plan.recipientRows
        : (Array.isArray(plan?.recipients) ? plan.recipients : (fallback.recipientRows || [])),
      recipientFileName: plan?.recipientFileName ?? plan?.recipient_file_name ?? fallback.recipientFileName ?? '',
      status: plan?.status ?? fallback.status ?? 'draft',
      description: plan?.description ?? meta.description ?? fallback.description ?? '',
      scheduledDate: plan?.scheduledDate ?? meta.scheduledDate ?? fallback.scheduledDate ?? null,
      timezone: plan?.timezone ?? meta.timezone ?? fallback.timezone ?? 'UTC',
      segmentType: plan?.segmentType ?? meta.segmentType ?? fallback.segmentType ?? 'all',
      sendingFrequency: plan?.sendingFrequency ?? meta.sendingFrequency ?? fallback.sendingFrequency ?? 'immediate',
      recipientLimit: plan?.recipientLimit ?? meta.recipientLimit ?? fallback.recipientLimit ?? '',
      useMcRange: typeof plan?.useMcRange === 'boolean'
        ? plan.useMcRange
        : (typeof strategy?.useMcRange === 'boolean' ? strategy.useMcRange : !!fallback.useMcRange),
      mcStartNumber: plan?.mcStartNumber ?? strategy?.mcStartNumber ?? fallback.mcStartNumber ?? '',
      mcEndNumber: plan?.mcEndNumber ?? strategy?.mcEndNumber ?? fallback.mcEndNumber ?? '',
      targetActiveCarriers: typeof plan?.targetActiveCarriers === 'boolean'
        ? plan.targetActiveCarriers
        : (typeof strategy?.targetActiveCarriers === 'boolean' ? strategy.targetActiveCarriers : fallback.targetActiveCarriers !== false),
      targetInactiveCarriers: typeof plan?.targetInactiveCarriers === 'boolean'
        ? plan.targetInactiveCarriers
        : (typeof strategy?.targetInactiveCarriers === 'boolean' ? strategy.targetInactiveCarriers : !!fallback.targetInactiveCarriers),
      targetBrokers: typeof plan?.targetBrokers === 'boolean'
        ? plan.targetBrokers
        : (typeof strategy?.targetBrokers === 'boolean' ? strategy.targetBrokers : !!fallback.targetBrokers),
      createdAt: plan?.createdAt ?? plan?.created_at ?? fallback.createdAt,
      updatedAt: plan?.updatedAt ?? plan?.updated_at ?? fallback.updatedAt,
    };
  }, []);

  const upsertPlanRecord = useCallback((record) => {
    if (!record?.id) return;
    setSavedPlans((prev) => {
      const existingIdx = prev.findIndex((p) => p.id === record.id);
      if (existingIdx >= 0) {
        const next = [...prev];
        next[existingIdx] = { ...prev[existingIdx], ...record };
        return next;
      }
      return [record, ...prev].slice(0, 20);
    });
  }, []);

  const refreshCampaignPlans = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setSavedPlans([]);
      return;
    }
    const res = await fetch(apiUrl('/api/campaign_plans.php?action=list'), {
      headers: authHeaders,
      credentials: 'include',
    });
    const payload = await res.json();
    if (!res.ok) {
      throw new Error(payload?.error || 'Failed to load plans');
    }
    const plans = Array.isArray(payload?.plans) ? payload.plans : [];
    setSavedPlans(plans.map((plan) => normalizePlanRecord(plan)));
  }, [isAuthenticated, token, apiUrl, authHeaders, normalizePlanRecord]);

  // Auto-sync email workspace data to database
 

  useEffect(() => {
    setCompose((prev) => ({ ...prev, accountId: selectedAccountId }));
  }, [selectedAccountId]);

  useEffect(() => {
    if (isAuthenticated) {
      // Reset to avoid leaking another user's data while loading
      setCampaignTemplates([]);
      setCampaignPlan(defaultCampaignPlan);
      setSavedPlans([]);
      setAccountGroups([]);
      setAccountProviderFilter('');
      setCompactAccounts(true);
      setPlannerLoaded(false);
      loadPrefsFromServer().finally(() => setPlannerLoaded(true));
    } else {
      setCampaignTemplates([]);
      setCampaignPlan(defaultCampaignPlan);
      setSavedPlans([]);
      setAccountGroups([]);
      setAccountProviderFilter('');
      setCompactAccounts(true);
      setPlannerLoaded(true);
    }
  }, [isAuthenticated, token, loadPrefsFromServer]);

  // Database synchronization removed
  // useEffect(() => {
  //   if (token) {
  //     // databaseService initialization removed
  //   }
  // }, [token]);

  // Load email accounts from database - REMOVED (database functionality disabled)
  // useEffect(() => {
  //   if (!isAuthenticated || !token) return;
  //   // Database loading code removed
  // }, [isAuthenticated, token]);

  // Load templates, plans, groups and campaigns from database - REMOVED (database functionality disabled)
  // useEffect(() => {
  //   if (!isAuthenticated || !token) return;
  //   // Database loading code removed
  // }, [isAuthenticated, token]);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setCampaignTemplates([]);
      return;
    }
    let cancelled = false;
    const loadTemplates = async () => {
      try {
        const response = await templateApi.listTemplates({}, token);
        if (cancelled) return;
        const templates = Array.isArray(response?.templates) ? response.templates : [];
        setCampaignTemplates(templates.map((t) => normalizeTemplateRecord(t)));
      } catch (error) {
        if (cancelled) return;
        const message = error instanceof TemplateApiError
          ? error.message
          : 'Failed to load templates from database.';
        setNotice({ open: true, type: 'error', message, scope: 'campaign' });
      }
    };
    loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, token, normalizeTemplateRecord]);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setSavedPlans([]);
      return;
    }
    let cancelled = false;
    const loadPlans = async () => {
      try {
        await refreshCampaignPlans();
        if (cancelled) return;
      } catch (error) {
        if (cancelled) return;
        setNotice({
          open: true,
          type: 'error',
          message: error?.message || 'Failed to load plans from database.',
          scope: 'campaign',
        });
      }
    };
    loadPlans();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, token, refreshCampaignPlans]);

  useEffect(() => {
    // Allow first-sync skip per user session
    initialSyncSkipped.current = false;
    uiPrefsSyncSkipped.current = false;
  }, [token]);

  useEffect(() => {
    externalTrackingInitializedRef.current = false;
    externalTrackingLastIdRef.current = 0;
    setExternalOpenCount(0);
  }, [effectiveTrackingProvider, token]);

  useEffect(() => {
    setSeenPage(1);
  }, [engagedSearch, engagedMinOpens]);

  const refreshUnifiedInbox = useCallback(async (notifyOnError = true) => {
    try {
      await fetchAllInboxes();
      setLastInboxRefreshAt(Date.now());
    } catch (err) {
      if (notifyOnError) {
        let errorMsg = err?.message || 'Inbox refresh failed';
        if (/imap/i.test(errorMsg)) {
          errorMsg += ' | Check IMAP settings in Account Settings';
        }
        setNotice({ open: true, type: 'error', message: errorMsg, scope: 'inbox' });
      }
      throw err;
    }
  }, [fetchAllInboxes]);

  useEffect(() => {
    if (!isAuthenticated || !plannerLoaded) return;
    if (!uiPrefsSyncSkipped.current) {
      uiPrefsSyncSkipped.current = true; // avoid spurious save on initial load
      return;
    }
    if (uiPrefsTimer.current) clearTimeout(uiPrefsTimer.current);
    uiPrefsTimer.current = setTimeout(() => {
      syncUiPrefsToServer();
    }, 800);
    return () => {
      if (uiPrefsTimer.current) clearTimeout(uiPrefsTimer.current);
    };
  }, [activeTab, composePlanId, composeTemplateId, compose, notificationPrefs, filterUnread, engagedSearch, engagedMinOpens, isAuthenticated, plannerLoaded, trackingProvider, trackingIntegration]);

  useEffect(() => {
    // Auto-refresh ALL inboxes when switching to the unified inbox tab
    if (activeTab === 3) {
      refreshUnifiedInbox(false).catch(() => {});
    }
  }, [activeTab, refreshUnifiedInbox]);

  useEffect(() => {
    if (onStatsChange) {
      onStatsChange({
        unread: stats?.unread ?? 0,
        totalSent: stats?.totalSent ?? 0,
      });
    }
  }, [stats, onStatsChange]);


  const setComposeBody = useCallback((html) => {
    setCompose((prev) => ({ ...prev, body: html || '' }));
  }, []);


  const playChime = useCallback(() => {
    if (!notificationPrefs.sound) return;
    try {
      // Soft bell made with Web Audio (two quick tones with fade-out)
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const now = ctx.currentTime;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.001, now);
      gain.gain.exponentialRampToValueAtTime(0.25, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.7);

      const tone = (freq, startOffset = 0, dur = 0.35) => {
        const osc = ctx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(freq, now + startOffset);
        osc.connect(gain);
        osc.start(now + startOffset);
        osc.stop(now + startOffset + dur);
      };

      tone(880, 0, 0.35); // A5
      tone(1175, 0.12, 0.3); // D6 chime

      gain.connect(ctx.destination);
    } catch {
      // Fallback to a tiny inline bell sample
      try {
        const audio = new Audio(
          'data:audio/ogg;base64,T2dnUwACAAAAAAAAAACBP5weAAAAAN+brS8BHgF2b3JiaXMAAAAAAkSsAAAAAAAAgD+coAAAAAAAAPoAAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAFYXQGPhZGVhbAIAAAAKAAAADAAAAGRhdGEAAAABAAABAgBQaW5rAAA='
        );
        audio.play().catch(() => {});
      } catch {
        /* ignore */
      }
    }
  }, [notificationPrefs.sound]);

  const triggerNotification = useCallback(
    (title, body) => {
      playChime();
      if (typeof window === 'undefined' || !('Notification' in window)) return;
      if (Notification.permission === 'granted') {
        new Notification(title, { body });
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then((perm) => {
          if (perm === 'granted') {
            new Notification(title, { body });
          }
        });
      }
    },
    [playChime]
  );

  const fetchExternalTrackingEvents = useCallback(async () => {
    if (!isAuthenticated || effectiveTrackingProvider !== 'mailsuite') {
      setExternalOpenCount(0);
      return;
    }

    const params = new URLSearchParams({
      since_id: String(externalTrackingLastIdRef.current || 0),
      limit: '20',
    });
    const res = await fetch(apiUrl(`/api/external_tracking.php?${params.toString()}`), {
      headers: authHeaders,
      credentials: 'include',
    });
    const payload = await res.json();
    if (!res.ok || !payload?.ok) {
      throw new Error(payload?.error || `External tracking fetch failed (${res.status})`);
    }

    const events = Array.isArray(payload.events) ? payload.events : [];
    const sortedAsc = [...events].sort((a, b) => (a.id || 0) - (b.id || 0));
    const lastId = sortedAsc.length ? sortedAsc[sortedAsc.length - 1].id : externalTrackingLastIdRef.current;
    if (lastId) {
      externalTrackingLastIdRef.current = Math.max(externalTrackingLastIdRef.current, lastId);
    }

    setExternalOpenCount(Number(payload.totalOpens || 0));

    if (!externalTrackingInitializedRef.current) {
      externalTrackingInitializedRef.current = true;
      return;
    }

    if (notificationPrefs.opens && sortedAsc.length > 0) {
      sortedAsc.forEach((event) => {
        const target = event.recipient_email || 'recipient';
        const subject = event.subject ? ` - ${event.subject}` : '';
        const message = `${target} opened an email${subject}`;
        triggerNotification('MailSuite email opened', message);
        setFooterNotice({ open: true, message });
      });
    }
  }, [apiUrl, authHeaders, effectiveTrackingProvider, isAuthenticated, notificationPrefs.opens, triggerNotification]);

  useEffect(() => {
    const unreadCount = inbox.filter((m) => m.status === 'unread').length;
    const readNow = new Set(inbox.filter((m) => m.status !== 'unread').map((m) => m.id));
    const totalOpens = effectiveTrackingProvider === 'mailsuite'
      ? externalOpenCount
      : (trackingTotals?.totalOpens || 0);

    if (!notificationsInitializedRef.current) {
      prevUnreadCount.current = unreadCount;
      prevReadIds.current = readNow;
      prevTotalOpens.current = totalOpens;
      notificationsInitializedRef.current = true;
      return;
    }

    if (notificationPrefs.newEmail && unreadCount > prevUnreadCount.current) {
      const diff = unreadCount - prevUnreadCount.current;
      const msg = `?? ${diff} new message${diff > 1 ? 's' : ''} in unified inbox`;
      triggerNotification('New email received', msg);
      setFooterNotice({ open: true, message: msg });
    }
    prevUnreadCount.current = unreadCount;

    if (notificationPrefs.seen) {
      for (const id of readNow) {
        if (!prevReadIds.current.has(id)) {
          const msg = '?? A message was marked as read.';
          triggerNotification('Email read', msg);
          setFooterNotice({ open: true, message: msg });
          break;
        }
      }
    }
    prevReadIds.current = readNow;

    if (!trackingInitializedRef.current) {
      prevTotalOpens.current = totalOpens;
      trackingInitializedRef.current = true;
      return;
    }

    if (effectiveTrackingProvider !== 'mailsuite' && notificationPrefs.opens && totalOpens > prevTotalOpens.current) {
      const diff = totalOpens - prevTotalOpens.current;
      const msg = `?? ${diff} email${diff > 1 ? 's' : ''} were opened.`;
      triggerNotification('Email opened', msg);
      setFooterNotice({ open: true, message: msg });
    }
    prevTotalOpens.current = totalOpens;
  }, [effectiveTrackingProvider, externalOpenCount, inbox, notificationPrefs, triggerNotification, trackingTotals]);

  // Load saved message statuses FIRST, then fetch ALL inboxes
  const statusesLoadedRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  
  useEffect(() => {
    if (initialLoadDoneRef.current) return;
    initialLoadDoneRef.current = true;
    
    const init = async () => {
      try {
        await fetchMessageStatuses();
        statusesLoadedRef.current = true;
      } catch (e) {
        console.warn('Failed to load message statuses:', e);
      }
      // Fetch ALL accounts for true unified inbox
      refreshUnifiedInbox(false).catch(() => {});
    };
    init();
  }, [fetchMessageStatuses, refreshUnifiedInbox]);

  // Poll ALL inboxes every 30s for background refresh
  useEffect(() => {
    const id = setInterval(() => {
      refreshUnifiedInbox(false).catch(() => {});
    }, 30000);
    return () => clearInterval(id);
  }, [refreshUnifiedInbox]);

  // Re-apply statuses when inbox changes (for any new messages)
  useEffect(() => {
    if (!statusesLoadedRef.current || inbox.length === 0) return;
    // Fetch latest statuses periodically to catch any updates
    fetchMessageStatuses()
      .then((statusMap) => {
        if (statusMap && Object.keys(statusMap).length > 0) {
          applyMessageStatuses(statusMap);
        }
      })
      .catch(() => {});
  }, [inbox.length, fetchMessageStatuses, applyMessageStatuses]);

  // Poll tracking totals every 60s for seen-email notifications
  useEffect(() => {
    const run = () => {
      if (effectiveTrackingProvider === 'mailsuite') {
        fetchExternalTrackingEvents().catch(() => {});
        return;
      }
      fetchTrackingTotals()
        .then((stats) => setTrackingTotals(stats))
        .catch(() => {});
    };
    run();
    const id = setInterval(run, 60000);
    return () => clearInterval(id);
  }, [effectiveTrackingProvider, fetchExternalTrackingEvents, fetchTrackingTotals]);

  // Light-weight md5 for gravatar (adapted for small payloads)
  const md5 = useCallback((str = '') => {
    const utf8 = new TextEncoder().encode(str);
    const rhex = (n) => {
      const s = '0123456789abcdef';
      return s[(n >> 4) & 0x0f] + s[n & 0x0f];
    };
    const add = (x, y) => (((x & 0xffff) + (y & 0xffff)) | 0) + ((((x >> 16) + (y >> 16)) & 0xffff) << 16);
    const rol = (x, c) => (x << c) | (x >>> (32 - c));
    const cmn = (q, a, b, x, s, t) => add(rol(add(add(a, q), add(x, t)), s), b);
    const ff = (a, b, c, d, x, s, t) => cmn((b & c) | (~b & d), a, b, x, s, t);
    const gg = (a, b, c, d, x, s, t) => cmn((b & d) | (c & ~d), a, b, x, s, t);
    const hh = (a, b, c, d, x, s, t) => cmn(b ^ c ^ d, a, b, x, s, t);
    const ii = (a, b, c, d, x, s, t) => cmn(c ^ (b | ~d), a, b, x, s, t);
    const data = Array.from(utf8);
    const origLen = data.length * 8;
    data.push(0x80);
    while ((data.length % 64) !== 56) data.push(0);
    for (let i = 0; i < 8; i++) data.push((origLen >>> (8 * i)) & 0xff);
    const words = [];
    for (let i = 0; i < data.length; i += 4) {
      words.push(data[i] | (data[i + 1] << 8) | (data[i + 2] << 16) | (data[i + 3] << 24));
    }
    let a = 1732584193;
    let b = -271733879;
    let c = -1732584194;
    let d = 271733878;
    for (let i = 0; i < words.length; i += 16) {
      const [oa, ob, oc, od] = [a, b, c, d];
      a = ff(a, b, c, d, words[i], 7, -680876936);
      d = ff(d, a, b, c, words[i + 1], 12, -389564586);
      c = ff(c, d, a, b, words[i + 2], 17, 606105819);
      b = ff(b, c, d, a, words[i + 3], 22, -1044525330);
      a = ff(a, b, c, d, words[i + 4], 7, -176418897);
      d = ff(d, a, b, c, words[i + 5], 12, 1200080426);
      c = ff(c, d, a, b, words[i + 6], 17, -1473231341);
      b = ff(b, c, d, a, words[i + 7], 22, -45705983);
      a = ff(a, b, c, d, words[i + 8], 7, 1770035416);
      d = ff(d, a, b, c, words[i + 9], 12, -1958414417);
      c = ff(c, d, a, b, words[i + 10], 17, -42063);
      b = ff(b, c, d, a, words[i + 11], 22, -1990404162);
      a = ff(a, b, c, d, words[i + 12], 7, 1804603682);
      d = ff(d, a, b, c, words[i + 13], 12, -40341101);
      c = ff(c, d, a, b, words[i + 14], 17, -1502002290);
      b = ff(b, c, d, a, words[i + 15], 22, 1236535329);
      a = gg(a, b, c, d, words[i + 1], 5, -165796510);
      d = gg(d, a, b, c, words[i + 6], 9, -1069501632);
      c = gg(c, d, a, b, words[i + 11], 14, 643717713);
      b = gg(b, c, d, a, words[i], 20, -373897302);
      a = gg(a, b, c, d, words[i + 5], 5, -701558691);
      d = gg(d, a, b, c, words[i + 10], 9, 38016083);
      c = gg(c, d, a, b, words[i + 15], 14, -660478335);
      b = gg(b, c, d, a, words[i + 4], 20, -405537848);
      a = gg(a, b, c, d, words[i + 9], 5, 568446438);
      d = gg(d, a, b, c, words[i + 14], 9, -1019803690);
      c = gg(c, d, a, b, words[i + 3], 14, -187363961);
      b = gg(b, c, d, a, words[i + 8], 20, 1163531501);
      a = gg(a, b, c, d, words[i + 13], 5, -1444681467);
      d = gg(d, a, b, c, words[i + 2], 9, -51403784);
      c = gg(c, d, a, b, words[i + 7], 14, 1735328473);
      b = gg(b, c, d, a, words[i + 12], 20, -1926607734);
      a = hh(a, b, c, d, words[i + 5], 4, -378558);
      d = hh(d, a, b, c, words[i + 8], 11, -2022574463);
      c = hh(c, d, a, b, words[i + 11], 16, 1839030562);
      b = hh(b, c, d, a, words[i + 14], 23, -35309556);
      a = hh(a, b, c, d, words[i + 1], 4, -1530992060);
      d = hh(d, a, b, c, words[i + 4], 11, 1272893353);
      c = hh(c, d, a, b, words[i + 7], 16, -155497632);
      b = hh(b, c, d, a, words[i + 10], 23, -1094730640);
      a = hh(a, b, c, d, words[i + 13], 4, 681279174);
      d = hh(d, a, b, c, words[i], 11, -358537222);
      c = hh(c, d, a, b, words[i + 3], 16, -722521979);
      b = hh(b, c, d, a, words[i + 6], 23, 76029189);
      a = hh(a, b, c, d, words[i + 9], 4, -640364487);
      d = hh(d, a, b, c, words[i + 12], 11, -421815835);
      c = hh(c, d, a, b, words[i + 15], 16, 530742520);
      b = hh(b, c, d, a, words[i + 2], 23, -995338651);
      a = ii(a, b, c, d, words[i], 6, -198630844);
      d = ii(d, a, b, c, words[i + 7], 10, 1126891415);
      c = ii(c, d, a, b, words[i + 14], 15, -1416354905);
      b = ii(b, c, d, a, words[i + 5], 21, -57434055);
      a = ii(a, b, c, d, words[i + 12], 6, 1700485571);
      d = ii(d, a, b, c, words[i + 3], 10, -1894986606);
      c = ii(c, d, a, b, words[i + 10], 15, -1051523);
      b = ii(b, c, d, a, words[i + 1], 21, -2054922799);
      a = ii(a, b, c, d, words[i + 8], 6, 1873313359);
      d = ii(d, a, b, c, words[i + 15], 10, -30611744);
      c = ii(c, d, a, b, words[i + 6], 15, -1560198380);
      b = ii(b, c, d, a, words[i + 13], 21, 1309151649);
      a = ii(a, b, c, d, words[i + 4], 6, -145523070);
      d = ii(d, a, b, c, words[i + 11], 10, -1120210379);
      c = ii(c, d, a, b, words[i + 2], 15, 718787259);
      b = ii(b, c, d, a, words[i + 9], 21, -343485551);
      a = add(a, oa);
      b = add(b, ob);
      c = add(c, oc);
      d = add(d, od);
    }
    const out = new Uint32Array([a, b, c, d]);
    const bytes = new Uint8Array(out.buffer);
    return Array.from(bytes).map((b) => rhex(b)).join('');
  }, []);

  const resolveAvatar = useCallback(
    (msg) => {
      if (msg?.avatarUrl || msg?.photo) return msg.avatarUrl || msg.photo;
      const email = String(msg?.from || '').trim().toLowerCase();
      if (!email || !email.includes('@')) return '';
      if (gravatarCache.current[email]) return gravatarCache.current[email];
      const hash = md5(email);
      const url = `https://www.gravatar.com/avatar/${hash}?d=identicon&s=64`;
      gravatarCache.current[email] = url;
      return url;
    },
    [md5]
  );

  useEffect(() => {
    return () => {
      if (campaignPollRef.current) clearInterval(campaignPollRef.current);
    };
  }, []);

  const applyTemplateToCompose = (tmpl) => {
    setCompose((prev) => ({
      ...prev,
      subject: tmpl.subject || '',
      body: tmpl.body || '',
    }));
    setComposeTemplateId(tmpl.id || '');
    setActiveTab(0);
    setNotice({ open: true, type: 'info', message: `Template "${tmpl.name}" applied to Compose.`, scope: 'compose' });
  };

  const savePlanToDatabase = async (record) => {
    if (!isAuthenticated || !token) {
      setNotice({ open: true, type: 'error', message: 'Please login to save plans.', scope: 'campaign' });
      return null;
    }
    const currentStatus = record.status || 'draft';
    const existingRunStats = { ...(record.strategy?.runStats || {}) };
    if (currentStatus === 'scheduled' || currentStatus === 'draft') {
      delete existingRunStats.status;
      delete existingRunStats.failedRecipients;
      delete existingRunStats.trackingWarnings;
      if (currentStatus === 'scheduled') {
        delete existingRunStats.processed;
        delete existingRunStats.sent;
        delete existingRunStats.failed;
        delete existingRunStats.lastRunAt;
      }
    }
    const resolvedAccountIds = expandAccountSelection(record.strategy?.selectedAccounts || []);
    const strategy = {
      ...(record.strategy || {}),
      useMcRange: !!record.useMcRange,
      mcStartNumber: record.mcStartNumber || '',
      mcEndNumber: record.mcEndNumber || '',
      targetActiveCarriers: record.targetActiveCarriers !== false,
      targetInactiveCarriers: !!record.targetInactiveCarriers,
      targetBrokers: !!record.targetBrokers,
      resolvedAccountIds,
      runStats: existingRunStats,
      meta: {
        description: record.description || '',
        scheduledDate: record.scheduledDate || null,
        timezone: record.timezone || 'UTC',
        segmentType: record.segmentType || 'all',
        sendingFrequency: record.sendingFrequency || 'immediate',
        recipientLimit: record.recipientLimit || '',
      },
    };
    const res = await fetch(apiUrl('/api/campaign_plans.php?action=save'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      credentials: 'include',
      body: JSON.stringify({
        id: record.id || '',
        name: record.name,
        strategy,
        templates: record.templates || [],
        recipients: record.recipientRows || [],
        recipientFileName: record.recipientFileName || '',
        status: record.status || 'draft',
      }),
    });
    const payload = await res.json();
    if (!res.ok || payload?.error) {
      throw new Error(payload?.error || 'Failed to save plan');
    }
    return payload?.plan ? normalizePlanRecord(payload.plan) : null;
  };

  const deletePlanFromDatabase = async (planId) => {
    if (!isAuthenticated || !token) {
      setNotice({ open: true, type: 'error', message: 'Please login to delete plans.', scope: 'campaign' });
      return;
    }
    const res = await fetch(apiUrl('/api/campaign_plans.php?action=delete'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      credentials: 'include',
      body: JSON.stringify({ id: planId }),
    });
    const payload = await res.json();
    if (!res.ok || payload?.error) {
      throw new Error(payload?.error || 'Failed to delete plan');
    }
  };

  const updatePlanStatus = async (planId, status) => {
    setSavedPlans((prev) =>
      prev.map((p) => (p.id === planId ? { ...p, status } : p))
    );
    const plan = savedPlans.find((p) => p.id === planId);
    if (!plan) return;
    const updated = { ...plan, status, updatedAt: new Date().toISOString() };
    try {
      const saved = await savePlanToDatabase(updated);
      if (saved) {
        upsertPlanRecord(saved);
      }
    } catch (error) {
      console.error('Error saving plan status:', error);
      setNotice({ open: true, type: 'error', message: 'Failed to save plan status.', scope: 'campaign' });
    }
  };

  const savePlanRunStats = async (planId, runStats) => {
    const plan = savedPlans.find((p) => p.id === planId);
    if (!plan) return;
    const updated = {
      ...plan,
      status: runStats?.status || plan.status,
      strategy: { ...(plan.strategy || {}), runStats: { ...(plan.strategy?.runStats || {}), ...runStats } },
      updatedAt: new Date().toISOString(),
    };
    try {
      const saved = await savePlanToDatabase(updated);
      if (saved) {
        upsertPlanRecord(saved);
      }
    } catch (error) {
      console.error('Error saving run stats:', error);
    }
  };

  const fetchCampaignStats = useCallback(
    async (planId) => {
      if (!isAuthenticated || !token || !planId) return null;
      const res = await fetch(apiUrl(`/api/campaign_stats.php?campaign_id=${encodeURIComponent(planId)}`), {
        headers: authHeaders,
        credentials: 'include',
      });
      const payload = await res.json();
      if (!res.ok || payload?.error || !payload?.ok) {
        throw new Error(payload?.error || 'Failed to load campaign stats');
      }
      return payload;
    },
    [apiUrl, authHeaders, isAuthenticated, token]
  );

  const loadPlannerStats = useCallback(async () => {
    if (!isAuthenticated || !token || !savedPlans.length) return;
    try {
      const statsEntries = await Promise.all(
        savedPlans.map(async (plan) => {
          if (!plan?.id) return null;
          const stats = await fetchCampaignStats(plan.id);
          return { planId: plan.id, stats };
        })
      );
      setPlannerStats((prev) => {
        const next = { ...prev };
        statsEntries.forEach((entry) => {
          if (!entry?.planId || !entry?.stats) return;
          const existing = next[entry.planId] || {};
          const remoteSent = Number(entry.stats.sent ?? 0) || 0;
          const remoteOpens = Number(entry.stats.opens ?? 0) || 0;
          const remoteClicks = Number(entry.stats.clicks ?? 0) || 0;
          const remoteFailed = existing.failed ?? 0;
          next[entry.planId] = {
            ...existing,
            totalRecipients: Math.max(existing.totalRecipients || 0, remoteSent),
            sent: remoteSent,
            failed: remoteFailed,
            opens: remoteOpens,
            clicks: remoteClicks,
            lastOpenedAt: entry.stats.lastOpenedAt ?? existing.lastOpenedAt ?? null,
          };
        });
        savedPlans.forEach((plan) => {
          const runStats = plan?.strategy?.runStats;
          if (!runStats) return;
          const existing = next[plan.id] || {};
          next[plan.id] = {
            ...existing,
            totalRecipients: Math.max(existing.totalRecipients || 0, runStats.totalRecipients || 0),
            sent: Math.max(existing.sent || 0, runStats.sent || 0),
            failed: Math.max(existing.failed || 0, runStats.failed || 0),
            failedRecipients: runStats.failedRecipients || existing.failedRecipients || [],
            trackingWarnings: runStats.trackingWarnings || existing.trackingWarnings || [],
            processed: Math.max(existing.processed || 0, runStats.processed || 0),
            lastRunAt: runStats.lastRunAt ?? existing.lastRunAt ?? null,
            lastMessage: runStats.lastMessage ?? existing.lastMessage ?? '',
            events: runStats.events || existing.events || [],
            discovery: runStats.discovery || existing.discovery || null,
            opens: Math.max(existing.opens || 0, runStats.opens || 0),
            clicks: Math.max(existing.clicks || 0, runStats.clicks || 0),
            lastOpenedAt: runStats.lastOpenedAt ?? existing.lastOpenedAt ?? null,
          };
        });
        return next;
      });
    } catch (error) {
      console.error('Failed to load campaign stats:', error);
    }
  }, [fetchCampaignStats, isAuthenticated, savedPlans, token]);

  useEffect(() => {
    loadPlannerStats().catch(() => {});
  }, [loadPlannerStats]);

  useEffect(() => {
    if (!isAuthenticated || !token || activeTab !== 1 || !savedPlans.length) return undefined;
    const intervalId = setInterval(() => {
      loadPlannerStats().catch(() => {});
      refreshCampaignPlans().catch(() => {});
    }, 30000);
    return () => clearInterval(intervalId);
  }, [activeTab, isAuthenticated, loadPlannerStats, refreshCampaignPlans, savedPlans.length, token]);

  const savePlan = async (name) => {
    if (!name.trim()) {
      setNotice({ open: true, type: 'error', message: 'Plan name is required.', scope: 'campaign' });
      return;
    }
    const record = normalizePlanRecord(
      {
        id: `plan-${Date.now()}`,
        name: name.trim(),
        strategy: { ...campaignPlan, perAccountLimits: campaignPlan.perAccountLimits || {} },
        templates: campaignTemplates,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
    );
    upsertPlanRecord(record);
    setNotice({ open: true, type: 'success', message: 'Plan saved.', scope: 'campaign' });
    // Clear selections so the next plan starts fresh
    setCampaignPlan((prev) => ({
      ...prev,
      selectedTemplates: [],
      selectedAccounts: [],
      perAccountLimits: {},
    }));

    try {
      const saved = await savePlanToDatabase(record);
      if (saved) {
        upsertPlanRecord(saved);
      }
      setNotice({ open: true, type: 'success', message: 'Plan saved to database.', scope: 'campaign' });
    } catch (error) {
      console.error('Error saving plan:', error);
      setNotice({ open: true, type: 'error', message: 'Failed to save plan to database.', scope: 'campaign' });
    }
  };

  const applyPlan = (plan) => {
    setCampaignPlan({ ...defaultCampaignPlan, ...plan.strategy, perAccountLimits: plan.strategy?.perAccountLimits || {} });
    setNotice({ open: true, type: 'success', message: `Plan "${plan.name}" loaded.`, scope: 'campaign' });
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const findEmailForRow = (row) => {
    if (!row) return '';
    if (typeof row === 'string') {
      const emails = normalizeEmails(row);
      return emails[0] || '';
    }
    if (Array.isArray(row)) {
      for (const cell of row) {
        const emails = normalizeEmails(cell || '');
        if (emails[0]) return emails[0];
      }
      return '';
    }
    if (typeof row !== 'object') return '';
    const keys = Object.keys(row);
    const emailKey =
      keys.find((k) => /email/i.test(k)) ||
      keys.find((k) => /e-mail/i.test(k)) ||
      keys.find((k) => /mail/i.test(k));
    if (emailKey) {
      const emails = normalizeEmails(row[emailKey] || '');
      if (emails[0]) return emails[0];
    }
    for (const value of Object.values(row)) {
      const emails = normalizeEmails(value || '');
      if (emails[0]) return emails[0];
    }
    return '';
  };

  const countRecipientsInRows = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return 0;
    let count = 0;
    rows.forEach((row) => {
      const email = findEmailForRow(row);
      if (email) count += 1;
    });
    return count;
  };

  const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const applyMerge = (text, row) => {
    if (!text || !row) return text || '';
    let result = String(text);
    const variants = new Map();
    const pick = (...keys) => {
      for (const key of keys) {
        const value = row?.[key];
        if (value !== undefined && value !== null && String(value).trim() !== '') {
          return String(value);
        }
      }
      return '';
    };
    const register = (key, value) => {
      const raw = String(key || '').trim();
      if (!raw) return;
      const normalized = raw.toLowerCase();
      const compact = normalized.replace(/[^a-z0-9]+/g, '');
      const underscored = normalized.replace(/[^a-z0-9]+/g, '_');
      [raw, normalized, compact, underscored].forEach((candidate) => {
        if (candidate && !variants.has(candidate)) {
          variants.set(candidate, value);
        }
      });
    };
    Object.entries(row).forEach(([key, value]) => {
      register(key, value);
    });
    const primaryName = pick('Primary Contact', 'primary_contact', 'name', 'Name');
    const companyName = pick('DBA', 'dba', 'Legal Name', 'legal_name', 'company', 'Company');
    const firstName = primaryName ? primaryName.split(/\s+/)[0] : '';
    register('name', primaryName || companyName);
    register('full_name', primaryName || companyName);
    register('first_name', firstName);
    register('company', companyName);
    register('company_name', companyName);
    register('dot_number', pick('DOT', 'dot'));
    register('mc_number', pick('MC', 'mc'));
    register('phone_number', pick('Phone', 'phone', 'Cell Phone', 'cell_phone'));
    register('email_address', pick('Email', 'email'));
    register('state', pick('State', 'state', 'State(Physical)', 'state_physical', 'State(Mailing)', 'state_mailing'));
    variants.forEach((value, key) => {
      const token = new RegExp(`{{\\s*${escapeRegExp(key)}\\s*}}`, 'gi');
      result = result.replace(token, String(value ?? ''));
    });
    const nameRaw =
      row.first_name ||
      row.firstname ||
      row['first name'] ||
      row.first ||
      row.name ||
      '';
    if (nameRaw) {
      const first = String(nameRaw).split(/\s+/)[0];
      result = result.replace(/{{\s*first_name\s*}}/gi, first);
      result = result.replace(/{{\s*name\s*}}/gi, String(nameRaw));
    }
    return result;
  };

  const isNetworkSendError = (error) => {
    const msg = String(error?.message || '').toLowerCase();
    return msg.includes('failed to fetch')
      || msg.includes('networkerror')
      || msg.includes('cors');
  };

  const loadPlanToCompose = (plan) => {
    applyPlan(plan);
    const firstTmplId = plan.strategy?.selectedTemplates?.[0];
    if (firstTmplId) {
      const tmpl = campaignTemplates.find((t) => t.id === firstTmplId);
      if (tmpl) {
        applyTemplateToCompose(tmpl);
        setComposeTemplateId(firstTmplId);
      }
    }
    setActiveTab(0);
  };

  const handlePlanEdit = (plan) => {
    // Load plan data into wizard for editing
    setEditingPlan(plan);
    // CampaignPlanner will handle opening the wizard with this data
  };

  const handlePauseCampaign = async (campaignId) => {
    if (!campaignId) return;
    
    try {
      const apiPath = apiUrl('/api/campaigns.php');
      const res = await fetch(apiPath, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({
          action: 'pause',
          campaign_id: campaignId
        }),
      });
      
      const payload = await res.json();
      
      if (res.ok && payload?.ok) {
        setNotice({ 
          open: true, 
          type: 'success', 
          message: 'Campaign paused successfully. Worker will stop processing.', 
          scope: 'campaign' 
        });
        
        // Stop polling if this is the current running campaign
        if (campaignRun.id === campaignId) {
          if (campaignPollRef.current) {
            clearInterval(campaignPollRef.current);
          }
          setCampaignRun({ id: null, counts: {}, running: false });
        }
      } else {
        throw new Error(payload?.error || 'Failed to pause campaign');
      }
    } catch (err) {
      setNotice({ 
        open: true, 
        type: 'error', 
        message: err?.message || 'Failed to pause campaign', 
        scope: 'campaign' 
      });
    }
  };

  const handleResumeCampaign = async (campaignId) => {
    if (!campaignId) return;
    
    try {
      const apiPath = apiUrl('/api/campaigns.php');
      const res = await fetch(apiPath, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({
          action: 'resume',
          campaign_id: campaignId
        }),
      });
      
      const payload = await res.json();
      
      if (res.ok && payload?.ok) {
        setNotice({ 
          open: true, 
          type: 'success', 
          message: 'Campaign resumed! Worker will continue processing.', 
          scope: 'campaign' 
        });
      } else {
        throw new Error(payload?.error || 'Failed to resume campaign');
      }
    } catch (err) {
      setNotice({ 
        open: true, 
        type: 'error', 
        message: err?.message || 'Failed to resume campaign', 
        scope: 'campaign' 
      });
    }
  };

  const startCampaignFromCompose = async () => {
    if (!compose.recipients || compose.recipients.length === 0) {
      setNotice({ open: true, type: 'error', message: 'Add recipients first.', scope: 'compose' });
      return;
    }
    const activePlan = composePlanId ? savedPlans.find((p) => p.id === composePlanId) : null;
    const strategy = activePlan?.strategy || campaignPlan;
    const strategyTemplateIds = strategy.selectedTemplates || [];
    let templatesPool = composeTemplateId
      ? campaignTemplates.filter((t) => t.id === composeTemplateId)
      : campaignTemplates.filter((t) => strategyTemplateIds.includes(t.id));
    // If the saved plan carries full template objects but no IDs matched current library, fall back to those
    if (!templatesPool.length && activePlan?.templates?.length) {
      templatesPool = activePlan.templates;
    }
    if (!templatesPool.length) {
      setNotice({ open: true, type: 'error', message: 'Select a template or choose a plan with templates.', scope: 'compose' });
      return;
    }
    const requestedSelection = (strategy.selectedAccounts || []).length > 0;
    const accountIdsForPlan = expandAccountSelection(strategy.selectedAccounts || []);
    let accountsPayloadBase = accountIdsForPlan.length
      ? accounts.filter((a) => accountIdsForPlan.includes(a.id))
      : accounts;
    // If nothing matched (e.g., plan selected but accounts filter empty), fall back to all available accounts
    if (!accountsPayloadBase.length && accounts.length) {
      accountsPayloadBase = accounts;
    }
    const accountsPayload = accountsPayloadBase.map((a) => ({
      ...a,
      dailyLimit: (strategy.perAccountLimits || {})[a.id] || a.dailyLimit || strategy.perAccountDaily,
    }));
    if (!accountsPayload.length) {
      setNotice({ open: true, type: 'error', message: 'Select an account.', scope: 'compose' });
      return;
    }
    if (requestedSelection && !accountIdsForPlan.length) {
      setNotice({ open: true, type: 'error', message: 'Selected group has no accounts.', scope: 'compose' });
      return;
    }
    setCampaignRun({ id: null, counts: {}, running: true });
    try {
      const apiPath = apiUrl('/api/campaigns.php');
      if (!apiPath) {
        throw new Error('API base URL is missing. Set REACT_APP_API_BASE (or REACT_APP_API_BASE_URL) to your PHP host.');
      }
      const res = await fetch(apiPath, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
          body: JSON.stringify({
            action: 'create',
            campaign_name: `Campaign ${new Date().toLocaleString()}`,
            templates: templatesPool,
            accounts: accountsPayload,
            recipients: compose.recipients,
            settings: {
              minDelaySec: strategy.minDelaySec,
              maxDelaySec: strategy.maxDelaySec,
              perAccountDaily: strategy.perAccountDaily,
              rotateAccounts: strategy.rotateAccounts,
              shuffleTemplates: strategy.shuffleTemplates,
              trackOpens: campaignTrackOpens,
            },
          }),
        });
      const resClone = res.clone();
      let payload;
      try {
        payload = await res.json();
      } catch (e) {
        const text = await resClone.text();
        throw new Error(`Unexpected response from server (create): ${text.slice(0, 200)}`);
      }
      if (!res.ok || !payload?.ok) {
        const text = await resClone.text();
        throw new Error(payload?.error || text || 'Campaign create failed');
      }
      setCampaignRun({ id: payload.campaignId, counts: {}, running: true });
      
      // ✅ Campaign is now scheduled!
      // Backend worker (via cron) will process it automatically
      // User can close browser - emails keep sending!
      setNotice({ 
        open: true, 
        type: 'success', 
        message: `Campaign queued! Backend worker will process ${payload.total_recipients} emails. You can close browser.`, 
        scope: 'compose' 
      });
      
      if (isAuthenticated) {
        adminApi.logActivity({ type: 'email_campaign_created', source: 'campaign', metadata: { campaignId: payload.campaign_id } }, token).catch(() => {});
      }
      
      // Poll campaign status to show progress
      if (campaignPollRef.current) clearInterval(campaignPollRef.current);
      campaignPollRef.current = setInterval(async () => {
        try {
          const statRes = await fetch(apiUrl(`/api/campaigns.php?action=status&campaign_id=${payload.campaign_id}`), {
            headers: authHeaders,
            credentials: 'include',
          });
          let stat;
          try {
            stat = await statRes.json();
          } catch (e) {
            const txt = await statRes.text();
            throw new Error(`Unexpected response from server (status): ${txt.slice(0, 120)}`);
          }
          if (stat?.ok) {
            const counts = stat.counts || {};
            
            setCampaignRun((prev) => ({ 
              ...prev, 
              counts: {
                sent: counts.sent || 0,
                opened: counts.opened || 0,
                clicked: counts.clicked || 0,
                failed: counts.failed || 0,
                pending: counts.pending || 0,
                sending: counts.sending || 0,
                total: counts.total || payload.total_recipients || 0
              }, 
              running: stat.campaign?.status !== 'sent' && stat.campaign?.status !== 'completed' && stat.campaign?.status !== 'failed',
              id: payload.campaign_id 
            }));
            
            const pending = counts.pending || 0;
            const processing = counts.sending || 0;
            const sent = counts.sent || 0;
            const total = counts.total || payload.total_recipients || 0;
            
            // Stop polling when campaign is complete
            if (stat.campaign?.status === 'completed' || stat.campaign?.status === 'sent' || stat.campaign?.status === 'failed') {
              clearInterval(campaignPollRef.current);
              setCampaignRun((prev) => ({ ...prev, running: false }));
              setNotice({ 
                open: true, 
                type: sent > 0 ? 'success' : 'error', 
                message: `Campaign completed! Sent: ${sent}/${total}, Opened: ${counts.opened || 0}, Clicked: ${counts.clicked || 0}${counts.failed ? `, Failed: ${counts.failed}` : ''}`, 
                scope: 'compose' 
              });
              return;
            }
            
            // Stop polling when all emails processed
            if (pending === 0 && processing === 0 && sent >= total && total > 0) {
              clearInterval(campaignPollRef.current);
              setCampaignRun((prev) => ({ ...prev, running: false }));
              setNotice({ 
                open: true, 
                type: 'success', 
                message: `Campaign completed! Sent: ${sent}/${total}, Opened: ${counts.opened || 0}, Clicked: ${counts.clicked || 0}`, 
                scope: 'compose' 
              });
              return;
            }
          }
        } catch (e) {
          // Continue polling even on error - worker is running!
        }
      }, 5000); // Poll every 5 seconds
    } catch (err) {
      setCampaignRun({ id: null, counts: {}, running: false });
      const msg =
        err?.message === 'Failed to fetch'
          ? 'Network error starting campaign. Check API base URL /api/campaign.php and CORS.'
          : err?.message || 'Campaign start failed';
      setNotice({ open: true, type: 'error', message: msg, scope: 'compose' });
    }
  };
  useEffect(() => {
    if (!sent.length) return;
    fetchTrackingTotals()
      .then((stats) => setTrackingTotals(stats))
      .catch(() => {});
  }, [sent, fetchTrackingTotals]);

  useEffect(() => {
    trackingCacheReadyRef.current = false;
    const cached = loadTrackingCache();
    if (cached?.items) {
      setTrackingData(cached.items);
      trackingCacheTimestampRef.current = cached.timestamp || 0;
      setLastSeenRefreshAt(cached.timestamp || null);
    } else {
      trackingCacheTimestampRef.current = cached?.timestamp || 0;
      setLastSeenRefreshAt(cached?.timestamp || null);
      setTrackingData((prev) => (prev.length ? [] : prev));
    }
    trackingCacheReadyRef.current = true;
  }, [loadTrackingCache]);

  useEffect(() => {
    if (!trackingCacheReadyRef.current || !isAuthenticated || !token) {
      return;
    }
    saveTrackingCache(trackingData);
  }, [trackingData, saveTrackingCache, isAuthenticated, token]);

  useEffect(() => {
    const active = inbox.find((m) => m.id === activeMessageId);
    if (!active) return;
    const normalizeSubject = (str = '') => {
      let out = str.trim();
      while (/^(re:|fw:|fwd:)\s*/i.test(out)) {
        out = out.replace(/^(re:|fw:|fwd:)\s*/i, '').trim();
      }
      return out.toLowerCase();
    };
    const subjectKey = normalizeSubject(active.subject || '');
    const relatedSent = sent.filter(
      (s) =>
        s.parentId === active.id ||
        normalizeSubject(s.subject || '') === subjectKey
    );
    relatedSent.forEach((s) => {
      const mid = s.messageId || s.id;
      if (!mid || trackingMap[mid]) return;
      fetchTrackingStats(mid)
        .then((stats) => {
          setTrackingMap((prev) => ({ ...prev, [mid]: stats }));
        })
        .catch(() => {});
    });
  }, [activeMessageId, inbox, sent, trackingMap, fetchTrackingStats]);

  const refreshSeenStats = React.useCallback(async () => {
    if (!sent.length) return {};
    const settled = await Promise.allSettled(
      sent.map(async (item) => {
        const mid = item.messageId || item.id;
        if (!mid) {
          return null;
        }
        const stats = await fetchTrackingStats(mid);
        return [mid, stats];
      })
    );

    const updates = {};
    settled.forEach((result) => {
      if (result.status !== 'fulfilled' || !result.value) {
        return;
      }
      const [mid, stats] = result.value;
      if (mid && stats) {
        updates[mid] = stats;
      }
    });

    if (Object.keys(updates).length) {
      setTrackingMap((prev) => ({ ...prev, ...updates }));
    }

    return updates;
  }, [sent, fetchTrackingStats]);

  const refreshTrackingListData = useCallback(async ({ force = false, limit = 200 } = {}) => {
    if (!isAuthenticated || !token) {
      setTrackingData((prev) => (prev.length ? [] : prev));
      setSeenError('');
      return [];
    }

    const now = Date.now();
    const cacheFresh = Boolean(
      !force &&
      trackingCacheTimestampRef.current &&
      now - trackingCacheTimestampRef.current < TRACKING_CACHE_TTL &&
      trackingData.length
    );

    if (cacheFresh) {
      return trackingData;
    }

    const payload = await fetchTrackingList(limit);
    const items = Array.isArray(payload?.items) ? payload.items : [];
    setTrackingData(items);
    trackingCacheTimestampRef.current = Date.now();
    saveTrackingCache(items);
    setLastSeenRefreshAt(Date.now());
    return items;
  }, [fetchTrackingList, isAuthenticated, saveTrackingCache, token, trackingData]);

  const refreshSeenData = useCallback(async ({ force = false, showLoading = true } = {}) => {
    if (showLoading) {
      setSeenLoading(true);
    }
    setSeenError('');

    try {
      const tasks = [
        refreshTrackingListData({ force }),
        fetchTrackingTotals().then((stats) => setTrackingTotals(stats)),
      ];

      if (sent.length) {
        tasks.push(refreshSeenStats());
      }

      const results = await Promise.allSettled(tasks);
      const firstFailure = results.find((result) => result.status === 'rejected');

      if (firstFailure?.status === 'rejected') {
        setSeenError(firstFailure.reason?.message || 'Failed to refresh seen emails.');
      }
    } finally {
      if (showLoading) {
        setSeenLoading(false);
      }
    }
  }, [fetchTrackingTotals, refreshSeenStats, refreshTrackingListData, sent.length]);

  useEffect(() => {
    if (activeTab === 4) {
      refreshSeenData({ force: false, showLoading: true }).catch(() => {});
      setSeenPage(1);
    }
  }, [activeTab, refreshSeenData]);

  useEffect(() => {
    if (activeTab !== 4 || !isAuthenticated || !token) {
      return;
    }
    const intervalId = setInterval(() => {
      refreshSeenData({ force: true, showLoading: false }).catch(() => {});
    }, 30000);
    return () => clearInterval(intervalId);
  }, [activeTab, isAuthenticated, refreshSeenData, token]);

  // AI Email Generation Handler for Templates
  const handleTemplateAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      alert('Please enter what you want the email template to be about');
      return;
    }
    
    setAiLoading(true);
    try {
      const result = await generateEmailWithAI(aiPrompt, {
        tone: aiTone,
        length: aiLength,
        industry: 'logistics',
        includeGreeting: true,
        includeClosing: true,
        avoidSpam: true,
        mergeTags: mergeTagOptions,
      });
      
      if (result.success) {
        // Extract subject from AI response if exists
        const lines = result.email.split('\n');
        const subjectLine = lines.find(l => l.toLowerCase().includes('subject:'));
        
        if (subjectLine) {
          const subject = subjectLine.replace(/^.*subject:\s*/i, '').replace(/<[^>]*>/g, '').trim();
          const body = lines.filter(l => !l.toLowerCase().includes('subject:')).join('\n').trim();
          setTemplateForm(prev => ({ ...prev, subject, body }));
        } else {
          setTemplateForm(prev => ({ ...prev, body: result.email }));
        }
        
        setAiDialogOpen(false);
        setAiPrompt('');
        
        // Analyze spam for the generated content
        const analysis = analyzeSpamScore(templateForm.subject, result.email);
        setSpamAnalysis(analysis);
      } else {
        alert(`AI Generation Error: ${result.error}`);
      }
    } catch (error) {
      alert('AI generation failed: ' + error.message);
    } finally {
      setAiLoading(false);
    }
  };
  
  // Rewrite Template to Remove Spam Words
  const handleTemplateRewriteSpamFree = async () => {
    if (!templateForm.body) {
      alert('Write some template content first!');
      return;
    }
    
    setRewriteLoading(true);
    try {
      const result = await rewriteToAvoidSpam(templateForm.body, spamAnalysis?.highlightWords || []);
      
      if (result.success) {
        setTemplateForm(prev => ({ ...prev, body: result.email }));
        
        // Re-analyze spam score
        const analysis = analyzeSpamScore(templateForm.subject, result.email);
        setSpamAnalysis(analysis);
      } else {
        alert(`Rewrite failed: ${result.error}`);
      }
    } catch (error) {
      alert('Rewrite failed: ' + error.message);
    } finally {
      setRewriteLoading(false);
    }
  };

  // Analyze spam whenever template subject/body changes
  useEffect(() => {
    if (templateForm.subject || templateForm.body) {
      const analysis = analyzeSpamScore(templateForm.subject, templateForm.body);
      setSpamAnalysis(analysis);
    } else {
      setSpamAnalysis(null);
    }
  }, [templateForm.subject, templateForm.body]);

  const saveTemplate = async (payload = null, options = {}) => {
    const data = payload || templateForm;
    if (!data.name.trim() || !data.subject.trim() || !data.body.trim()) {
      setNotice({ open: true, type: 'error', message: 'Template name, subject, and body are required.', scope: 'campaign' });
      return;
    }
    const record = normalizeTemplateRecord(data, {
      id: data.id || `tmpl-${Date.now()}`,
      name: data.name.trim(),
      subject: data.subject.trim(),
      body: data.body.trim(),
      category: data.category || 'general',
      description: data.description || '',
      tags: Array.isArray(data.tags) ? data.tags : [],
      isFavorite: !!data.isFavorite,
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });

    if (options.skipRemote) {
      upsertTemplateRecord(record);
      return;
    }

    if (!isAuthenticated || !token) {
      setNotice({ open: true, type: 'error', message: 'Please login to save templates.', scope: 'campaign' });
      return;
    }

    try {
      const response = await templateApi.saveTemplate(record, token);
      const saved = normalizeTemplateRecord(response?.template || record, {
        ...record,
        updatedAt: new Date().toISOString(),
      });
      upsertTemplateRecord(saved);
      setTemplateForm({ id: '', name: '', subject: '', body: '' });
      setNotice({ open: true, type: 'success', message: 'Template saved successfully.', scope: 'campaign' });
    } catch (error) {
      console.error('Error saving template:', error);
      const message = error instanceof TemplateApiError
        ? error.message
        : 'Failed to save template to database.';
      setNotice({ open: true, type: 'error', message, scope: 'campaign' });
    }
  };

  const deleteTemplate = async (id, options = {}) => {
    setCampaignTemplates((prev) => prev.filter((t) => t.id !== id));
    if (options.skipRemote) return;
    if (!isAuthenticated || !token) {
      setNotice({ open: true, type: 'error', message: 'Please login to delete templates.', scope: 'campaign' });
      return;
    }
    try {
      await templateApi.deleteTemplate(id, token);
      setNotice({ open: true, type: 'success', message: 'Template deleted.', scope: 'campaign' });
    } catch (error) {
      console.error('Error deleting template:', error);
      const message = error instanceof TemplateApiError
        ? error.message
        : 'Failed to delete template.';
      setNotice({ open: true, type: 'error', message, scope: 'campaign' });
    }
  };

  const buildPlanPreview = () => {
    const templates = campaignTemplates.filter((t) => campaignPlan.selectedTemplates.includes(t.id));
    const acctIds = expandAccountSelection(campaignPlan.selectedAccounts);
    const poolIds = acctIds.length ? acctIds : accounts.map((a) => a.id);
    if (!templates.length || !poolIds.length) {
      setNotice({ open: true, type: 'error', message: 'Select at least one template and one account.', scope: 'campaign' });
      return;
    }
    const items = [];
    for (let i = 0; i < 10; i++) {
      const acctId = poolIds[campaignPlan.rotateAccounts ? i % poolIds.length : 0];
      const tmpl = campaignPlan.shuffleTemplates
        ? templates[Math.floor(Math.random() * templates.length)]
        : templates[i % templates.length];
      const delay =
        campaignPlan.minDelaySec +
        Math.floor(Math.random() * Math.max(1, campaignPlan.maxDelaySec - campaignPlan.minDelaySec + 1));
      items.push({
        idx: i + 1,
        account: accounts.find((a) => a.id === acctId)?.label || acctId,
        template: tmpl.name,
        delay,
      });
    }
    // preview removed in simplified planner
  };

  function stripHtmlAssets(html = '') {
    return String(html || '')
      .replace(/<img[^>]*>/gi, '')
      .replace(/(?:track_open\.php|tracking\/pixel\.php)[^"' ]*/gi, '');
  }

  function normalizeSubjectLine(str = '') {
    let out = String(str || '').trim();
    while (/^(re:|fw:|fwd:)\s*/i.test(out)) {
      out = out.replace(/^(re:|fw:|fwd:)\s*/i, '').trim();
    }
    return out.toLowerCase();
  }

  function formatRelativeTimestamp(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const diff = Date.now() - date.getTime();
    if (diff < 60 * 1000) return 'Just now';
    if (diff < 60 * 60 * 1000) return `${Math.max(1, Math.floor(diff / (60 * 1000)))}m ago`;
    if (diff < 24 * 60 * 60 * 1000) return `${Math.floor(diff / (60 * 60 * 1000))}h ago`;
    if (diff < 7 * 24 * 60 * 60 * 1000) {
      return date.toLocaleDateString('en', { weekday: 'short' });
    }
    return date.toLocaleDateString('en', { month: 'short', day: 'numeric' });
  }

  const cleanBody = useCallback((text = '') => {
    if (showRaw) return text;
    // Convert simple HTML bodies to text before stripping quoted blocks
    let plain = text;
    if (/<[a-z][\s\S]*>/i.test(text)) {
      const div = document.createElement('div');
      div.innerHTML = stripHtmlAssets(text);
      plain = div.innerText || div.textContent || text;
    }

    plain = String(plain)
      .replace(/\u00a0/g, ' ')
      .replace(/\r\n|\r/g, '\n')
      .replace(/On\s+[A-Z][a-z]{2},?\s+[A-Z][a-z]{2}\s+\d{1,2},\s+\d{4}[\s\S]*?\bwrote:\s*/gi, '\nOn ')
      .replace(/(-{2,}\s*Original Message\s*-{2,}[\s\S]*)$/i, '')
      .replace(/(Begin forwarded message:[\s\S]*)$/i, '')
      .replace(/(From:\s.*[\s\S]*)$/i, '')
      .replace(/(Sent from my .*)$/i, '');

    const inlineReplyHeader = plain.search(/\bOn\s+.+?\bwrote:\s*/i);
    if (inlineReplyHeader > 0) {
      plain = plain.slice(0, inlineReplyHeader);
    }

    const lines = String(plain).split(/\r?\n/);
    const filtered = [];
    for (const line of lines) {
      const trimmed = line.trim();
      // stop at typical reply headers
      if (/^on .*wrote:?$/i.test(trimmed)) break;
      if (/^on .*\bwrote:\s*/i.test(trimmed)) break;
      if (/^-{2,}\s*original message\s*-{2,}$/i.test(trimmed)) break;
      if (/^begin forwarded message:?$/i.test(trimmed)) break;
      if (/^>/.test(trimmed)) continue; // skip quoted lines
      if (/^from: /i.test(trimmed)) continue;
      if (/^to: /i.test(trimmed)) continue;
      if (/^cc: /i.test(trimmed)) continue;
      if (/^sent: /i.test(trimmed)) continue;
      if (/^date: /i.test(trimmed)) continue;
      if (/^subject: /i.test(trimmed)) continue;
      if (/^best regards[,]?$/i.test(trimmed)) break;
      if (/^kind regards[,]?$/i.test(trimmed)) break;
      if (/^thanks[,]?$/i.test(trimmed) && filtered.length > 1) break;
      if (/^sent from my /i.test(trimmed)) break;
      filtered.push(line);
    }
    const collapsed = filtered
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\s{3,}/g, '  ')
      .trim();
    return collapsed;
  }, [showRaw]);

  function htmlToText(html = '') {
    const temp = document.createElement('div');
    temp.innerHTML = stripHtmlAssets(html);
    return temp.innerText || temp.textContent || '';
  }

  const getMessagePreview = useCallback((message) => {
    const cleaned = cleanBody(message?.body || '');
    return cleaned.replace(/\s+/g, ' ').trim();
  }, [cleanBody]);
  const insertMergeTag = useCallback((token) => {
    if (!token) return;
    setCompose((prev) => ({ ...prev, body: `${prev.body || ''}${token}` }));
  }, []);

  const insertTemplateMarkup = useCallback((before, after = '') => {
    const textarea = templateBodyRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = templateForm.body || '';
    const selectedText = text.substring(start, end) || 'text';
    const newText =
      text.substring(0, start) + before + selectedText + after + text.substring(end);
    setTemplateForm((prev) => ({ ...prev, body: newText }));
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + before.length,
        start + before.length + selectedText.length
      );
    }, 0);
  }, [templateForm.body]);

  const applyTemplateFormat = useCallback(
    (format) => {
      switch (format) {
        case 'bold':
          insertTemplateMarkup('**', '**');
          break;
        case 'italic':
          insertTemplateMarkup('*', '*');
          break;
        case 'underline':
          insertTemplateMarkup('<u>', '</u>');
          break;
        case 'code':
          insertTemplateMarkup('```', '```');
          break;
        case 'quote':
          insertTemplateMarkup('> ', '');
          break;
        case 'link':
          insertTemplateMarkup('[Link](', ')');
          break;
        case 'heading':
          insertTemplateMarkup('## ', '');
          break;
        case 'bullet':
          insertTemplateMarkup('- ', '');
          break;
        case 'number':
          insertTemplateMarkup('1. ', '');
          break;
        case 'clear':
          setTemplateForm((prev) => ({ ...prev, body: '' }));
          break;
        default:
          break;
      }
    },
    [insertTemplateMarkup]
  );

  const handleEmailChange = (email) => {
    setAccountForm((prev) => ({ ...prev, fromEmail: email.trim() }));
    
    // Auto-detect provider
    const detectedProvider = detectProviderByEmail(email);
    setSelectedProviderTemplate(detectedProvider);
    
    // Auto-fill SMTP/IMAP settings if provider detected
    if (detectedProvider) {
      const template = applyProviderTemplate(email, detectedProvider);
      setAccountForm((prev) => ({
        ...prev,
        ...template,
      }));
    }
  };

  const handleProviderSelect = (provider) => {
    setSelectedProviderTemplate(provider);
    
    // Apply provider template
    const template = applyProviderTemplate(accountForm.fromEmail, provider);
    if (template) {
      setAccountForm((prev) => ({
        ...prev,
        ...template,
      }));
    }
    
    setShowProviderGuide(true);
  };

  const handleAccountSave = async () => {
    setSavingAccount(true);
    try {
      setAccountError('');
      const effectiveForm = { ...accountForm };
      if (!effectiveForm.fromEmail && effectiveForm.username) {
        effectiveForm.fromEmail = effectiveForm.username;
        setAccountForm((prev) => ({ ...prev, fromEmail: effectiveForm.fromEmail }));
      }
      if (!effectiveForm.fromEmail || !effectiveForm.host) {
        setAccountError('From email and SMTP host are required.');
        return false;
      }
      if (editingId) {
        const updated = { ...effectiveForm, id: editingId };
        updateAccount(editingId, effectiveForm);
        setAccountMessage('Account updated.');
      } else {
        const record = addAccount(effectiveForm);
        setEditingId(record.id);
        setAccountMessage('Account added.');
      }
      setNotice({ open: true, type: 'success', message: 'Account saved successfully!', scope: 'accounts' });
      return true;
    } catch (err) {
      const msg = err?.message || 'Failed to save account';
      setAccountError(msg);
      setNotice({ open: true, type: 'error', message: msg, scope: 'accounts' });
      console.error('[EmailWorkspace] Account save failed', err);
      return false;
    } finally {
      setSavingAccount(false);
    }
  };

  const handleAccountDelete = async (accountId) => {
    try {
      // Delete from local state
      deleteAccount(accountId);
      setNotice({ open: true, type: 'success', message: 'Account deleted successfully!', scope: 'accounts' });
    } catch (err) {
      const msg = err?.message || 'Delete failed';
      setNotice({ open: true, type: 'error', message: msg, scope: 'accounts' });
      console.error('[EmailWorkspace] Account delete failed', err);
    }
  };

  const handleAccountTest = async () => {
    setTestingAccount(true);
    try {
      const res = await testAccount(
        editingId
          ? { ...accountForm, id: editingId }
          : { ...accountForm, id: 'draft' }
      );
      setAccountMessage(res.message);
      setAccountError('');
      setTestPassed(true);
      setNotice({ open: true, type: 'success', message: res.message, scope: 'accounts' });
    } catch (err) {
      setAccountError(err.message || 'Unable to test connection');
      setTestPassed(false);
      setNotice({ open: true, type: 'error', message: err.message || 'Test failed', scope: 'accounts' });
    } finally {
      setTestingAccount(false);
    }
  };

  const handleInlineAccountTest = async (acc) => {
    try {
      const res = await testAccount(acc);
      const msg = res?.message || 'Connection successful';
      if (acc?.id) {
        updateAccount(acc.id, { status: 'connected', lastTestAt: new Date().toISOString() });
      }
      setNotice({ open: true, type: 'success', message: msg, scope: 'accounts' });
    } catch (err) {
      if (acc?.id) {
        updateAccount(acc.id, {
          status: 'failed',
          lastTestAt: new Date().toISOString(),
          lastTestError: err.message || 'Test failed',
        });
      }
      setNotice({ open: true, type: 'error', message: err.message || 'Test failed', scope: 'accounts' });
    }
  };

  const launchPlannerCampaign = async (planId) => {
    const plan = savedPlans.find((p) => p.id === planId);
    if (!plan) return;
    const useMcRange = !!plan.useMcRange;
    const templatesPool = (plan.templates && plan.templates.length)
      ? plan.templates
      : campaignTemplates.filter((t) => (plan.strategy?.selectedTemplates || []).includes(t.id));
    if (!templatesPool.length) {
      setNotice({ open: true, type: 'error', message: 'Select at least one template for this campaign.', scope: 'campaign' });
      return;
    }
    const rows = Array.isArray(plan.recipientRows) ? plan.recipientRows : [];
    if (!useMcRange && !rows.length) {
      setNotice({ open: true, type: 'error', message: 'Import a recipient file first.', scope: 'campaign' });
      return;
    }
    const accountIds = expandAccountSelection(plan.strategy?.selectedAccounts || []);
    const accountPool = accounts.filter((a) => accountIds.includes(a.id));
    if (!accountPool.length) {
      setNotice({ open: true, type: 'error', message: 'Select at least one account.', scope: 'campaign' });
      return;
    }

    if (useMcRange) {
      if (!String(plan.mcStartNumber || '').trim() || !String(plan.mcEndNumber || '').trim()) {
        setNotice({ open: true, type: 'error', message: 'Enter starting and ending MC numbers.', scope: 'campaign' });
        return;
      }
      if (!plan.targetActiveCarriers && !plan.targetInactiveCarriers && !plan.targetBrokers) {
        setNotice({ open: true, type: 'error', message: 'Select at least one carrier type.', scope: 'campaign' });
        return;
      }
      const estimatedTotal = Math.max(
        0,
        (Number(plan.mcEndNumber) || 0) - (Number(plan.mcStartNumber) || 0) + 1
      );
      updatePlanStatus(planId, 'scheduled');
      await savePlanRunStats(planId, {
        totalRecipients: estimatedTotal,
        processed: 0,
        sent: 0,
        failed: 0,
        failedRecipients: [],
        trackingWarnings: [],
        lastMessage: 'Campaign queued. Backend will search the MC range and send to matched recipients.',
        events: [{
          type: 'info',
          message: 'Campaign queued. Backend will search the MC range and send to matched recipients.',
          at: new Date().toISOString(),
        }],
        status: 'scheduled',
      });
      setPlannerStats((prev) => ({
        ...prev,
        [planId]: {
          totalRecipients: estimatedTotal,
          sent: 0,
          failed: 0,
          failedRecipients: [],
          trackingWarnings: [],
        },
      }));
      setNotice({
        open: true,
        type: 'success',
        message: 'Campaign queued. Backend will search the MC range, build merge-tag data, and send in background.',
        scope: 'campaign',
      });
      return;
    }

    updatePlanStatus(planId, 'running');

    const totalRecipients = countRecipientsInRows(rows);
    setPlannerStats((prev) => ({
      ...prev,
      [planId]: {
        totalRecipients,
        sent: 0,
        failed: 0,
        failedRecipients: [],
        trackingWarnings: [],
      },
    }));

    const strategy = plan.strategy || defaultCampaignPlan;
    const minDelayBase = Math.max(0, Number(strategy.minDelaySec || 0));
    const minDelay = strategy.enforceMinDelay ? Math.max(minDelayBase, 15) : minDelayBase;
    const maxDelay = Math.max(minDelay, Number(strategy.maxDelaySec || 0));
    const jitterSec = Math.max(0, Number(strategy.jitterSec || 0));
    const batchPauseEvery = Math.max(0, Number(strategy.batchPauseEvery || 0));
    const batchPauseSec = Math.max(0, Number(strategy.batchPauseSec || 0));
    const maxPerAccountPerHour = Math.max(0, Number(strategy.maxPerAccountPerHour || 0));
    const maxFailures = Math.max(0, Number(strategy.maxFailures || 0));
    const trackOpens = strategy.trackOpens !== false;
    const useRotation = strategy.rotateAccounts !== false;
    const sentByAccount = new Map();

    const pickAccount = async (index) => {
      if (!accountPool.length) return null;
      const poolSize = accountPool.length;
      const startIdx = useRotation ? index % poolSize : 0;
      const now = Date.now();
      if (!maxPerAccountPerHour) {
        return accountPool[startIdx];
      }
      let nextWaitMs = null;
      for (let offset = 0; offset < poolSize; offset += 1) {
        const idx = (startIdx + offset) % poolSize;
        const acct = accountPool[idx];
        const history = sentByAccount.get(acct.id) || [];
        const recent = history.filter((ts) => ts > now - 3600 * 1000);
        if (recent.length < maxPerAccountPerHour) {
          sentByAccount.set(acct.id, recent);
          return acct;
        }
        const earliest = Math.min(...recent);
        const waitMs = Math.max(0, earliest + 3600 * 1000 - now);
        if (nextWaitMs === null || waitMs < nextWaitMs) {
          nextWaitMs = waitMs;
        }
      }
      if (nextWaitMs && nextWaitMs > 0) {
        await sleep(nextWaitMs);
      }
      return accountPool[startIdx];
    };

    let sentCount = 0;
    let failedCount = 0;
    let failedRecipients = [];
    let trackingWarnings = [];
    let stoppedEarly = false;

    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      const email = findEmailForRow(row);
      if (!email) {
        failedCount += 1;
        failedRecipients = [
          ...failedRecipients,
          { email: '(missing email)', error: 'No email found in row' },
        ];
        setPlannerStats((prev) => ({
          ...prev,
          [planId]: {
            ...prev[planId],
            totalRecipients,
            sent: sentCount,
            failed: failedCount,
            failedRecipients,
          },
        }));
        continue;
      }

      const account = await pickAccount(i);
      if (!account) {
        failedCount += 1;
        failedRecipients = [
          ...failedRecipients,
          { email, error: 'No account available' },
        ];
        setPlannerStats((prev) => ({
          ...prev,
          [planId]: {
            ...prev[planId],
            totalRecipients,
            sent: sentCount,
            failed: failedCount,
            failedRecipients,
          },
        }));
        continue;
      }
        const template = strategy.shuffleTemplates
        ? templatesPool[Math.floor(Math.random() * templatesPool.length)]
        : templatesPool[i % templatesPool.length];
      const subject = applyMerge(template.subject || '', row);
      const bodyHtml = applyMerge(template.body || '', row);
      const bodyText = htmlToText(bodyHtml || '');

      try {
        const record = await sendEmail({
          subject,
          body: bodyText,
          bodyText,
          bodyHtml,
          recipients: [email],
          accountId: account.id,
          campaignId: planId,
          trackOpens: effectiveTrackingProvider === 'internal' ? trackOpens : false,
          trackingProvider: effectiveTrackingProvider,
          trackingIntegration,
        });
        if (record?.trackingError) {
          trackingWarnings = [
            ...trackingWarnings,
            { email, error: record.trackingError },
          ];
        }
        sentCount += 1;
        if (isAuthenticated) {
          adminApi.logActivity(
            { action: 'email_send', source: 'campaign', metadata: { campaignId: planId, count: 1 } },
            token
          ).catch(() => {});
        }
        if (maxPerAccountPerHour) {
          const history = sentByAccount.get(account.id) || [];
          history.push(Date.now());
          sentByAccount.set(account.id, history);
        }
      } catch (err) {
        if (isNetworkSendError(err)) {
          sentCount += 1;
          trackingWarnings = [
            ...trackingWarnings,
            { email, error: err?.message || 'Response blocked by browser' },
          ];
        } else {
          failedCount += 1;
          failedRecipients = [
            ...failedRecipients,
            { email, error: err?.message || 'Send failed' },
          ];
        }
      }

      setPlannerStats((prev) => ({
        ...prev,
        [planId]: {
          ...prev[planId],
          totalRecipients,
          sent: sentCount,
          failed: failedCount,
          failedRecipients,
          trackingWarnings,
        },
      }));

      if (maxFailures && failedCount >= maxFailures) {
        updatePlanStatus(planId, 'failed');
        setNotice({
          open: true,
          type: 'error',
          message: `Campaign stopped after ${failedCount} failures.`,
          scope: 'campaign',
        });
        stoppedEarly = true;
        await savePlanRunStats(planId, {
          totalRecipients,
          sent: sentCount,
          failed: failedCount,
          failedRecipients,
          trackingWarnings,
          lastRunAt: new Date().toISOString(),
          status: 'failed',
        });
        break;
      }

      const randomDelay =
        minDelay + Math.floor(Math.random() * Math.max(1, maxDelay - minDelay + 1));
      const delayMs = (randomDelay + (jitterSec ? Math.floor(Math.random() * (jitterSec + 1)) : 0)) * 1000;
      if (delayMs > 0) {
        await sleep(delayMs);
      }

      if (batchPauseEvery && batchPauseSec && (i + 1) % batchPauseEvery === 0) {
        await sleep(batchPauseSec * 1000);
      }
    }

    if (stoppedEarly) {
      return;
    }
    updatePlanStatus(planId, 'completed');
    await savePlanRunStats(planId, {
      totalRecipients,
      sent: sentCount,
      failed: failedCount,
      failedRecipients,
      trackingWarnings,
      lastRunAt: new Date().toISOString(),
      status: 'completed',
    });
    setNotice({
      open: true,
      type: failedCount ? 'warning' : 'success',
      message: `Campaign finished. Sent: ${sentCount}, Failed: ${failedCount}.`,
      scope: 'campaign',
    });
  };

  const handleBulkAccountTest = async () => {
    if (bulkTesting || visibleAccounts.length === 0) return;
    setBulkTesting(true);
    let success = 0;
    let failed = 0;
    for (const acc of visibleAccounts) {
      try {
        await testAccount(acc);
        updateAccount(acc.id, { status: 'connected', lastTestAt: new Date().toISOString() });
        success += 1;
      } catch (err) {
        updateAccount(acc.id, {
          status: 'failed',
          lastTestAt: new Date().toISOString(),
          lastTestError: err.message || 'Test failed',
        });
        failed += 1;
      }
    }
    setBulkTestSummary({ success, failed });
    const msg = `Bulk test finished. Success: ${success}, Failed: ${failed}.`;
    setNotice({ open: true, type: failed ? 'warning' : 'success', message: msg, scope: 'accounts' });
    setBulkTesting(false);
  };

  const handleAccountEdit = (acc) => {
    setAccountForm({
      label: acc.label || '',
      provider: acc.provider || 'SMTP',
      fromName: acc.fromName || '',
      fromEmail: acc.fromEmail || '',
      host: acc.host || '',
      port: acc.port || 587,
      secure: acc.secure !== false,
      username: acc.username || '',
      password: acc.password || '',
      imapHost: acc.imapHost || '',
      imapPort: acc.imapPort || 993,
      imapSecure: acc.imapSecure !== false,
      mailbox: acc.mailbox || 'INBOX',
      popHost: acc.popHost || '',
      popPort: acc.popPort || 995,
      popSecure: acc.popSecure !== false,
      dailyLimit: acc.dailyLimit || 0,
    });
    setEditingId(acc.id);
    setSelectedAccountId(acc.id);
    // Auto-detect provider from email domain or SMTP host so modal shows all fields
    const detected = detectProviderByEmail(acc.fromEmail) || detectProviderByHost(acc.host);
    setSelectedProviderTemplate(detected);
    setShowAdvanced(true);
    setTestPassed(true);
    setAccountMessage('');
    setAccountError('');
    setAccountModalOpen(true);
  };

  const handleRecipientsFromManual = () => {
    if (!compose.manualBlock.trim()) return;
    setCompose((prev) => ({
      ...prev,
      recipients: mergeRecipients(prev.recipients, prev.manualBlock.split(/[\n,;]+/)),
      manualBlock: '',
    }));
  };

  const handleFileImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadError('');
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      if (!rows.length) {
        throw new Error('No rows found in the uploaded file.');
      }
      const headers = (rows[0] || []).map((h, idx) => {
        const label = String(h || '').trim();
        return label || `Column ${idx + 1}`;
      });
      const dataRows = rows.slice(1).filter((row) => row.some((cell) => String(cell || '').trim() !== ''));
      const mappedRows = dataRows.map((row) => {
        const record = {};
        headers.forEach((key, idx) => {
          record[key] = row[idx] ?? '';
        });
        return record;
      });
      const emailKey =
        headers.find((key) => /email/i.test(key)) ||
        headers.find((key) => /e-mail/i.test(key)) ||
        headers.find((key) => /mail/i.test(key));
      const extracted = [];
      const byEmail = {};
      mappedRows.forEach((row) => {
        if (emailKey && row[emailKey]) {
          const emails = normalizeEmails(row[emailKey]);
          emails.forEach((email) => {
            extracted.push(email);
            byEmail[email] = row;
          });
        }
      });
      if (!extracted.length) {
        throw new Error('No emails found in the uploaded file.');
      }
      setComposeRecipientRows({ headers, rows: mappedRows, byEmail });
      setCompose((prev) => ({
        ...prev,
        recipients: mergeRecipients(prev.recipients, extracted),
      }));
    } catch (err) {
      setUploadError(err.message || 'Failed to read file');
    } finally {
      event.target.value = '';
    }
  };

  const handleLoadCarrierExportForCompose = () => {
    const record = loadCarrierExport();
    if (!record || !record.rows?.length) {
      setNotice({
        open: true,
        type: 'error',
        message: 'No carrier search export found. Please run a carrier search first.',
        scope: 'compose',
      });
      return;
    }

    const headers = record.headers || Object.keys(record.rows[0] || {});
    const emailKey =
      headers.find((key) => /email/i.test(key)) ||
      headers.find((key) => /e-mail/i.test(key)) ||
      headers.find((key) => /mail/i.test(key));

    const extracted = [];
    const byEmail = {};
    record.rows.forEach((row) => {
      const raw = emailKey ? row[emailKey] : row.Email || row.email || '';
      if (!raw) return;
      const emails = normalizeEmails(raw);
      emails.forEach((email) => {
        extracted.push(email);
        byEmail[email] = row;
      });
    });

    if (!extracted.length) {
      setNotice({
        open: true,
        type: 'error',
        message: 'Carrier export loaded, but no valid emails were found.',
        scope: 'compose',
      });
      return;
    }

    setComposeRecipientRows({ headers, rows: record.rows, byEmail });
    setCompose((prev) => ({
      ...prev,
      recipients: mergeRecipients(prev.recipients, extracted),
    }));
    setNotice({
      open: true,
      type: 'success',
      message: `Loaded ${extracted.length} recipients with merge tags.`,
      scope: 'compose',
    });
  };

  const handleSend = async () => {
    setSendError('');
    try {
      const composeText = htmlToText(compose.body || '');
      await sendEmail({
        subject: compose.subject,
        body: composeText,
        bodyText: composeText,
        bodyHtml: compose.body || '',
        recipients: compose.recipients,
        accountId: compose.accountId,
        perAccountDaily: campaignPlan.perAccountDaily,
        perAccountLimits: campaignPlan.perAccountLimits || {},
        mergeData: composeRecipientRows.byEmail || {},
        trackOpens: effectiveTrackingProvider === 'internal' ? compose.trackOpens !== false : false,
        trackingProvider: effectiveTrackingProvider,
        trackingIntegration,
      });
      setCompose((prev) => ({
        ...prev,
        subject: '',
        body: '',
        recipients: [],
        manualBlock: '',
      }));
      setComposeRecipientRows({ headers: [], rows: [], byEmail: {} });
      setNotice({
        open: true,
        type: 'success',
        message:
          effectiveTrackingProvider === 'mailsuite'
            ? 'Email sent. Internal tracking was skipped because MailSuite mode is selected.'
            : effectiveTrackingProvider === 'internal'
              ? 'Email sent successfully with internal tracking preference applied.'
              : 'Email sent successfully without tracking.',
        scope: 'compose',
      });
      if (quickComposeFlowActive && quickComposeReturnToSearch && typeof onQuickComposeSent === 'function') {
        setQuickComposeFlowActive(false);
        setTimeout(() => {
          onQuickComposeSent();
        }, 250);
      }
      if (isAuthenticated) {
        adminApi
          .logActivity(
            { action: 'email_send', source: 'compose', metadata: { count: (compose.recipients || []).length } },
            token
          )
          .catch(() => {});
      }
    } catch (err) {
      setSendError(err.message || 'Unable to send email');
      setNotice({ open: true, type: 'error', message: err.message || 'Unable to send email', scope: 'compose' });
    }
  };

  const handleGroupSave = async () => {
    const name = (groupForm.name || '').trim();
    if (!name) {
      setNotice({ open: true, type: 'error', message: 'Group name is required.', scope: 'accounts' });
      return;
    }
    const payload = {
      id: groupForm.id || `grp-${Date.now()}`,
      name,
      accountIds: Array.from(new Set(groupForm.accountIds || [])),
      createdAt: groupForm.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setAccountGroups((prev) => {
      const exists = prev.findIndex((g) => g.id === payload.id);
      if (exists >= 0) {
        const next = [...prev];
        next[exists] = payload;
        return next;
      }
      return [payload, ...prev];
    });
    setGroupModalOpen(false);
    setGroupForm({ id: '', name: '', accountIds: [] });
    
    setNotice({ open: true, type: 'success', message: 'Group saved.', scope: 'accounts' });
  };

  const handleGroupEdit = (group) => {
    setGroupForm({
      id: group.id,
      name: group.name,
      accountIds: group.accountIds || [],
    });
    setGroupModalOpen(true);
  };

  const handleGroupDelete = async (id) => {
    setAccountGroups((prev) => prev.filter((g) => g.id !== id));
    setCampaignPlan((p) => ({
      ...p,
      selectedAccounts: (p.selectedAccounts || []).filter((key) => key !== `group:${id}`),
    }));
    
    setNotice({ open: true, type: 'info', message: 'Group removed.', scope: 'accounts' });
  };

  const handleReply = async (messageId) => {
    const replyHtml = replyDrafts[messageId] || '';
    const reply = htmlToText(replyHtml);
    if (!reply.trim()) {
      setSendError('Reply cannot be empty.');
      setNotice({ open: true, type: 'error', message: 'Reply cannot be empty.', scope: 'inbox' });
      return;
    }
    const msg = inbox.find((m) => m.id === messageId);
    const to = msg?.from || '';
    if (!to) {
      setSendError('No recipient found for this message.');
      setNotice({ open: true, type: 'error', message: 'No recipient found for this message.' });
      return;
    }
    try {
      setReplySending(true);
      setNotice({ open: true, type: 'info', message: 'Sending reply...', scope: 'inbox' });
        await sendEmail({
          subject: `Re: ${msg?.subject || ''}`,
          body: reply,
          bodyText: reply,
          bodyHtml: '',
          recipients: [to],
          accountId: compose.accountId,
          replyToId: messageId,
          attachments: [],
          trackOpens: false,
          trackingProvider: effectiveTrackingProvider,
          trackingIntegration,
        });
      setReplyDrafts((prev) => ({ ...prev, [messageId]: '' }));
      setReplyAttachments([]);
      setMessageStatus(messageId, 'read');
      setNotice({ open: true, type: 'success', message: 'Reply sent.', scope: 'inbox' });
      if (isAuthenticated) {
        adminApi.logActivity({ type: 'email_reply', source: 'inbox', metadata: { messageId } }, token).catch(() => {});
      }
    } catch (err) {
      setSendError(err.message || 'Reply failed');
      setNotice({ open: true, type: 'error', message: err.message || 'Reply failed', scope: 'inbox' });
    } finally {
      setReplySending(false);
    }
  };

  // Account lookup map for unified inbox account labels
  const accountMap = useMemo(() => {
    const map = {};
    (accounts || []).forEach((acc) => {
      map[acc.id] = acc.fromEmail || acc.username || acc.id;
    });
    return map;
  }, [accounts]);

  const filteredInbox = useMemo(() => {
    let rows = [...inbox];
    if (filterUnread) {
      rows = rows.filter((m) => m.status === 'unread');
    }
    // Account-level filter for unified inbox
    if (inboxAccountFilter && inboxAccountFilter !== 'all') {
      rows = rows.filter(
        (m) => m.accountId === inboxAccountFilter
      );
    }
    const query = String(inboxSearch || '').trim().toLowerCase();
    if (query) {
      rows = rows.filter((m) => {
        const text = `${m.from || ''} ${m.subject || ''} ${htmlToText(m.body || '')}`.toLowerCase();
        return text.includes(query);
      });
    }
    // Sort by timestamp descending (newest first)
    rows.sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
    return rows;
  }, [inbox, filterUnread, inboxAccountFilter, inboxSearch]);

  useEffect(() => {
    if (!filteredInbox.length) {
      if (activeMessageId) {
        setActiveMessageId('');
      }
      return;
    }
    if (!activeMessageId || !filteredInbox.some((message) => message.id === activeMessageId)) {
      setActiveMessageId(filteredInbox[0].id);
    }
  }, [activeMessageId, filteredInbox]);

  const inboxSummary = useMemo(() => ({
    total: filteredInbox.length,
    unread: filteredInbox.filter((message) => message.status === 'unread').length,
    accounts: accounts.filter((account) => account.imapHost || account.popHost).length,
  }), [accounts, filteredInbox]);

  const activeInboxMessage = useMemo(
    () => inbox.find((message) => message.id === activeMessageId) || filteredInbox[0] || null,
    [activeMessageId, filteredInbox, inbox]
  );

  const conversationItems = useMemo(() => {
    if (!activeInboxMessage) {
      return [];
    }
    const subjectKey = normalizeSubjectLine(activeInboxMessage.subject || '');
    const relatedInbox = inbox.filter((message) => normalizeSubjectLine(message.subject || '') === subjectKey);
    const relatedSent = sent.filter(
      (message) => message.parentId === activeInboxMessage.id || normalizeSubjectLine(message.subject || '') === subjectKey
    );
    const combined = [
      ...relatedInbox.map((message) => ({
        __type: 'in',
        id: message.id,
        accountId: message.accountId,
        from: message.from,
        body: message.body,
        displayBody: cleanBody(message.body || ''),
        preview: getMessagePreview(message),
        ts: message.timestamp,
      })),
      ...relatedSent.map((message) => ({
        __type: 'out',
        id: message.id,
        accountId: message.accountId || activeInboxMessage.accountId,
        from: 'You',
        body: message.body,
        displayBody: cleanBody(message.body || ''),
        preview: getMessagePreview(message),
        ts: message.sentAt,
        messageId: message.messageId || message.id,
      })),
    ];

    const seen = new Set();
    return combined
      .filter((message) => {
        const key = `${message.__type}-${message.id}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => new Date(a.ts || 0) - new Date(b.ts || 0));
  }, [activeInboxMessage, cleanBody, getMessagePreview, inbox, sent]);

  const expandAccountSelection = useCallback(
    (selection = []) => {
      const selected = Array.isArray(selection) ? selection : [];
      if (!selected.length) return accounts.map((a) => a.id);
      const ids = new Set();
      selected.forEach((id) => {
        if (String(id || '').startsWith('group:')) {
          const gid = String(id).replace('group:', '');
          const grp = accountGroups.find((g) => g.id === gid);
          grp?.accountIds?.forEach((aid) => ids.add(aid));
        } else {
          ids.add(id);
        }
      });
      return Array.from(ids);
    },
    [accounts, accountGroups]
  );

  const accountProviders = useMemo(() => {
    const set = new Set();
    accounts.forEach((a) => {
      if (a.provider) set.add(a.provider);
    });
    return Array.from(set);
  }, [accounts]);

  const accountUsageToday = useMemo(() => {
    const map = {};
    const today = new Date().toISOString().slice(0, 10);
    const trackingSeen = new Set();
    sent.forEach((s) => {
      const accId = s.accountId || s.account_id;
      if (!accId) return;
      const ts = s.timestamp || s.sentAt || s.createdAt;
      if (!ts) return;
      if (new Date(ts).toISOString().slice(0, 10) !== today) return;
      map[accId] = (map[accId] || 0) + 1;
    });
    trackingData.forEach((item) => {
      const ts = item?.sentAt || item?.updatedAt || item?.openedAt || item?.lastOpenedAt;
      if (!ts) return;
      if (new Date(ts).toISOString().slice(0, 10) !== today) return;
      const dedupeKey = item?.trackingId || item?.messageId || `${item?.recipient || ''}-${ts}`;
      if (!dedupeKey || trackingSeen.has(dedupeKey)) return;
      trackingSeen.add(dedupeKey);
      const directAccountId = item?.accountId || item?.account_id || '';
      const fallbackAccount = accounts.find((account) =>
        (item?.fromEmail && (account.fromEmail === item.fromEmail || account.username === item.fromEmail)) ||
        (item?.account && (account.label === item.account || account.fromEmail === item.account))
      );
      const accId = directAccountId || fallbackAccount?.id || '';
      if (!accId) return;
      map[accId] = (map[accId] || 0) + 1;
    });
    return map;
  }, [accounts, sent, trackingData]);

  const handleAccountLimitUpdate = useCallback((accountId, dailyLimit) => {
    updateAccount(accountId, { dailyLimit });
    setNotice({
      open: true,
      type: 'success',
      message: `Daily sending limit updated to ${dailyLimit}.`,
      scope: 'accounts',
    });
  }, [updateAccount]);

  const visibleAccounts = useMemo(() => {
    const term = accountSearch.trim().toLowerCase();
    return accounts.filter((acc) => {
      if (accountProviderFilter && acc.provider !== accountProviderFilter) return false;
      if (!term) return true;
      const hay = `${acc.label || ''} ${acc.fromEmail || ''} ${acc.username || ''} ${acc.host || ''}`.toLowerCase();
      return hay.includes(term);
    });
  }, [accounts, accountProviderFilter, accountSearch]);

  const selectedAccount =
    accounts.find((a) => a.id === (compose.accountId || selectedAccountId)) ||
    accounts[0];
  return (
    <>
      <Box
        sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        height: '100%',
        bgcolor: 'background.default',
      }}
    >


      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', px: { xs: 1.5, sm: 3, lg: 5 }, pb: { xs: 2, sm: 3 } }}>
        <Card
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: { xs: 2, md: 3 },
            overflow: 'hidden',
          }}
        >
          {!inboxOnly && (
            <>
              {/* Premium Animated Header with Gradient */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  py: { xs: 2.5, md: 3.5 },
                  px: { xs: 2, md: 4 },
                  borderBottom: '4px solid',
                  borderImage: 'linear-gradient(90deg, #667eea, #764ba2, #667eea) 1',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle at top right, rgba(255,255,255,0.15), transparent 50%)',
                    pointerEvents: 'none',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '-50%',
                    right: '-50%',
                    width: '200%',
                    height: '200%',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                    animation: 'pulse 4s ease-in-out infinite',
                  },
                  '@keyframes pulse': {
                    '0%, 100%': { 
                      transform: 'scale(1)',
                      opacity: 0.5,
                    },
                    '50%': { 
                      transform: 'scale(1.1)',
                      opacity: 0.8,
                    },
                  }
                }}
              >
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  spacing={2} 
                  alignItems={{ xs: 'flex-start', sm: 'center' }} 
                  sx={{ position: 'relative', zIndex: 1 }}
                >
                  <Box
                    sx={{
                      width: { xs: 52, md: 64 },
                      height: { xs: 52, md: 64 },
                      borderRadius: '16px',
                      background: 'rgba(255,255,255,0.25)',
                      backdropFilter: 'blur(12px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '3px solid rgba(255,255,255,0.4)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                      transition: 'all 0.3s ease',
                      animation: 'float 3s ease-in-out infinite',
                      '&:hover': {
                        transform: 'scale(1.05) rotate(5deg)',
                      },
                      '@keyframes float': {
                        '0%, 100%': { transform: 'translateY(0px)' },
                        '50%': { transform: 'translateY(-8px)' },
                      }
                    }}
                  >
                    {activeTab === 0 && <Send sx={{ fontSize: { xs: 26, md: 32 } }} />}
                    {activeTab === 1 && <Campaign sx={{ fontSize: { xs: 26, md: 32 } }} />}
                    {activeTab === 2 && <Layers sx={{ fontSize: { xs: 26, md: 32 } }} />}
                    {activeTab === 3 && <Inbox sx={{ fontSize: { xs: 26, md: 32 } }} />}
                    {activeTab === 4 && <Visibility sx={{ fontSize: { xs: 26, md: 32 } }} />}
                    {activeTab === 5 && <Settings sx={{ fontSize: { xs: 26, md: 32 } }} />}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant={isMobile ? 'h5' : 'h4'} 
                      fontWeight={900} 
                      sx={{ 
                        mb: 0.5,
                        textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        letterSpacing: '-0.5px',
                      }}
                    >
                      {activeTab === 0 && '✉️ Compose Email'}
                      {activeTab === 1 && '📊 Campaign Planner'}
                      {activeTab === 2 && '📝 Email Templates'}
                      {activeTab === 3 && '📬 Unified Inbox'}
                      {activeTab === 4 && '👁️ Seen Emails'}
                      {activeTab === 5 && '⚙️ Email Accounts'}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        opacity: 0.95,
                        fontWeight: 500,
                        textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                      }}
                    >
                      {activeTab === 0 && 'Create and send professional emails with AI assistance and spam detection'}
                      {activeTab === 1 && 'Plan and execute sophisticated multi-step email campaigns'}
                      {activeTab === 2 && 'Manage your email templates library with full CRUD operations'}
                      {activeTab === 3 && 'View and manage all your emails in one unified inbox'}
                      {activeTab === 4 && 'Track emails that have been opened by recipients with analytics'}
                      {activeTab === 5 && 'Manage your SMTP and OAuth email accounts with security'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'flex' },
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    <Chip
                      label="Premium"
                      size="small"
                      sx={{
                        background: 'rgba(255,255,255,0.25)',
                        color: '#fff',
                        fontWeight: 700,
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(255,255,255,0.3)',
                      }}
                    />
                  </Box>
                </Stack>
              </Box>

              <CardContent
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  px: { xs: 1.5, sm: 3 },
                  py: { xs: 2, sm: 3 },
                  gap: 2,
                }}
              >
                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {activeTab === 5 && (
                <>
                  {prefsError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {prefsError}. Check login/session and `/api/user_accounts.php`.
                    </Alert>
                  )}
                  <AccountsManager
                    accounts={accounts}
                    accountGroups={accountGroups}
                    onAddAccount={() => {
                      setAccountForm(defaultAccount);
                      setEditingId('');
                      setAccountError('');
                      setAccountMessage('');
                      setAccountModalOpen(true);
                    }}
                    onEditAccount={handleAccountEdit}
                    onDeleteAccount={handleAccountDelete}
                    onTestAccount={handleInlineAccountTest}
                    onCreateGroup={(accountIds) => {
                  setGroupForm({ id: '', name: '', accountIds: accountIds || [] });
                  setGroupModalOpen(true);
                }}
                onEditGroup={handleGroupEdit}
                onDeleteGroup={handleGroupDelete}
                selectedAccountId={selectedAccountId}
                onSelectAccount={setSelectedAccountId}
                bulkTesting={bulkTesting}
                onBulkTest={handleBulkAccountTest}
                accountUsageToday={accountUsageToday}
                campaignPlan={campaignPlan}
                compactView={compactAccounts}
                onCompactViewChange={setCompactAccounts}
                sentRecords={sent}
                trackingData={trackingData}
                inboxRecords={inbox}
                onUpdateAccountLimit={handleAccountLimitUpdate}
                analyticsRefreshing={seenLoading || fetchingInbox}
                lastAnalyticsRefreshAt={lastSeenRefreshAt || lastInboxRefreshAt}
                onRefreshAnalytics={() => {
                  Promise.allSettled([
                    refreshSeenData({ force: true, showLoading: true }),
                    refreshUnifiedInbox(false),
                  ]).catch(() => {});
                }}
              />
            </>
          )}
          {activeTab === 4 && false && (
            <Stack spacing={2}>
              <Card variant="outlined">
                <CardContent>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1.5} flexWrap="wrap">
                    <Box>
                      <Typography variant="h6" fontWeight={700}>Accounts & Groups</Typography>
                      <Typography variant="body2" color="text.secondary">Organize SMTP/OAuth accounts into reusable groups for campaigns.</Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Button size="small" variant="outlined" startIcon={<Groups />} onClick={() => { setGroupForm({ id: '', name: '', accountIds: [] }); setGroupModalOpen(true); }}>New Group</Button>
                      <Button size="small" variant="contained" startIcon={<Add />} onClick={() => { setAccountForm(defaultAccount); setEditingId(''); setAccountError(''); setAccountMessage(''); setTestPassed(false); setAccountModalOpen(true); }}>Add SMTP</Button>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" flexWrap="wrap" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      <TextField size="small" placeholder="Search accounts" value={accountSearch} onChange={(e) => setAccountSearch(e.target.value)} sx={{ minWidth: 200, flex: 1 }} />
                      <TextField select size="small" label="Provider" value={accountProviderFilter} onChange={(e) => setAccountProviderFilter(e.target.value)} sx={{ minWidth: 160 }}>
                        <MenuItem value="">All providers</MenuItem>
                        {accountProviders.map((p) => (<MenuItem key={p} value={p}>{p}</MenuItem>))}
                      </TextField>
                      <FormControlLabel control={<Switch checked={compactAccounts} onChange={(e) => setCompactAccounts(e.target.checked)} size="small" />} label="Compact view" />
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip size="small" label={`Accounts: ${accounts.length}`} />
                      <Chip size="small" label={`Providers: ${accountProviders.length}`} variant="outlined" />
                      <Chip size="small" label={`Visible: ${visibleAccounts.length}`} variant="outlined" />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<MailOutline />}
                        disabled={bulkTesting || visibleAccounts.length === 0}
                        onClick={handleBulkAccountTest}
                      >
                        {bulkTesting ? 'Testing...' : 'Test visible'}
                      </Button>
                      {(bulkTestSummary.success > 0 || bulkTestSummary.failed > 0) && (
                        <Chip
                          size="small"
                          color={bulkTestSummary.failed ? 'warning' : 'success'}
                          label={`Tested: ${bulkTestSummary.success} ok, ${bulkTestSummary.failed} failed`}
                        />
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AccountTree fontSize="small" />
                        <Typography fontWeight={700}>Account Groups</Typography>
                      </Stack>
                      <Button size="small" variant="text" startIcon={<Add />} onClick={() => { setGroupForm({ id: '', name: '', accountIds: [] }); setGroupModalOpen(true); }}>Create</Button>
                    </Stack>
                    {accountGroups.length === 0 && (<Alert severity="info">No groups yet. Create one to rotate multiple accounts in campaigns.</Alert>)}
                    <Stack spacing={1}>
                      {accountGroups.map((grp) => {
                        const open = groupOpen[grp.id];
                        return (
                          <Card key={grp.id} variant="outlined">
                            <CardContent sx={{ py: 1.25 }}>
                              <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <IconButton size="small" onClick={() => setGroupOpen((prev) => ({ ...prev, [grp.id]: !open }))}>{open ? <ExpandLess /> : <ExpandMore />}</IconButton>
                                  <Box>
                                    <Typography fontWeight={700}>{grp.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{grp.accountIds?.length || 0} accounts</Typography>
                                  </Box>
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                  <Button size="small" variant="outlined" onClick={() => handleGroupEdit(grp)}>Edit</Button>
                                  <Button size="small" color="error" variant="text" onClick={() => handleGroupDelete(grp.id)}>Delete</Button>
                                </Stack>
                              </Stack>
                              <Collapse in={open} timeout="auto" unmountOnExit>
                                <Stack spacing={0.5} sx={{ mt: 1, pl: 5 }}>
                                  {(grp.accountIds || []).map((id) => {
                                    const acct = accounts.find((a) => a.id === id);
                                    if (!acct) return null;
                                    const accLimit = acct.dailyLimit || campaignPlan.perAccountDaily || 0;
                                    const used = accountUsageToday[acct.id] || 0;
                                    const remaining = accLimit > 0 ? Math.max(accLimit - used, 0) : '8';
                                    return (
                                      <Stack key={id} direction="row" spacing={1} alignItems="center">
                                        <CheckCircle fontSize="small" color={selectedAccountId === id ? 'success' : 'disabled'} />
                                        <Typography variant="body2" fontWeight={600}>{acct.label || acct.fromEmail || acct.username}</Typography>
                                        <Typography variant="caption" color="text.secondary">{acct.provider || 'SMTP'}</Typography>
                                        <Typography variant="caption" color="text.secondary">Limit: {accLimit > 0 ? accLimit : '8'}</Typography>
                                        <Typography variant="caption" color="text.secondary">Today: {used} • Rem: {remaining}</Typography>
                                      </Stack>
                                    );
                                  })}
                                  {(grp.accountIds || []).length === 0 && (<Typography variant="body2" color="text.secondary">No accounts assigned.</Typography>)}
                                </Stack>
                              </Collapse>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
                    <Typography variant="subtitle1" fontWeight={700}>Connected Accounts</Typography>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Chip size="small" label={`Default/day: ${campaignPlan.perAccountDaily || 0}`} variant="outlined" />
                      <Typography variant="body2" color="text.secondary">Showing {visibleAccounts.length} of {accounts.length}</Typography>
                    </Stack>
                  </Stack>
                  {accounts.length === 0 && (<Alert severity="warning" sx={{ mt: 1 }}>No accounts yet. Add SMTP or provider credentials to start sending.</Alert>)}
                  {accounts.length > 0 && visibleAccounts.length === 0 && (<Alert severity="info" sx={{ mt: 1 }}>No accounts match this filter.</Alert>)}
                  <Box sx={{ mt: 1, maxHeight: { xs: 360, sm: 520 }, overflow: 'auto' }}>
                    <Stack spacing={compactAccounts ? 0.75 : 1.25}>
                      {visibleAccounts.map((acc) => (
                        <Card key={acc.id} variant="outlined" sx={{ borderColor: selectedAccountId === acc.id ? 'primary.main' : 'divider', backgroundColor: selectedAccountId === acc.id ? 'rgba(59,130,246,0.06)' : 'inherit' }}>
                          <CardContent sx={{ py: compactAccounts ? 1 : 1.5, px: compactAccounts ? 1.25 : 2 }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={compactAccounts ? 0.5 : 1}>
                              <Box>
                                <Typography fontWeight={700} noWrap>{acc.label || acc.fromEmail || acc.username}</Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>{acc.host}:{acc.port} {acc.secure ? '(TLS)' : ''}</Typography>
                              </Box>
                              <Stack direction="row" spacing={compactAccounts ? 0.5 : 1} alignItems="center" flexWrap="wrap">
                                <Chip icon={<Layers fontSize="small" />} label={acc.provider || 'SMTP'} size="small" variant="outlined" />
                                <Tooltip title="Set active">
                                  <IconButton size="small" onClick={() => setSelectedAccountId(acc.id)}>
                                    <CheckCircle color={selectedAccountId === acc.id ? 'success' : 'disabled'} fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Test connection">
                                  <IconButton size="small" onClick={() => handleInlineAccountTest(acc)}>
                                    <MailOutline fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => handleAccountEdit(acc)}>
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton size="small" onClick={() => handleAccountDelete(acc.id)} color="error">
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Stack>
          )}
          {false && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      spacing={1}
                    >
                      <Box>
                        <Typography variant="h6" fontWeight={700}>
                          Accounts & Groups
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Organize SMTP/OAuth accounts into reusable groups for campaigns.
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Groups />}
                          onClick={() => {
                            setGroupForm({ id: '', name: '', accountIds: [] });
                            setGroupModalOpen(true);
                          }}
                        >
                          New Group
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => {
                            setAccountForm(defaultAccount);
                            setEditingId('');
                            setAccountError('');
                            setAccountMessage('');
                            setAccountModalOpen(true);
                          }}
                        >
                          Add SMTP
                        </Button>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <TextField
                          size="small"
                          placeholder="Search accounts"
                          value={accountSearch}
                          onChange={(e) => setAccountSearch(e.target.value)}
                          sx={{ minWidth: 200 }}
                        />
                        
                        <FormControlLabel
                          control={
                            <Switch
                              checked={compactAccounts}
                              onChange={(e) => setCompactAccounts(e.target.checked)}
                              size="small"
                            />
                          }
                          label="Compact view"
                        />
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={5} lg={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Account Groups
                      </Typography>
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<Add />}
                        onClick={() => {
                          setGroupForm({ id: '', name: '', accountIds: [] });
                          setGroupModalOpen(true);
                        }}
                      >
                        Create
                      </Button>
                    </Stack>
                    {accountGroups.length === 0 && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        No groups yet. Create one to rotate multiple accounts in campaigns.
                      </Alert>
                    )}
                    <Stack spacing={1}>
                      {accountGroups.map((grp) => (
                        <Card key={grp.id} variant="outlined">
                          <CardContent sx={{ py: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                              <Box>
                                <Typography fontWeight={700}>{grp.name}</Typography>
                                <Stack direction="row" spacing={0.5} alignItems="center" mt={0.5} flexWrap="wrap">
                                  <Chip
                                    size="small"
                                    label={`${grp.accountIds?.length || 0} account(s)`}
                                    variant="outlined"
                                  />
                                  {(grp.accountIds || []).slice(0, 3).map((id) => {
                                    const acct = accounts.find((a) => a.id === id);
                                    return (
                                      <Chip
                                        key={id}
                                        size="small"
                                        label={acct?.label || acct?.fromEmail || id}
                                        sx={{ maxWidth: 140 }}
                                      />
                                    );
                                  })}
                                  {(grp.accountIds || []).length > 3 && (
                                    <Typography variant="caption" color="text.secondary">
                                      +{(grp.accountIds || []).length - 3} more
                                    </Typography>
                                  )}
                                </Stack>
                              </Box>
                              <Stack direction="row" spacing={1}>
                                <Button size="small" variant="outlined" onClick={() => handleGroupEdit(grp)}>
                                  Edit
                                </Button>
                                <Button size="small" color="error" variant="text" onClick={() => handleGroupDelete(grp.id)}>
                                  Delete
                                </Button>
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={7} lg={8}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Connected Accounts
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Showing {visibleAccounts.length} of {accounts.length}
                      </Typography>
                    </Stack>
                    {accounts.length === 0 && (
                      <Alert severity="warning">
                        No accounts yet. Add SMTP or provider credentials to start sending.
                      </Alert>
                    )}
                    {accounts.length > 0 && visibleAccounts.length === 0 && (
                      <Alert severity="info">No accounts match this filter.</Alert>
                    )}
                    <Box sx={{ maxHeight: 520, overflow: 'auto' }}>
                      <Stack spacing={compactAccounts ? 0.75 : 1.25}>
                        {visibleAccounts.map((acc) => (
                        <Card
                          key={acc.id}
                          variant="outlined"
                          sx={{
                            borderColor:
                                selectedAccountId === acc.id ? 'primary.main' : 'divider',
                              backgroundColor:
                                selectedAccountId === acc.id ? 'rgba(59,130,246,0.06)' : 'inherit',
                            }}
                          >
                            <CardContent sx={{ py: compactAccounts ? 1 : 1.5, px: compactAccounts ? 1.25 : 2 }}>
                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                spacing={compactAccounts ? 0.5 : 1}
                              >
                                <Box>
                                  <Typography fontWeight={700} noWrap>
                                    {acc.label || acc.fromEmail || acc.username}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {acc.host}:{acc.port} {acc.secure ? '(TLS)' : ''}
                                  </Typography>
                                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
                                    <Chip
                                      icon={<Layers fontSize="small" />}
                                      label={acc.provider || 'SMTP'}
                                      size="small"
                                      variant="outlined"
                                    />
                                    <Chip
                                      label={`Limit: ${acc.dailyLimit || campaignPlan.perAccountDaily || 0}`}
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                    />
                                    <Chip
                                      label={`Sent today: ${accountUsageToday[acc.id] || 0}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                    <Chip
                                      label={`Remaining: ${
                                        (acc.dailyLimit || campaignPlan.perAccountDaily || 0) > 0
                                          ? Math.max(
                                              (acc.dailyLimit || campaignPlan.perAccountDaily || 0) -
                                                (accountUsageToday[acc.id] || 0),
                                              0
                                            )
                                          : '8'
                                      }`}
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                    />
                                  </Stack>
                                </Box>
                                <Stack direction="row" spacing={compactAccounts ? 0.5 : 1} alignItems="center" flexWrap="wrap">
                                  <Tooltip title="Set active">
                                    <IconButton
                                      size="small"
                                      onClick={() => setSelectedAccountId(acc.id)}
                                    >
                                      <CheckCircle
                                        color={
                                          selectedAccountId === acc.id ? 'success' : 'disabled'
                                        }
                                        fontSize="small"
                                      />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Test connection">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleInlineAccountTest(acc)}
                                    >
                                      <MailOutline fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Edit">
                                    <IconButton size="small" onClick={() => handleAccountEdit(acc)}>
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleAccountDelete(acc.id)}
                                    color="error"
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                              <Typography variant="caption" color="text.secondary">
                                Usage today: {accountUsageToday[acc.id] || 0} /{' '}
                                {acc.dailyLimit || campaignPlan.perAccountDaily || '8'} • Remaining:{' '}
                                {(acc.dailyLimit || campaignPlan.perAccountDaily || 0) > 0
                                  ? Math.max(
                                      (acc.dailyLimit || campaignPlan.perAccountDaily || 0) -
                                        (accountUsageToday[acc.id] || 0),
                                      0
                                    )
                                  : '8'}
                              </Typography>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

            </Grid>
          )}
          {activeTab === 0 && (
            <Box>
              {sendError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {sendError}
                </Alert>
              )}
              {statusText && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {statusText}
                </Alert>
              )}
              {sendProgress && sendProgress.total > 1 && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" fontWeight={700}>
                        Sending {sendProgress.total} emails…
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.round((sendProgress.current / sendProgress.total) * 100)}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Sent {sendProgress.sent} • Failed {sendProgress.failed} • Remaining {Math.max(sendProgress.total - sendProgress.current, 0)}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              )}
              <AdvancedEmailComposer
                compose={compose}
                setCompose={setCompose}
                accounts={accounts}
                carrierEmails={carrierEmails}
                recipientStats={recipientStats}
                subjectInsights={subjectInsights}
                bodyInsights={bodyInsights}
                fileInputRef={fileInputRef}
                onFileImport={handleFileImport}
                onLoadCarrierExport={handleLoadCarrierExportForCompose}
                uploadError={uploadError}
                mergeTags={mergeTagOptions}
                trackingProvider={effectiveTrackingProvider}
                trackingIntegrationConnected={trackingIntegrationConnected}
                onSend={handleSend}
                sendLoading={sending}
              />
              {/* Deliverability Guide - Real-time scoring */}
              <Box sx={{ mt: 2 }}>
                <EmailDeliverabilityGuide
                  accounts={accounts}
                  compose={compose}
                  spamScore={analyzeSpamScore(compose.body || '', compose.subject || '').score}
                  accountUsageToday={accountUsageToday}
                  campaignPlan={campaignPlan}
                />
              </Box>
              {sendProgress && sendProgress.total > 1 && (
                <Card
                  variant="outlined"
                  sx={{
                    mt: 2,
                    position: 'sticky',
                    bottom: 8,
                    zIndex: 2,
                    backgroundColor: 'background.paper',
                    borderColor: 'divider',
                  }}
                >
                  <CardContent sx={{ py: 1.5 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                      <Stack spacing={0.5} sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={700}>
                          Sending emails
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={Math.round((sendProgress.current / sendProgress.total) * 100)}
                        />
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip size="small" label={`Total ${sendProgress.total}`} />
                        <Chip size="small" color="success" label={`Sent ${sendProgress.sent}`} />
                        <Chip size="small" color="error" label={`Failed ${sendProgress.failed}`} />
                        <Chip size="small" variant="outlined" label={`Remaining ${Math.max(sendProgress.total - sendProgress.current, 0)}`} />
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              )}
              {campaignRun && campaignRun.running && campaignRun.counts && (
                <Card
                  variant="outlined"
                  sx={{
                    mt: 2,
                    position: 'sticky',
                    bottom: 8,
                    zIndex: 2,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.04)',
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  }}
                >
                  <CardContent sx={{ py: 1.5 }}>
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={20} thickness={4} />
                          <Typography variant="body2" fontWeight={700}>
                            Campaign Running (Background Worker)
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          Campaign ID: {campaignRun.id}
                        </Typography>
                      </Stack>
                      
                      <Stack spacing={0.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" color="text.secondary">
                            Progress
                          </Typography>
                          <Typography variant="caption" fontWeight={600}>
                            {campaignRun.counts.sent || 0}/{campaignRun.counts.total || 0}
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(
                            100,
                            ((campaignRun.counts.sent || 0) / Math.max(campaignRun.counts.total || 1, 1)) * 100
                          )}
                          sx={{ 
                            height: 8, 
                            borderRadius: 1,
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                          }}
                        />
                      </Stack>
                      
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip 
                          size="small" 
                          icon={<Send fontSize="small" />}
                          color="success" 
                          label={`Sent: ${campaignRun.counts.sent || 0}`} 
                        />
                        {(campaignRun.counts.pending || 0) > 0 && (
                          <Chip 
                            size="small" 
                            color="warning" 
                            variant="outlined"
                            label={`Pending: ${campaignRun.counts.pending}`} 
                          />
                        )}
                        {(campaignRun.counts.sending || 0) > 0 && (
                          <Chip 
                            size="small" 
                            color="info" 
                            label={`Sending: ${campaignRun.counts.sending}`} 
                          />
                        )}
                        {(campaignRun.counts.failed || 0) > 0 && (
                          <Chip 
                            size="small" 
                            color="error" 
                            label={`Failed: ${campaignRun.counts.failed}`} 
                          />
                        )}
                        {(campaignRun.counts.opened || 0) > 0 && (
                          <Chip 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            label={`Opened: ${campaignRun.counts.opened}`} 
                          />
                        )}
                        {(campaignRun.counts.clicked || 0) > 0 && (
                          <Chip 
                            size="small" 
                            color="primary" 
                            label={`Clicked: ${campaignRun.counts.clicked}`} 
                          />
                        )}
                      </Stack>
                      
                      <Alert severity="info" sx={{ py: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                          <Typography variant="caption">
                            ✓ Campaign is running in background. You can close browser - emails will continue sending!
                          </Typography>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            color="warning"
                            startIcon={<Pause />}
                            onClick={() => handlePauseCampaign(campaignRun.id)}
                            sx={{ ml: 1, flexShrink: 0 }}
                          >
                            Pause
                          </Button>
                        </Stack>
                      </Alert>
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
          {activeTab === 1 && (
            <CampaignPlanner
              plans={savedPlans.map((p) => ({
                ...p,
                status: p.status || 'draft',
                scheduledDate: p.scheduledDate || null,
                timezone: p.timezone || 'UTC',
                segmentType: p.segmentType || 'all',
              }))}
              campaigns={savedPlans}
              accounts={accounts}
              accountGroups={accountGroups}
              templates={campaignTemplates}
              onRecipientColumns={(cols) => setPlannerRecipientColumns(cols || [])}
              editingPlan={editingPlan}
              onEditComplete={(updatedPlan) => {
                if (updatedPlan && editingPlan) {
                  const selectedTemplates = updatedPlan.selectedTemplates || editingPlan.strategy?.selectedTemplates || [];
                  const selectedAccounts = updatedPlan.selectedAccounts || editingPlan.strategy?.selectedAccounts || [];
                  const nextStrategy = {
                    ...(editingPlan.strategy || {}),
                    selectedTemplates,
                    selectedAccounts,
                    minDelaySec: Number(updatedPlan.minDelaySec) || editingPlan.strategy?.minDelaySec || defaultCampaignPlan.minDelaySec,
                    maxDelaySec: Number(updatedPlan.maxDelaySec) || editingPlan.strategy?.maxDelaySec || defaultCampaignPlan.maxDelaySec,
                    rotateAccounts: updatedPlan.rotateAccounts !== false,
                    shuffleTemplates: updatedPlan.shuffleTemplates !== false,
                    jitterSec: Number(updatedPlan.jitterSec) || 0,
                    batchPauseEvery: Number(updatedPlan.batchPauseEvery) || 0,
                    batchPauseSec: Number(updatedPlan.batchPauseSec) || 0,
                    maxPerAccountPerHour: Number(updatedPlan.maxPerAccountPerHour) || 0,
                    maxFailures: Number(updatedPlan.maxFailures) || 0,
                    enforceMinDelay: updatedPlan.enforceMinDelay !== false,
                    trackOpens: updatedPlan.trackOpens !== false,
                    trackClicks: updatedPlan.trackClicks !== false,
                    runStats: {},
                  };
                  const updated = { 
                    ...editingPlan,
                    ...updatedPlan,
                    id: editingPlan.id,
                    templates: campaignTemplates.filter((t) => selectedTemplates.includes(t.id)),
                    strategy: nextStrategy,
                    useMcRange: !!updatedPlan.useMcRange,
                    mcStartNumber: updatedPlan.mcStartNumber || '',
                    mcEndNumber: updatedPlan.mcEndNumber || '',
                    targetActiveCarriers: updatedPlan.targetActiveCarriers !== false,
                    targetInactiveCarriers: !!updatedPlan.targetInactiveCarriers,
                    targetBrokers: !!updatedPlan.targetBrokers,
                    status: updatedPlan.scheduledDate ? 'scheduled' : 'draft',
                    updatedAt: new Date().toISOString() 
                  };
                  upsertPlanRecord(updated);
                  savePlanToDatabase(updated).then((saved) => {
                    if (saved) {
                      upsertPlanRecord(saved);
                      setNotice({ open: true, type: 'success', message: 'Campaign updated successfully.', scope: 'campaign' });
                    }
                  }).catch((error) => {
                    console.error('Error saving plan:', error);
                    setNotice({ open: true, type: 'error', message: 'Failed to save campaign to database.', scope: 'campaign' });
                  });
                }
                setEditingPlan(null);
              }}
              onCreatePlan={(payload) => {
                const name = (payload?.name || '').trim();
                if (!name) return;
                const selectedTemplates = payload.selectedTemplates || [];
                const selectedAccounts = payload.selectedAccounts || [];
                const newPlan = {
                  id: `plan-${Date.now()}`,
                  name,
                  description: payload.description || '',
                  status: payload.scheduledDate ? 'scheduled' : 'draft',
                  scheduledDate: payload.scheduledDate || null,
                  timezone: payload.timezone || 'UTC',
                  segmentType: payload.segmentType || 'all',
                  sendingFrequency: payload.sendingFrequency || 'immediate',
                  recipientLimit: payload.recipientLimit || '',
                  recipientFileName: payload.recipientFileName || '',
                  recipientRows: payload.recipientRows || [],
                  useMcRange: !!payload.useMcRange,
                  mcStartNumber: payload.mcStartNumber || '',
                  mcEndNumber: payload.mcEndNumber || '',
                  targetActiveCarriers: payload.targetActiveCarriers !== false,
                  targetInactiveCarriers: !!payload.targetInactiveCarriers,
                  targetBrokers: !!payload.targetBrokers,
                  templates: campaignTemplates.filter((t) => selectedTemplates.includes(t.id)),
                    strategy: {
                      ...defaultCampaignPlan,
                      selectedTemplates,
                      selectedAccounts,
                      perAccountLimits: {},
                      minDelaySec: Number(payload.minDelaySec) || defaultCampaignPlan.minDelaySec,
                      maxDelaySec: Number(payload.maxDelaySec) || defaultCampaignPlan.maxDelaySec,
                      rotateAccounts: payload.rotateAccounts !== false,
                      shuffleTemplates: payload.shuffleTemplates !== false,
                      jitterSec: Number(payload.jitterSec) || 0,
                      batchPauseEvery: Number(payload.batchPauseEvery) || 0,
                      batchPauseSec: Number(payload.batchPauseSec) || 0,
                      maxPerAccountPerHour: Number(payload.maxPerAccountPerHour) || 0,
                      maxFailures: Number(payload.maxFailures) || 0,
                      enforceMinDelay: payload.enforceMinDelay !== false,
                      trackOpens: payload.trackOpens !== false,
                      trackClicks: payload.trackClicks !== false,
                      useMcRange: !!payload.useMcRange,
                      mcStartNumber: payload.mcStartNumber || '',
                      mcEndNumber: payload.mcEndNumber || '',
                      targetActiveCarriers: payload.targetActiveCarriers !== false,
                      targetInactiveCarriers: !!payload.targetInactiveCarriers,
                      targetBrokers: !!payload.targetBrokers,
                    },
                };
                const record = normalizePlanRecord(newPlan);
                upsertPlanRecord(record);
                savePlanToDatabase(record)
                  .then((saved) => {
                    if (saved) upsertPlanRecord(saved);
                  })
                  .catch((error) => {
                    console.error('Error saving plan:', error);
                    setNotice({ open: true, type: 'error', message: 'Failed to save plan to database.', scope: 'campaign' });
                  });
              }}
              onEditPlan={(plan) => handlePlanEdit(plan)}
              onDeletePlan={(id) => {
                setSavedPlans((prev) => prev.filter((p) => p.id !== id));
                deletePlanFromDatabase(id).catch((error) => {
                  console.error('Error deleting plan:', error);
                  setNotice({ open: true, type: 'error', message: 'Failed to delete plan from database.', scope: 'campaign' });
                });
              }}
              onSchedulePlan={() => {}}
              onApplySegmentation={() => {}}
              onLaunchCampaign={(id) => launchPlannerCampaign(id)}
              onPauseCampaign={(id) => handlePauseCampaign(id)}
              onResumeCampaign={(id) => handleResumeCampaign(id)}
              planStats={plannerStats}
            />
          )}
          {activeTab === 2 && (
            <TemplateManager
              templates={campaignTemplates}
              mergeTags={mergeTagOptions}
              onCreateTemplate={saveTemplate}
              onEditTemplate={saveTemplate}
              onDeleteTemplate={deleteTemplate}
              onDuplicateTemplate={() => {}}
              onUseTemplate={(template) => {
                applyTemplateToCompose(template);
                setActiveTab(0);
              }}
              stats={{
                totalTemplates: campaignTemplates.length,
                avgOpenRate: campaignTemplates.length > 0 
                  ? Math.round((campaignTemplates.reduce((sum, t) => sum + (parseFloat(t.openRate) || 0), 0) / campaignTemplates.length) * 100) / 100 
                  : 0,
                totalUses: campaignTemplates.reduce((sum, t) => sum + (t.uses || 0), 0),
                favorites: campaignTemplates.filter(t => t.isFavorite).length
              }}
            />
          )}
          {activeTab === 3 && (
            <Box sx={{ display: 'none' }} />
          )}
          {activeTab === 4 && (
            <TrackingAnalytics
              trackingData={trackingData || []}
              sentRecords={sent || []}
              accounts={accounts || []}
              emailStats={emailStats || {}}
              campaignAnalytics={campaignAnalytics || {}}
              isRefreshing={seenLoading}
              refreshError={seenError}
              lastRefreshedAt={lastSeenRefreshAt}
              onRefresh={() => refreshSeenData({ force: true, showLoading: true })}
              onExportReport={() => {}}
              onShareStats={() => {}}
            />
          )}
          {activeTab === 2 && false && (
            <Stack spacing={2}>
              <Card variant="outlined">
                <CardContent>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Layers fontSize="small" />
                      <Typography fontWeight={700}>Template Library</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                      <TextField
                        size="small"
                        placeholder="Search templates"
                        value={templateSearch}
                        onChange={(e) => setTemplateSearch(e.target.value)}
                        sx={{ minWidth: 200 }}
                      />
                      <Button size="small" variant="contained" startIcon={<Add />} onClick={() => { setTemplateForm({ id: '', name: '', subject: '', body: '' }); setTemplateModalOpen(true); }}>New Template</Button>
                      <Chip size="small" label={`${filteredTemplates.length} of ${campaignTemplates.length}`} />
                      <Chip size="small" color="info" variant="outlined" label={`In strategy: ${campaignPlan.selectedTemplates.length}`} />
                    </Stack>
                  </Stack>
                  {campaignTemplates.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No templates yet. Add one to reuse in Compose and Planner.
                    </Typography>
                  )}
                  <Stack spacing={1.2} sx={{ maxHeight: 420, overflowY: 'auto', mt: 1 }}>
                    {filteredTemplates.map((t) => {
                      const inStrategy = campaignPlan.selectedTemplates.includes(t.id);
                      return (
                        <Card key={t.id} variant="outlined" sx={{ p: 1 }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography fontWeight={700} noWrap>{t.name}</Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>{t.subject}</Typography>
                            </Box>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {inStrategy && <Chip size="small" color="success" label="In strategy" />}
                              <Button size="small" variant="outlined" onClick={() => applyTemplateToCompose(t)}>Use</Button>
                              <Button size="small" variant="text" onClick={() => { setTemplateForm(t); setTemplateModalOpen(true); }}>Edit</Button>
                              <Button size="small" variant="text" onClick={() => setCampaignPlan((p) => ({ ...p, selectedTemplates: inStrategy ? p.selectedTemplates.filter((id) => id !== t.id) : Array.from(new Set([...(p.selectedTemplates || []), t.id])) }))}>
                                {inStrategy ? 'Remove' : 'Add'}
                              </Button>
                              <Button size="small" color="error" variant="text" onClick={() => deleteTemplate(t.id)}>Delete</Button>
                            </Stack>
                          </Stack>
                        </Card>
                      );
                    })}
                    {campaignTemplates.length > 0 && filteredTemplates.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No templates match the current search.
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" mb={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Campaign fontSize="small" />
                      <Typography fontWeight={700}>Send Strategy</Typography>
                    </Stack>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        const name = prompt('Save this plan as:');
                        if (name) savePlan(name);
                      }}
                    >
                      Save plan
                    </Button>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Store sending preferences per account or group. Strategy and plans save to your account and can be loaded in Compose.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Accounts & Groups</Typography>
                      </Stack>
                      <Stack spacing={1} sx={{ maxHeight: 280, overflowY: 'auto', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        {accountGroups.length > 0 && (
                          <Box>
                            <Typography variant="caption" fontWeight={700} color="text.secondary">Groups</Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" mt={0.5}>
                              {accountGroups.map((g) => {
                                const key = `group:${g.id}`;
                                const active = campaignPlan.selectedAccounts.includes(key);
                                return (
                                  <Chip
                                    key={key}
                                    label={`${g.name} (${g.accountIds?.length || 0})`}
                                    color={active ? 'primary' : 'default'}
                                    onClick={() =>
                                      setCampaignPlan((p) => ({
                                        ...p,
                                        selectedAccounts: active
                                          ? p.selectedAccounts.filter((id) => id !== key)
                                          : [...p.selectedAccounts, key],
                                      }))
                                    }
                                  />
                                );
                              })}
                            </Stack>
                          </Box>
                        )}
                        {accounts.map((a) => {
                          const active = campaignPlan.selectedAccounts.includes(a.id);
                          const perLimit = (campaignPlan.perAccountLimits || {})[a.id] || '';
                          return (
                            <Card key={a.id} variant="outlined" sx={{ p: 0.75 }}>
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                                <Chip
                                  label={a.label || a.fromEmail || a.username}
                                  color={active ? 'primary' : 'default'}
                                  onClick={() =>
                                    setCampaignPlan((p) => ({
                                      ...p,
                                      selectedAccounts: active
                                        ? p.selectedAccounts.filter((id) => id !== a.id)
                                        : [...p.selectedAccounts, a.id],
                                    }))
                                  }
                                />
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                  <TextField
                                    size="small"
                                    type="number"
                                    label="Emails to send"
                                    value={perLimit}
                                    placeholder="0"
                                    onChange={(e) =>
                                      setCampaignPlan((p) => ({
                                        ...p,
                                        perAccountLimits: {
                                          ...(p.perAccountLimits || {}),
                                          [a.id]: Number(e.target.value) || 0,
                                        },
                                      }))
                                    }
                                    sx={{ width: 120 }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    Uses group rotation/shuffle when groups selected.
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Card>
                          );
                        })}
                        {accounts.length === 0 && (<Typography variant="body2" color="text.secondary">Add an account first.</Typography>)}
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>Cadence & Randomization</Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}><TextField label="Per account / day" type="number" size="small" value={campaignPlan.perAccountDaily} onChange={(e) => setCampaignPlan((p) => ({ ...p, perAccountDaily: Number(e.target.value) || 0 }))} fullWidth /></Grid>
                        <Grid item xs={6}><TextField label="Batch size" type="number" size="small" value={campaignPlan.batchSize} onChange={(e) => setCampaignPlan((p) => ({ ...p, batchSize: Number(e.target.value) || 0 }))} fullWidth /></Grid>
                        <Grid item xs={6}><TextField label="Min delay (s)" type="number" size="small" value={campaignPlan.minDelaySec} onChange={(e) => setCampaignPlan((p) => ({ ...p, minDelaySec: Number(e.target.value) || 0 }))} fullWidth /></Grid>
                        <Grid item xs={6}><TextField label="Max delay (s)" type="number" size="small" value={campaignPlan.maxDelaySec} onChange={(e) => setCampaignPlan((p) => ({ ...p, maxDelaySec: Number(e.target.value) || 0 }))} fullWidth /></Grid>
                        <Grid item xs={12}><TextField label="Random start offset (s)" type="number" size="small" value={campaignPlan.startOffsetSec || 0} onChange={(e) => setCampaignPlan((p) => ({ ...p, startOffsetSec: Number(e.target.value) || 0 }))} fullWidth helperText="Add a random delay before first send to avoid patterns." /></Grid>
                      </Grid>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} mt={1} flexWrap="wrap">
                        <FormControlLabel control={<Switch checked={campaignPlan.shuffleTemplates} onChange={(e) => setCampaignPlan((p) => ({ ...p, shuffleTemplates: e.target.checked }))} />} label="Shuffle templates" />
                        <FormControlLabel control={<Switch checked={campaignPlan.rotateAccounts} onChange={(e) => setCampaignPlan((p) => ({ ...p, rotateAccounts: e.target.checked }))} />} label="Rotate accounts" />
                        <FormControlLabel control={<Switch checked onChange={() => {}} />} label="Group-aware rotation" />
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Strategy and saved plans are stored in your account. Rotation and shuffle help spread risk across accounts and content.
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1} mb={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Campaign fontSize="small" />
                      <Typography fontWeight={700}>Saved Plans</Typography>
                    </Stack>
                    <Chip size="small" label={`${savedPlans.length} saved`} />
                  </Stack>
                  {savedPlans.length === 0 && (<Typography variant="body2" color="text.secondary">No saved plans.</Typography>)}
                  {savedPlans.length > 0 && (
                    <Card variant="outlined">
                      <TableContainer sx={{ maxHeight: 420 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Plan</TableCell>
                              <TableCell>Templates</TableCell>
                              <TableCell>Accounts</TableCell>
                              <TableCell>Cadence</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {savedPlans.map((p) => {
                              const open = planOpen[p.id];
                              const acctIds = expandAccountSelection(p.strategy.selectedAccounts);
                              const acctCount = acctIds.length || 'All';
                              return (
                                <React.Fragment key={p.id}>
                                  <TableRow hover>
                                    <TableCell sx={{ minWidth: 200 }}>
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        <IconButton
                                          size="small"
                                          onClick={() => setPlanOpen((prev) => ({ ...prev, [p.id]: !open }))}
                                        >
                                          {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                        </IconButton>
                                        <Box sx={{ minWidth: 0 }}>
                                          <Typography fontWeight={700} noWrap>{p.name}</Typography>
                                          <Typography variant="caption" color="text.secondary" noWrap>
                                            Rotation: {p.strategy.rotateAccounts ? 'On' : 'Off'} • Shuffle: {p.strategy.shuffleTemplates ? 'On' : 'Off'}
                                          </Typography>
                                        </Box>
                                      </Stack>
                                    </TableCell>
                                    <TableCell>{(p.strategy.selectedTemplates || []).length}</TableCell>
                                    <TableCell>{acctCount}</TableCell>
                                    <TableCell>
                                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                        <Chip size="small" label={`${p.strategy.perAccountDaily || 0}/day`} />
                                        <Chip size="small" label={`Batch ${p.strategy.batchSize || 0}`} />
                                        <Chip size="small" label={`${p.strategy.minDelaySec || 0}-${p.strategy.maxDelaySec || 0}s`} />
                                      </Stack>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Tooltip title="Load to Compose">
                                        <IconButton size="small" onClick={() => loadPlanToCompose(p)}><Send fontSize="small" /></IconButton>
                                      </Tooltip>
                                      <Tooltip title="Edit">
                                        <IconButton size="small" onClick={() => handlePlanEdit(p)}><Edit fontSize="small" /></IconButton>
                                      </Tooltip>
                                      <Tooltip title="Details">
                                        <IconButton
                                          size="small"
                                          onClick={() => setPlanOpen((prev) => ({ ...prev, [p.id]: !open }))}
                                        >
                                          <Visibility fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Delete">
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => {
                                            setSavedPlans((prev) => prev.filter((x) => x.id !== p.id));
                                            deletePlanFromDatabase(p.id).catch((error) => {
                                              console.error('Error deleting plan:', error);
                                              setNotice({ open: true, type: 'error', message: 'Failed to delete plan from database.', scope: 'campaign' });
                                            });
                                          }}
                                        >
                                          <Delete fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell colSpan={5} sx={{ p: 0, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                                      <Collapse in={open} timeout="auto" unmountOnExit>
                                        <Box sx={{ p: 2, backgroundColor: 'action.hover' }}>
                                          <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                              <Typography variant="subtitle2">Cadence</Typography>
                                              <Typography variant="body2" color="text.secondary">
                                                Per account/day: {p.strategy.perAccountDaily || 0} • Batch: {p.strategy.batchSize || 0}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary">
                                                Delays: {p.strategy.minDelaySec || 0}s - {p.strategy.maxDelaySec || 0}s • Start offset: {p.strategy.startOffsetSec || 0}s
                                              </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                              <Typography variant="subtitle2">Templates</Typography>
                                              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                {(p.strategy.selectedTemplates || []).map((id) => {
                                                  const tpl = campaignTemplates.find((t) => t.id === id);
                                                  return <Chip key={id} size="small" label={tpl?.name || id} />;
                                                })}
                                                {(p.strategy.selectedTemplates || []).length === 0 && (
                                                  <Typography variant="body2" color="text.secondary">None</Typography>
                                                )}
                                              </Stack>
                                            </Grid>
                                            <Grid item xs={12}>
                                              <Typography variant="subtitle2">Accounts</Typography>
                                              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                {acctIds.map((id) => {
                                                  const acct = accounts.find((a) => a.id === id);
                                                  return <Chip key={id} size="small" label={acct?.label || acct?.fromEmail || id} icon={<Mail fontSize="small" />} />;
                                                })}
                                                {acctIds.length === 0 && (
                                                  <Typography variant="body2" color="text.secondary">All accounts</Typography>
                                                )}
                                              </Stack>
                                            </Grid>
                                          </Grid>
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                </React.Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Card>
                  )}
                </CardContent>
              </Card>
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={1} alignItems="flex-start">
                    <Typography variant="subtitle2" fontWeight={700}>How to use</Typography>
                    <Typography variant="body2" color="text.secondary">1) Choose accounts or groups and set per-account limits.</Typography>
                    <Typography variant="body2" color="text.secondary">2) Adjust cadence: batch size, delays, and optional random start.</Typography>
                    <Typography variant="body2" color="text.secondary">3) Save the plan to reuse later.</Typography>
                    <Typography variant="body2" color="text.secondary">4) In Compose, load the saved plan, pick a template, and send.</Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          )}

          {activeTab === 3 && (
            <Box sx={{ height: { xs: 'auto', md: '82vh' }, display: 'flex', flexDirection: 'column' }}>
              {/* Unified Inbox Header */}
              <Paper
                elevation={0}
                sx={{
                  p: isMobile ? 1.5 : 2,
                  mb: 2,
                  borderRadius: 2.5,
                  background: 'linear-gradient(135deg, rgba(102,126,234,0.05) 0%, rgba(255,255,255,0.96) 58%, rgba(118,75,162,0.04) 100%)',
                  border: '1px solid rgba(102,126,234,0.10)',
                  boxShadow: '0 10px 30px rgba(15, 23, 42, 0.05)',
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: '#667eea', color: 'white' }}>
                      <Inbox fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>Unified Inbox</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {inboxSummary.total} message{inboxSummary.total !== 1 ? 's' : ''} • {inboxSummary.unread} unread
                        {accounts.length > 1 && ` • ${inboxSummary.accounts} accounts`}
                        {lastInboxRefreshAt && ` • Updated ${formatRelativeTimestamp(lastInboxRefreshAt)}`}
                      </Typography>
                      <Stack direction="row" spacing={0.75} mt={1} flexWrap="wrap">
                        <Chip
                          size="small"
                          label={`${inboxSummary.total} in view`}
                          variant="outlined"
                          sx={{ borderRadius: 1.5, fontWeight: 600 }}
                        />
                        <Chip
                          size="small"
                          label={`${inboxSummary.unread} unread`}
                          color={inboxSummary.unread ? 'primary' : 'default'}
                          variant={inboxSummary.unread ? 'filled' : 'outlined'}
                          sx={{ borderRadius: 1.5, fontWeight: 600 }}
                        />
                        <Chip
                          size="small"
                          label={lastInboxRefreshAt ? `Last sync ${formatRelativeTimestamp(lastInboxRefreshAt)}` : 'Waiting for first sync'}
                          variant="outlined"
                          sx={{ borderRadius: 1.5, fontWeight: 600 }}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<Refresh />}
                      onClick={() => { refreshUnifiedInbox(true).catch(() => {}); }}
                      disabled={fetchingInbox}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        fontWeight: 600,
                        borderRadius: 2,
                        textTransform: 'none',
                        px: 2,
                      }}
                    >
                      {fetchingInbox ? 'Refreshing...' : 'Refresh All'}
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96, textAlign: 'right' }}>
                      {fetchingInbox ? 'Syncing inboxes' : lastInboxRefreshAt ? `Synced ${formatRelativeTimestamp(lastInboxRefreshAt)}` : 'Not synced yet'}
                    </Typography>
                    <Tooltip title={notificationPrefs.newEmail ? 'Notifications on' : 'Notifications off'}>
                      <IconButton
                        onClick={() => setNotificationPrefs((p) => ({ ...p, newEmail: !p.newEmail }))}
                        size="small"
                        sx={{
                          bgcolor: notificationPrefs.newEmail ? '#667eea15' : 'transparent',
                          color: notificationPrefs.newEmail ? '#667eea' : 'text.secondary',
                        }}
                      >
                        <MailOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={notificationPrefs.sound ? 'Sound on' : 'Sound off'}>
                      <IconButton
                        onClick={() => setNotificationPrefs((p) => ({ ...p, sound: !p.sound }))}
                        size="small"
                        sx={{
                          bgcolor: notificationPrefs.sound ? '#667eea15' : 'transparent',
                          color: notificationPrefs.sound ? '#667eea' : 'text.secondary',
                        }}
                      >
                        <VolumeUp fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>

                {/* Search, Filter & Account Selector */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mt={2}>
                  <TextField
                    size="small"
                    placeholder="Search emails..."
                    value={inboxSearch}
                    onChange={(e) => setInboxSearch(e.target.value)}
                    InputProps={{
                      startAdornment: <Search sx={{ mr: 0.5, color: 'text.secondary', fontSize: 18 }} />,
                      endAdornment: inboxSearch && (
                        <IconButton size="small" onClick={() => setInboxSearch('')}>
                          <Close sx={{ fontSize: 16 }} />
                        </IconButton>
                      ),
                    }}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        '&:hover fieldset': { borderColor: '#667eea' },
                      },
                    }}
                  />
                  <Stack direction="row" spacing={1} alignItems="center">
                    {/* Account filter */}
                    {accounts.length > 1 && (
                      <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 180 }}>
                        <Select
                          value={inboxAccountFilter}
                          onChange={(e) => setInboxAccountFilter(e.target.value)}
                          displayEmpty
                          sx={{
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            fontSize: 13,
                            '& .MuiSelect-select': { py: 0.75 },
                          }}
                        >
                          <MenuItem value="all">
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Inbox sx={{ fontSize: 16, color: '#667eea' }} />
                              <span>All Accounts</span>
                            </Stack>
                          </MenuItem>
                          {accounts.filter(a => a.imapHost || a.popHost).map(acc => (
                            <MenuItem key={acc.id} value={acc.id}>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <Avatar sx={{ width: 18, height: 18, fontSize: 9, bgcolor: '#667eea', color: 'white' }}>
                                  {(acc.fromEmail || 'A').charAt(0).toUpperCase()}
                                </Avatar>
                                <Typography variant="body2" noWrap sx={{ fontSize: 13, maxWidth: 140 }}>
                                  {acc.fromEmail || acc.username}
                                </Typography>
                              </Stack>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    <Chip
                      label={filterUnread ? 'Unread Only' : 'All Messages'}
                      onClick={() => setFilterUnread(!filterUnread)}
                      color={filterUnread ? 'primary' : 'default'}
                      variant={filterUnread ? 'filled' : 'outlined'}
                      size="small"
                      sx={{ fontWeight: 600, borderRadius: 2 }}
                    />
                  </Stack>
                </Stack>

                {/* Loading indicator */}
                {fetchingInbox && (
                  <LinearProgress sx={{ mt: 1.5, borderRadius: 1, height: 3, '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #667eea, #764ba2)' } }} />
                )}
              </Paper>

              {/* Two-pane Layout */}
              <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                {/* Left: Message List */}
                <Grid item xs={12} md={3.5}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: { xs: 360, md: '78vh' },
                      overflow: 'auto',
                      borderRadius: 2.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)',
                      '&::-webkit-scrollbar': { width: 6 },
                      '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(102,126,234,0.2)', borderRadius: 3 },
                    }}
                  >
                    {filteredInbox.length === 0 ? (
                      <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Inbox sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          {fetchingInbox ? 'Loading emails...' : 'No messages'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fetchingInbox
                            ? 'Fetching from all accounts...'
                            : accounts.length === 0
                              ? 'Add email accounts in the Accounts tab first'
                              : 'No messages match your current filters'}
                        </Typography>
                      </Box>
                    ) : (
                      filteredInbox.map((msg) => {
                        const isActive = activeMessageId === msg.id;
                        const isUnread = msg.status === 'unread';
                        const accLabel = accountMap[msg.accountId] || '';
                        const cleanedPreview = getMessagePreview(msg);
                        return (
                          <Box
                            key={msg.id}
                            onClick={() => {
                              setActiveMessageId(msg.id);
                              setMessageStatus(msg.id, 'read');
                            }}
                            sx={{
                              px: 2,
                              py: 1.5,
                              cursor: 'pointer',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              bgcolor: isActive ? 'rgba(102,126,234,0.08)' : isUnread ? 'rgba(102,126,234,0.03)' : 'transparent',
                              borderLeft: isActive ? '3px solid #667eea' : '3px solid transparent',
                              transition: 'all 0.15s ease',
                              '&:hover': { bgcolor: isActive ? 'rgba(102,126,234,0.1)' : 'rgba(0,0,0,0.02)' },
                            }}
                          >
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                              <Avatar
                                sx={{
                                  width: 36, height: 36, fontSize: 13, fontWeight: 700,
                                  bgcolor: isActive ? '#667eea' : '#e2e8f0',
                                  color: isActive ? 'white' : '#64748b',
                                  mt: 0.25,
                                }}
                                src={resolveAvatar(msg) || undefined}
                              >
                                {(msg.from || 'U').slice(0, 2).toUpperCase()}
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography
                                    variant="body2"
                                    fontWeight={isUnread ? 800 : 600}
                                    noWrap
                                    sx={{ maxWidth: '65%', fontSize: 13, color: isUnread ? 'text.primary' : 'text.secondary' }}
                                  >
                                    {msg.from || '(Unknown)'}
                                  </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, flexShrink: 0 }}>
                                    {formatRelativeTimestamp(msg.timestamp)}
                                  </Typography>
                                </Stack>
                                <Typography
                                  variant="body2"
                                  fontWeight={isUnread ? 700 : 400}
                                  noWrap
                                  sx={{ fontSize: 12, mt: 0.25, color: isUnread ? 'text.primary' : 'text.secondary' }}
                                >
                                  {msg.subject || '(No subject)'}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  sx={{ fontSize: 11, mt: 0.25, display: 'block', opacity: 0.7 }}
                                >
                                  {cleanedPreview.slice(0, 110) || 'No preview available'}
                                </Typography>
                                <Stack direction="row" spacing={0.5} mt={0.75} flexWrap="wrap">
                                  {accLabel && (
                                    <Chip
                                      label={accLabel}
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: 9,
                                        fontWeight: 600,
                                        bgcolor: 'rgba(102,126,234,0.08)',
                                        color: '#667eea',
                                        borderRadius: 1,
                                        '& .MuiChip-label': { px: 0.75 },
                                      }}
                                    />
                                  )}
                                  {msg.to && (
                                    <Chip
                                      label={`To ${msg.to}`}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        height: 18,
                                        fontSize: 9,
                                        fontWeight: 600,
                                        borderRadius: 1,
                                        '& .MuiChip-label': { px: 0.75 },
                                      }}
                                    />
                                  )}
                                </Stack>
                              </Box>
                              {isUnread && (
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#667eea', flexShrink: 0, mt: 1 }} />
                              )}
                            </Stack>
                          </Box>
                        );
                      })
                    )}
                  </Paper>
                </Grid>

                {/* Right: Conversation View */}
                <Grid item xs={12} md={8.5}>
                  {(() => {
                    const active = activeInboxMessage;
                    if (!active) {
                      return (
                        <Paper
                          elevation={0}
                          sx={{
                            height: { xs: 'auto', md: '78vh' },
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            borderRadius: 2.5, border: '1px solid', borderColor: 'divider',
                          }}
                        >
                          <Mail sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
                          <Typography variant="body1" color="text.secondary" fontWeight={600}>
                            Select an email to read
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            Choose a message from the list to view the conversation
                          </Typography>
                        </Paper>
                      );
                    }
                    const activeAccLabel = accountMap[active.accountId] || '';
                    const activeReplyDraft = replyDrafts[active.id] || '';
                    return (
                      <Paper
                        elevation={0}
                        sx={{
                          height: { xs: 'auto', md: '78vh' },
                          display: 'flex', flexDirection: 'column',
                          borderRadius: 2.5, border: '1px solid', borderColor: 'divider',
                          background: 'linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(248,250,252,0.95) 100%)',
                          overflow: 'hidden',
                        }}
                      >
                        {/* Email Header */}
                        <Box sx={{
                          px: isMobile ? 2 : 3, py: 2,
                          borderBottom: '1px solid', borderColor: 'divider',
                          background: 'linear-gradient(135deg, rgba(102,126,234,0.04), rgba(118,75,162,0.04))',
                        }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5, fontSize: isMobile ? '1rem' : '1.15rem' }}>
                                {active.subject || '(No subject)'}
                              </Typography>
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mb: 0.75 }}>
                                {activeAccLabel && (
                                  <Chip
                                    label={`Inbox ${activeAccLabel}`}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontSize: 11,
                                      fontWeight: 600,
                                      bgcolor: 'rgba(102,126,234,0.1)',
                                      color: '#667eea',
                                      borderRadius: 1.5,
                                    }}
                                  />
                                )}
                                <Chip
                                  label={active.status === 'unread' ? 'Unread' : 'Read'}
                                  size="small"
                                  color={active.status === 'unread' ? 'primary' : 'default'}
                                  variant={active.status === 'unread' ? 'filled' : 'outlined'}
                                  sx={{ height: 22, fontSize: 11, fontWeight: 600, borderRadius: 1.5 }}
                                />
                                <Chip
                                  label={formatRelativeTimestamp(active.timestamp) || 'Recently updated'}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 22, fontSize: 11, fontWeight: 600, borderRadius: 1.5 }}
                                />
                              </Stack>
                            </Box>
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title={active.status === 'unread' ? 'Mark as read' : 'Mark as unread'}>
                                <IconButton
                                  size="small"
                                  onClick={() => setMessageStatus(active.id, active.status === 'unread' ? 'read' : 'unread')}
                                  sx={{ color: 'text.secondary' }}
                                >
                                  {active.status === 'unread' ? <MarkEmailRead fontSize="small" /> : <MarkEmailUnread fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => { deleteMessage(active.id); if (activeMessageId === active.id) setActiveMessageId(''); }}
                                  sx={{ color: 'error.main' }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Stack>
                          <Stack direction="row" spacing={1.5} alignItems="center" mt={0.5}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: '#667eea', color: 'white' }}>
                              {(active.from || 'U').slice(0, 2).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={700} sx={{ fontSize: 13 }}>
                                {active.from || '(Unknown sender)'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                                {active.to ? `${active.to} • ` : ''}{new Date(active.timestamp).toLocaleString()}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>

                        {/* Conversation Thread */}
                        <Box sx={{
                          flex: 1, overflowY: 'auto', px: isMobile ? 2 : 3, py: 2,
                          '&::-webkit-scrollbar': { width: 6 },
                          '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(102,126,234,0.2)', borderRadius: 3 },
                        }}>
                          {(() => {
                            const relatedInbox = conversationItems.filter((item) => item.__type === 'in');
                            const relatedSent = conversationItems.filter((item) => item.__type === 'out');
                            const repliesCount = Math.max(0, relatedInbox.length - 1);
                            const primarySent = relatedSent.find((item) => item.messageId || item.id);
                            const tracking = primarySent ? trackingMap[primarySent.messageId || primarySent.id] : null;

                            return (
                              <>
                                {/* Thread Stats */}
                                <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
                                  <Chip label={`${conversationItems.length} messages`} size="small" variant="outlined" sx={{ fontWeight: 600, borderRadius: 2 }} />
                                  <Chip label={`Sent: ${relatedSent.length}`} size="small" variant="outlined" sx={{ fontWeight: 600, borderRadius: 2 }} />
                                  <Chip
                                    label={`Opens: ${tracking?.totalOpens ?? 0}`}
                                    size="small"
                                    color={tracking?.totalOpens >= 2 ? 'success' : 'default'}
                                    variant="outlined"
                                    icon={<Visibility fontSize="small" />}
                                    sx={{ fontWeight: 600, borderRadius: 2 }}
                                  />
                                  <Chip label={`Replies: ${repliesCount}`} size="small" variant="outlined" sx={{ fontWeight: 600, borderRadius: 2 }} />
                                  {tracking?.multiOpenRecipients?.length > 0 && (
                                    <Chip label="Engaged" size="small" color="success" variant="outlined" sx={{ fontWeight: 600, borderRadius: 2 }} />
                                  )}
                                </Stack>

                                {/* Messages */}
                                <Stack spacing={1.5}>
                                  {conversationItems.map((item) => {
                                      const isOut = item.__type === 'out';
                                      const itemAccountLabel = item.accountId ? accountMap[item.accountId] || '' : activeAccLabel;
                                      return (
                                        <Box
                                          key={`${item.__type}-${item.id}`}
                                          sx={{
                                            display: 'flex',
                                            justifyContent: isOut ? 'flex-end' : 'flex-start',
                                            width: '100%',
                                          }}
                                        >
                                          <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ maxWidth: { xs: '96%', md: '88%' } }}>
                                            {!isOut && (
                                              <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: '#e2e8f0', color: '#64748b', flexShrink: 0 }}>
                                                {(item.from || 'U').slice(0, 2).toUpperCase()}
                                              </Avatar>
                                            )}
                                            <Box
                                              sx={{
                                                px: 2, py: 1.25,
                                                borderRadius: isOut ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                                bgcolor: isOut ? '#667eea' : '#f1f5f9',
                                                color: isOut ? 'white' : 'text.primary',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                              }}
                                            >
                                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, fontSize: 13 }}>
                                                {item.displayBody || item.preview || 'No message content'}
                                              </Typography>
                                              {itemAccountLabel && (
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    display: 'block',
                                                    opacity: isOut ? 0.8 : 0.65,
                                                    mt: 0.75,
                                                    fontSize: 10.5,
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {isOut ? `Sent via ${itemAccountLabel}` : `Received in ${itemAccountLabel}`}
                                                </Typography>
                                              )}
                                              <Typography variant="caption" sx={{ display: 'block', opacity: 0.65, mt: 0.5, fontSize: 10.5, textAlign: isOut ? 'right' : 'left' }}>
                                                {new Date(item.ts || Date.now()).toLocaleString()}
                                              </Typography>
                                            </Box>
                                            {isOut && (
                                              <Avatar sx={{ width: 28, height: 28, fontSize: 10, bgcolor: '#667eea', color: 'white', flexShrink: 0 }}>
                                                You
                                              </Avatar>
                                            )}
                                          </Stack>
                                        </Box>
                                      );
                                    })}
                                </Stack>
                              </>
                            );
                          })()}
                        </Box>

                        {/* Reply Box */}
                        <Box sx={{
                          px: isMobile ? 2 : 3, py: 1.5,
                          borderTop: '1px solid', borderColor: 'divider',
                          bgcolor: 'rgba(248,250,252,0.92)',
                        }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} mb={1}>
                            <Box>
                              <Typography variant="body2" fontWeight={700}>
                                Reply to {active.from || 'contact'}
                              </Typography>
                            </Box>
                          </Stack>
                          <TextField
                            multiline
                            minRows={4}
                            maxRows={8}
                            fullWidth
                            size="small"
                            placeholder="Write your reply..."
                            value={activeReplyDraft}
                            onChange={(e) => setReplyDrafts((prev) => ({ ...prev, [active.id]: e.target.value }))}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                bgcolor: 'background.paper',
                                '&:hover fieldset': { borderColor: '#667eea' },
                                '&.Mui-focused fieldset': { borderColor: '#667eea' },
                              },
                            }}
                          />
                          <Stack direction="row" spacing={1} justifyContent="flex-end" mt={1}>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={replySending ? null : <Reply />}
                              onClick={() => handleReply(active.id)}
                              disabled={!activeReplyDraft.trim() || replySending}
                              sx={{
                                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                fontWeight: 600, borderRadius: 2, textTransform: 'none',
                              }}
                            >
                              {replySending ? 'Sending...' : 'Reply'}
                            </Button>
                            {activeReplyDraft.trim() && (
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => setReplyDrafts((prev) => { const next = { ...prev }; delete next[active.id]; return next; })}
                                sx={{ color: 'text.secondary', textTransform: 'none' }}
                              >
                                Discard
                              </Button>
                            )}
                            <Tooltip title={showRaw ? 'Hide raw' : 'Show raw'}>
                              <IconButton onClick={() => setShowRaw((v) => !v)} size="small">
                                {showRaw ? <MarkEmailRead fontSize="small" /> : <MarkEmailUnread fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Box>
                      </Paper>
                    );
                  })()}
                </Grid>
              </Grid>
            </Box>
          )}

                </Box>

        <Snackbar
            open={
              notice.open &&
              (
                notice.scope === 'global' ||
                (notice.scope === 'accounts' && activeTab === 5) ||
                (notice.scope === 'compose' && activeTab === 0) ||
                (notice.scope === 'campaign' && activeTab === 1) ||
                (notice.scope === 'inbox' && activeTab === 3) ||
                (notice.scope === 'engaged' && activeTab === 4)
              )
            }
            autoHideDuration={4000}
            onClose={() => setNotice((prev) => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setNotice((prev) => ({ ...prev, open: false }))}
              severity={notice.type || 'info'}
              sx={{ width: '100%' }}
            >
              {notice.message}
            </Alert>
          </Snackbar>

          <Snackbar
            open={footerNotice.open}
            onClose={() => setFooterNotice({ open: false, message: '' })}
            message={footerNotice.message}
            autoHideDuration={7000}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          />
        </CardContent>
            </>
          )}
          {inboxOnly && (
            <CardContent>
              <InboxManager
                emails={inboxEmails || []}
                threads={threads || []}
                unreadCount={unreadCount}
                onReplyEmail={() => {}}
                onDeleteEmail={() => {}}
                onArchiveEmail={() => {}}
                onMarkAsRead={() => {}}
                onFlagEmail={() => {}}
                onSearchEmails={() => {}}
                selectedEmailId={selectedEmailId}
                onSelectEmail={setSelectedEmailId}
              />
            </CardContent>
          )}
        </Card>
      </Box>
    </Box>

    <Dialog open={accountModalOpen} onClose={() => { setAccountModalOpen(false); setTestPassed(false); }} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontWeight: 700, fontSize: '1.3rem', padding: '20px' }}>
          {editingId ? '✏️ Edit Email Account' : '📧 Add Email Account'}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '85vh', overflowY: 'auto', padding: '24px' }}>
          {accountError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {accountError}
            </Alert>
          )}
          {accountMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {accountMessage}
            </Alert>
          )}

          <Stack spacing={2.5} mt={2}>
            {/* Step 1: Email Input with Auto-Detect */}
            <Card sx={{ border: '2px solid #e0e0e0', backgroundColor: '#fafafa' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                  <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: '#667eea', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                    1
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Enter Your Email
                  </Typography>
                </Stack>
                <TextField
                  label="Email Address"
                  placeholder="you@gmail.com"
                  value={accountForm.fromEmail}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  fullWidth
                  size="small"
                  helperText="Type your email - we'll auto-detect your provider"
                  sx={{ mb: 1 }}
                />
                {selectedProviderTemplate && (
                  <Chip
                    label={`${selectedProviderTemplate.logo} ${selectedProviderTemplate.name} Detected ✓`}
                    size="small"
                    color="success"
                    variant="filled"
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </CardContent>
            </Card>

            {/* Step 2: Provider Selection (if not auto-detected) */}
            {!selectedProviderTemplate && (
              <Card sx={{ border: '2px solid #e0e0e0', backgroundColor: '#fafafa' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: '#667eea', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                      2
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                      Select Your Email Provider
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Choose from 13+ providers or enter manual SMTP settings:
                  </Typography>
                  
                  {/* Group providers by category */}
                  {['Popular', 'Business', 'Domain', 'Transactional', 'Custom'].map((category) => {
                    const categoryProviders = getProviderOptions().filter(p => p.category === category);
                    if (categoryProviders.length === 0) return null;
                    
                    return (
                      <Box key={category} sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#667eea', display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {category === 'Domain' ? '🌐 Domain Email Hosting' : 
                           category === 'Business' ? '💼 Business Email' : 
                           category === 'Transactional' ? '📤 Transactional Services' :
                           category === 'Custom' ? '⚙️ Other Options' :
                           '⭐ Popular'}
                        </Typography>
                        <Grid container spacing={1}>
                          {categoryProviders.map((provider) => (
                            <Grid item xs={12} sm={6} md={4} key={provider.id}>
                              <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => handleProviderSelect(EMAIL_PROVIDERS[provider.id])}
                                sx={{
                                  py: 1.8,
                                  px: 2,
                                  border: '2px solid #e0e0e0',
                                  borderRadius: 1.5,
                                  textTransform: 'none',
                                  fontSize: '0.95rem',
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  justifyContent: 'flex-start',
                                  transition: 'all 0.2s ease',
                                  backgroundColor: 'white',
                                  '&:hover': {
                                    borderColor: provider.color,
                                    backgroundColor: provider.color + '08',
                                    boxShadow: `0 4px 12px ${provider.color}20`,
                                    transform: 'translateY(-2px)',
                                  },
                                }}
                              >
                                <Box sx={{ fontSize: '1.8rem', minWidth: '32px' }}>{provider.label.split(' ')[0]}</Box>
                                <Box sx={{ textAlign: 'left', flex: 1 }}>
                                  <Box sx={{ fontWeight: 700 }}>{provider.label.split(' ').slice(1).join(' ')}</Box>
                                </Box>
                              </Button>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Step 3: Setup Guide & Instructions */}
            {selectedProviderTemplate && (
              <Card sx={{ border: '2px solid #FFA500', backgroundColor: '#fff8e1' }}>
                <CardContent>
                  <Stack direction="row" alignItems="flex-start" spacing={1} mb={1.5}>
                    <Box sx={{ fontSize: '1.5rem' }}>📺</Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#f57c00' }}>
                        Setup Tutorial: {selectedProviderTemplate.name}
                      </Typography>
                      <Button
                        size="small"
                        href={selectedProviderTemplate.youtubeLink || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ mt: 0.5 }}
                        endIcon={<VolumeUp />}
                      >
                        Watch YouTube Tutorial
                      </Button>
                    </Box>
                  </Stack>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ backgroundColor: 'white', p: 1.5, borderRadius: 1, maxHeight: 300, overflowY: 'auto' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '0.9rem' }}>
                      {selectedProviderTemplate.guide}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setShowProviderGuide(!showProviderGuide)}
                    sx={{ mt: 1 }}
                  >
                    {showProviderGuide ? 'Hide Guide' : 'Show Full Guide'}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setSelectedProviderTemplate(null);
                      setShowProviderGuide(false);
                    }}
                    startIcon={<Close />}
                    sx={{ mt: 1, ml: 1, color: '#f44336', borderColor: '#f44336' }}
                  >
                    Change Provider
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Step 4: Account Details */}
            {selectedProviderTemplate && (
              <Card sx={{ border: '2px solid #e0e0e0', backgroundColor: '#fafafa' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: '#667eea', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                      3
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Account Details
                    </Typography>
                  </Stack>
                  <Stack spacing={1.5}>
                    <TextField
                      label="Account Label"
                      placeholder={`My ${selectedProviderTemplate.name} Account`}
                      value={accountForm.label}
                      onChange={(e) => setAccountForm((prev) => ({ ...prev, label: e.target.value }))}
                      fullWidth
                      size="small"
                      helperText="Use a descriptive name (e.g., 'Primary Gmail', 'Work Outlook')"
                    />

                    <TextField
                      label="Display Name"
                      placeholder="Your Full Name"
                      value={accountForm.fromName}
                      onChange={(e) => setAccountForm((prev) => ({ ...prev, fromName: e.target.value }))}
                      fullWidth
                      size="small"
                      helperText="Recipients will see this name when you send emails"
                    />
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Step 5: Credentials Box */}
            {selectedProviderTemplate && (
              <Card sx={{ border: '2px solid #2196F3', backgroundColor: '#E3F2FD' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: '#2196F3', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                      4
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      🔐 Enter Your Credentials
                    </Typography>
                  </Stack>
                  
                  {selectedProviderTemplate.smtp?.requiresAppPassword && (
                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        ⚠️ App Password Required
                      </Typography>
                      <Typography variant="body2">
                        {selectedProviderTemplate.name} requires a special "App Password", not your regular password.
                      </Typography>
                      {selectedProviderTemplate.smtp?.appPasswordUrl && (
                        <Button
                          size="small"
                          href={selectedProviderTemplate.smtp.appPasswordUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="text"
                          sx={{ mt: 0.5 }}
                        >
                          Get App Password →
                        </Button>
                      )}
                    </Alert>
                  )}

                  <Stack spacing={1.2}>
                    <TextField
                      label="Username / Email"
                      placeholder={accountForm.fromEmail || 'your.email@gmail.com'}
                      value={accountForm.username}
                      onChange={(e) => setAccountForm((prev) => ({ ...prev, username: e.target.value }))}
                      fullWidth
                      size="small"
                      helperText="Usually your email address"
                    />
                    <TextField
                      label="Password / API Key"
                      type="password"
                      placeholder="••••••••••••••••"
                      value={accountForm.password}
                      onChange={(e) => setAccountForm((prev) => ({ ...prev, password: e.target.value }))}
                      fullWidth
                      size="small"
                      helperText="Your password (encrypted, never stored as plain text)"
                    />
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Advanced SMTP Settings - Collapsible */}
            {selectedProviderTemplate && (
              <Card sx={{ border: '2px solid #9C27B0', backgroundColor: '#F3E5F5' }}>
                <CardContent>
                  <Box
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      userSelect: 'none',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ fontSize: '1.2rem' }}>⚙️</Box>
                      <Typography sx={{ fontWeight: 700 }}>Advanced SMTP Settings</Typography>
                    </Stack>
                    {showAdvanced ? <ExpandLess /> : <ExpandMore />}
                  </Box>
                  <Collapse in={showAdvanced} sx={{ mt: 1.5 }}>
                    <Stack spacing={1}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        These settings are usually pre-filled. Only change if needed.
                      </Typography>
                      <TextField
                        label="SMTP Host"
                        placeholder="smtp.gmail.com"
                        value={accountForm.host}
                        onChange={(e) => setAccountForm((prev) => ({ ...prev, host: e.target.value }))}
                        fullWidth
                        size="small"
                      />
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <TextField
                            label="SMTP Port"
                            type="number"
                            value={accountForm.port}
                            onChange={(e) => setAccountForm((prev) => ({ ...prev, port: Number(e.target.value) }))}
                            fullWidth
                            size="small"
                            helperText="587 (TLS) or 465 (SSL)"
                          />
                        </Grid>
                        <Grid item xs={6} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={accountForm.secure}
                                onChange={(e) => setAccountForm((prev) => ({ ...prev, secure: e.target.checked }))}
                                size="small"
                              />
                            }
                            label="Use TLS/SSL"
                          />
                        </Grid>
                      </Grid>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1.5 }}>
                        IMAP (For Receiving Emails)
                      </Typography>
                      <TextField
                        label="IMAP Host"
                        placeholder="imap.gmail.com"
                        value={accountForm.imapHost}
                        onChange={(e) => setAccountForm((prev) => ({ ...prev, imapHost: e.target.value }))}
                        fullWidth
                        size="small"
                        helperText="Leave empty to disable receiving emails"
                      />
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <TextField
                            label="IMAP Port"
                            type="number"
                            value={accountForm.imapPort || 993}
                            onChange={(e) => setAccountForm((prev) => ({ ...prev, imapPort: Number(e.target.value) }))}
                            fullWidth
                            size="small"
                            helperText="Usually 993"
                          />
                        </Grid>
                        <Grid item xs={6} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={accountForm.imapSecure !== false}
                                onChange={(e) => setAccountForm((prev) => ({ ...prev, imapSecure: e.target.checked }))}
                                size="small"
                              />
                            }
                            label="Use Secure"
                          />
                        </Grid>
                      </Grid>
                      <TextField
                        label="Daily Send Limit"
                        type="number"
                        value={accountForm.dailyLimit || '0'}
                        onChange={(e) => setAccountForm((prev) => ({ ...prev, dailyLimit: Number(e.target.value) || 0 }))}
                        fullWidth
                        size="small"
                        helperText="0 = unlimited. Typical: 500 per day"
                      />
                    </Stack>
                  </Collapse>
                </CardContent>
              </Card>
            )}

            {/* Info: What You Can Do */}
            {selectedProviderTemplate && (
              <Alert severity="info" icon={<Check />}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  ✅ Once Added, You Can:
                </Typography>
                <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.25rem', fontSize: '0.9rem' }}>
                  <li>Send emails from this account</li>
                  <li>Receive emails in the Inbox</li>
                  <li>Track email opens and clicks</li>
                  <li>Schedule campaigns</li>
                  <li>Use multiple accounts simultaneously</li>
                </ul>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, background: '#f5f5f5', display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Button onClick={() => { setAccountModalOpen(false); setSelectedProviderTemplate(null); setShowAdvanced(false); setTestPassed(false); }}>
              Cancel
            </Button>
            {selectedProviderTemplate && (
              <Button
                variant="text"
                onClick={() => {
                  setSelectedProviderTemplate(null);
                  setAccountForm({
                    label: '',
                    fromEmail: '',
                    username: '',
                    password: '',
                    host: '',
                    port: 587,
                    secure: true,
                    imapHost: '',
                    imapPort: 993,
                    imapSecure: true,
                    mailbox: 'INBOX',
                    popHost: '',
                    popPort: 995,
                    popSecure: true,
                  });
                  setShowAdvanced(false);
                }}
                startIcon={<ExpandLess />}
                sx={{ ml: 1 }}
              >
                ← Back to Providers
              </Button>
            )}
          </Box>
          {selectedProviderTemplate && (
            <Box>
              <Button
                variant="outlined"
                onClick={() => {
                  handleAccountTest();
                }}
                disabled={!accountForm.host || !accountForm.username || !accountForm.password || testingAccount}
                startIcon={testingAccount ? <CircularProgress size={20} /> : <Mail />}
              >
                {testingAccount ? 'Testing...' : 'Test Connection'}
              </Button>
              <Button
                variant="contained"
                sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', ml: 1 }}
                onClick={async () => {
                  const saved = await handleAccountSave();
                  if (!saved) return;
                  setAccountModalOpen(false);
                  setSelectedProviderTemplate(null);
                  setShowAdvanced(false);
                  setTestPassed(false);
                }}
                disabled={!testPassed || savingAccount}
                startIcon={savingAccount ? <CircularProgress size={20} /> : <Save />}
              >
                {savingAccount ? 'Saving...' : (editingId ? 'Update Account' : 'Add Account')}
              </Button>
            </Box>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={templateModalOpen} onClose={() => setTemplateModalOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <span>{templateForm.id ? 'Edit Template' : 'New Template'}</span>
            <Box display="flex" gap={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AutoAwesome />}
                onClick={() => setAiDialogOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                AI Writer
              </Button>
              {spamAnalysis && spamAnalysis.score > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  color={spamAnalysis.risk === 'critical' || spamAnalysis.risk === 'risky' ? 'error' : 'warning'}
                  startIcon={<Shield />}
                  onClick={handleTemplateRewriteSpamFree}
                  disabled={rewriteLoading}
                  sx={{ textTransform: 'none' }}
                >
                  {rewriteLoading ? 'Rewriting...' : 'Remove Spam Words'}
                </Button>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={0.5}>
            {/* Spam Analysis Alert */}
            {spamAnalysis && spamAnalysis.score > 0 && (
              <Alert 
                severity={
                  spamAnalysis.risk === 'critical' ? 'error' :
                  spamAnalysis.risk === 'risky' ? 'warning' :
                  spamAnalysis.risk === 'good' ? 'info' : 'success'
                }
                sx={{ cursor: 'pointer' }}
                onClick={() => setShowSpamDetails(!showSpamDetails)}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Typography variant="body2">
                    <strong>Spam Score: {spamAnalysis.score}%</strong> - {spamAnalysis.risk === 'excellent' ? 'Excellent!' : spamAnalysis.risk === 'good' ? 'Good' : spamAnalysis.risk === 'risky' ? 'Risky' : 'Critical'}
                  </Typography>
                  <Chip 
                    label={showSpamDetails ? 'Hide Details' : 'Show Details'} 
                    size="small" 
                    sx={{ ml: 1 }}
                  />
                </Box>
                <Collapse in={showSpamDetails}>
                  <Box mt={1}>
                    <Typography variant="caption" display="block"><strong>Issues:</strong></Typography>
                    {spamAnalysis.warnings.map((warn, i) => (
                      <Typography key={i} variant="caption" display="block">• {warn}</Typography>
                    ))}
                    <Typography variant="caption" display="block" mt={1}>
                      <strong>Recommendation:</strong> {spamAnalysis.recommendation}
                    </Typography>
                  </Box>
                </Collapse>
              </Alert>
            )}
            
            <TextField
              label="Template name"
              size="small"
              value={templateForm.name}
              onChange={(e) => setTemplateForm((p) => ({ ...p, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Subject"
              size="small"
              value={templateForm.subject}
              onChange={(e) => setTemplateForm((p) => ({ ...p, subject: e.target.value }))}
              fullWidth
            />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Email Body (supports HTML formatting, images, and merge tags)
              </Typography>
              <RichTextEditor
                value={templateForm.body || ''}
                onChange={(html) => setTemplateForm((p) => ({ ...p, body: html }))}
                placeholder="Design your email template with formatting, images, and merge tags..."
                height={350}
                minHeight={250}
                mergeTags={mergeTagOptions}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={() => {
              saveTemplate();
              setTemplateModalOpen(false);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Email Writer Dialog for Templates */}
      <Dialog open={aiDialogOpen} onClose={() => setAiDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AutoAwesome color="primary" />
            <Typography variant="h6">AI Email Writer</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info" icon={<Lightbulb />}>
              AI will write a professional email template with proper HTML formatting and merge tags
            </Alert>
            
            <TextField
              label="What should this email template be about?"
              multiline
              rows={3}
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="e.g., 'Professional truck dispatching service offer for owner operators'"
              fullWidth
              helperText="AI will intelligently select and use merge tags"
            />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tone</InputLabel>
                  <Select value={aiTone} onChange={(e) => setAiTone(e.target.value)} label="Tone">
                    <MenuItem value="professional">Professional</MenuItem>
                    <MenuItem value="friendly">Friendly</MenuItem>
                    <MenuItem value="formal">Formal</MenuItem>
                    <MenuItem value="casual">Casual</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Length</InputLabel>
                  <Select value={aiLength} onChange={(e) => setAiLength(e.target.value)} label="Length">
                    <MenuItem value="short">Short</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="long">Long</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={aiLoading ? <CircularProgress size={20} /> : <AutoAwesome />}
            onClick={handleTemplateAIGenerate}
            disabled={!aiPrompt.trim() || aiLoading}
          >
            {aiLoading ? 'Generating...' : 'Generate Template'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={groupModalOpen} onClose={() => setGroupModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{groupForm.id ? 'Edit Account Group' : 'Create Account Group'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Group name"
              value={groupForm.name}
              onChange={(e) => setGroupForm((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              select
              fullWidth
              size="small"
              label="Accounts in this group"
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  const selectedArray = Array.isArray(selected) ? selected : [];
                  return selectedArray
                    .map((id) => accounts.find((a) => a.id === id)?.label || id)
                    .join(', ');
                },
              }}
              value={groupForm.accountIds}
              onChange={(e) => {
                setGroupForm((prev) => ({ ...prev, accountIds: e.target.value }));
              }}
            >
              {accounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  <Checkbox checked={groupForm.accountIds.indexOf(account.id) > -1} />
                  {account.label || account.fromEmail}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              saveAccountGroup();
              setGroupModalOpen(false);
            }}
          >
            {groupForm.id ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmailWorkspace;



